import React, { useState, useEffect, useRef } from "react";

const T = {
  bg:"#0b0c10", surface:"#13141a", card:"#1c1d26", border:"#2e2f42",
  accent:"#e8ff00", green:"#00e87a", blue:"#00b4ff", orange:"#ff9500",
  red:"#ff4455", purple:"#c084fc", muted:"#6b6c88", text:"#eeeeff",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#0b0c10;color:#eeeeff;font-family:'DM Sans',sans-serif}
  input,textarea,select,button{font-family:inherit;outline:none}
  ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:#2e2f42;border-radius:2px}
  @keyframes fu{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes tourPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.12)}}
  @keyframes slideInRight{from{opacity:.4;transform:translateX(100%)}to{opacity:1;transform:translateX(0)}}
  @keyframes slideInLeft{from{opacity:.4;transform:translateX(-100%)}to{opacity:1;transform:translateX(0)}}
  .fu{animation:fu .25s ease}
  .slide-r{animation:slideInRight .32s cubic-bezier(.22,.68,0,1) both}
  .slide-l{animation:slideInLeft .32s cubic-bezier(.22,.68,0,1) both}
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const pad      = n  => String(n).padStart(2,"0");
const nowISO   = () => new Date().toISOString().slice(0,10);
const nowTime  = () => { const d=new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const fmtDate  = iso => { if(!iso)return"—"; const[,m,d]=iso.split("-"); return`${d}/${m}`; };
const fmtDateFull = iso => { if(!iso)return"—"; const[y,m,d]=iso.split("-"); return`${d}/${m}/${y}`; };
const WSHORT   = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];
const WLONG    = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"];
const weekday  = iso => WSHORT[new Date(iso+"T12:00:00").getDay()];
const uid      = () => Math.random().toString(36).slice(2,9);
// Convierte texto a número aceptando tanto coma como punto decimal (ej: "5,1" o "5.1" → 5.1).
// Los campos numéricos de toda la app pasan por acá antes de guardar el valor.
const toNum = v => { const n = parseFloat(String(v??"").replace(",",".")); return isNaN(n) ? 0 : n; };
const rnd      = n  => Math.round(toNum(n));

function getMondayOf(iso) {
  const d=new Date(iso+"T12:00:00"), day=d.getDay();
  d.setDate(d.getDate()+(day===0?-6:1-day));
  return d.toISOString().slice(0,10);
}
function getWeekDates(mon) {
  const out=[], b=new Date(mon+"T12:00:00");
  for(let i=0;i<7;i++){const d=new Date(b);d.setDate(b.getDate()+i);out.push(d.toISOString().slice(0,10));}
  return out;
}

// ─── Storage ──────────────────────────────────────────────────────────────────
const DATA_KEY = "gd_data_v1";
const IMG_KEY  = "gd_img_v1";
const lsGet = (k,d) => { try{const v=localStorage.getItem(k);return v?JSON.parse(v):d;}catch{return d;} };
let _lastStorageWarnAt = 0;
const lsSet = (k,v) => {
  try{ localStorage.setItem(k,JSON.stringify(v)); return true; }
  catch(e){
    // Si se llenó el espacio del navegador, avisar en vez de fallar en silencio
    // (antes, una foto sin comprimir podía hacer fallar el guardado sin que se notara).
    // Usamos un evento (no alert(), que en algunas PWA instaladas puede quedar "trabado"
    // sin dibujarse pero bloqueando igual toda la app) — como máximo un aviso cada 15s.
    console.error("No se pudo guardar en localStorage:", e);
    const now = Date.now();
    if(now - _lastStorageWarnAt > 15000){
      _lastStorageWarnAt = now;
      try{ window.dispatchEvent(new CustomEvent("gd:storage-full")); }catch{}
    }
    return false;
  }
};
// Comprime y redimensiona una imagen antes de guardarla — evita que una foto de cámara sin
// comprimir (varios MB) haga fallar el guardado en localStorage (que tiene un límite total ~5-10MB).
function compressImageFile(file, maxDim=600, quality=0.72){
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        let {width, height} = img;
        if(width>height && width>maxDim){ height = Math.round(height*(maxDim/width)); width = maxDim; }
        else if(height>maxDim){ width = Math.round(width*(maxDim/height)); height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => reject(new Error("No se pudo leer la imagen"));
      img.src = ev.target.result;
    };
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}
const loadData = ()  => lsGet(DATA_KEY, {});
const saveData = d   => lsSet(DATA_KEY, d);
const loadLib  = ()  => { const v=lsGet(IMG_KEY,[]); return Array.isArray(v)?v:[]; };
const saveLib  = v   => lsSet(IMG_KEY, v);

// ─── Nutrition config ─────────────────────────────────────────────────────────
// ── Guía de inicio guiada: un paso por cada sección principal ──
const TOUR_STEPS = [
  {tabId:"today", title:"👋 Pestaña Hoy", message:"Acá arrancás: tu resumen del día con anillos de progreso, la frase motivacional, el clima, cuánto llevás de tu alimentación planificada, y qué te toca entrenar hoy. Deslizá entre 'Info' y 'Hoy' arriba."},
  {tabId:"routine", title:"🏋 Ejercicios", message:"Tu banco de ejercicios propio, organizado por grupo muscular. Podés cargarlos a mano o buscarlos en internet con el botón 'Buscar en la web'. Estos son los ladrillos con los que después armás tus rutinas."},
  {tabId:"routines", title:"📋 Rutinas", message:"Armá rutinas combinando los ejercicios que cargaste, y programalas en el Calendario para que la app sepa qué día te toca cada una. También podés generar una rutina automática con IA."},
  {tabId:"nutrition", title:"🍽 Nutrición", message:"Elegí qué alimentos consumís habitualmente, armá recetas, planificá tus comidas por día de la semana, y llevá el control de tus macros según tu objetivo actual."},
  {tabId:"cardio", title:"🏃 Cardio", message:"Programá una sesión de cardio con fecha y hora, o registrá una que ya hiciste. La app calcula el ritmo, las calorías quemadas, y hasta guarda el clima de ese momento."},
  {tabId:"stats", title:"📊 Estadísticas", message:"Mirá tu progreso real: qué ejercicios hacés más seguido, cómo evolucionan tus pesos en cada uno, tu historial de cardio, y tu cumplimiento de macros semana a semana."},
  {tabId:"share", title:"📤 Compartir", message:"Exportá tus rutinas para mandárselas a alguien, o hacé una copia de seguridad completa de todos tus datos — te recomiendo guardarla de vez en cuando."},
];

const GOALS = {
  perdida:      {label:"Pérdida de grasa", adj:-500, prot:2.2, carb:.30, gras:.25, color:"#ff4455"},
  definicion:   {label:"Definición",      adj:-250, prot:2.0, carb:.35, gras:.25, color:"#ff9500"},
  mantenimiento:{label:"Mantenimiento",   adj:0,    prot:1.8, carb:.45, gras:.30, color:"#00b4ff"},
  fuerza:       {label:"Fuerza",          adj:200,  prot:2.0, carb:.45, gras:.30, color:"#00e87a"},
  hipertrofia:  {label:"Hipertrofia",     adj:300,  prot:2.2, carb:.50, gras:.25, color:"#e8ff00"},
  resistencia:  {label:"Resistencia",     adj:100,  prot:1.6, carb:.55, gras:.25, color:"#c084fc"},
};

// ── Historial de objetivos: cada período tiene un objetivo, fecha de inicio y fecha de fin planeada.
// Así, si mirás datos de un período viejo, se compara contra el objetivo que estaba vigente en ese momento
// (no el actual) — y al vencerse el plazo planeado, se avisa para elegir el próximo objetivo.
function addMonthsISO(dateISO, months){
  const d = new Date(dateISO+"T12:00:00");
  d.setMonth(d.getMonth()+months);
  return d.toISOString().slice(0,10);
}
function addDaysISO(dateISO, days){
  const d = new Date(dateISO+"T12:00:00");
  d.setDate(d.getDate()+days);
  return d.toISOString().slice(0,10);
}
// Devuelve el período de objetivo activo HOY (el más reciente con fecha de inicio <= hoy), o null si no hay ninguno.
function getActiveObjectivePeriod(history){
  if(!Array.isArray(history)||history.length===0) return null;
  const today = nowISO();
  // Los períodos siempre se agregan al final del array al crearse, así que si dos tienen la
  // misma fecha de inicio (ej: cambiaste el objetivo dos veces el mismo día), el orden del
  // array desempata correctamente — el último agregado es el vigente, no una fecha ambigua.
  const candidatos = history.filter(p=>p.startDate<=today);
  return candidatos[candidatos.length-1]||null;
}
// Devuelve qué objetivo estaba vigente en una fecha específica (para no romper el historial al cambiar de objetivo).
function getObjectiveForDate(history, dateISO){
  if(!Array.isArray(history)||history.length===0) return null;
  const candidatos = history.filter(p=>p.startDate<=dateISO);
  return candidatos[candidatos.length-1]?.objetivo || null;
}
// ¿El período activo ya venció su fecha de fin planeada? (para mostrar el aviso de elegir el próximo objetivo)
function isActiveObjectiveExpired(history){
  const active = getActiveObjectivePeriod(history);
  if(!active || !active.endDate) return false;
  return active.endDate < nowISO();
}

const SUPLS = {
  perdida:     [["Proteína whey","1.5–2g/kg"],["L-Carnitina","1–2g pre-entreno"],["Omega-3","2–3g/día"],["Vitamina D3","2000 UI"],["Magnesio","300mg noche"]],
  definicion:  [["Proteína whey","2g/kg"],["BCAA","5–10g/sesión"],["Omega-3","2–3g/día"],["Vitamina D3","2000 UI"],["Zinc","25mg"]],
  mantenimiento:[["Proteína whey","1.8g/kg"],["Omega-3","1–2g/día"],["Vitamina D3","1000 UI"],["Magnesio","300mg"],["Multivitamínico","1/día"]],
  fuerza:      [["Creatina","5g/día"],["Proteína whey","2g/kg"],["Beta-Alanina","3.2g/día"],["Omega-3","3g/día"],["Vitamina D3","3000 UI"],["ZMA","nocturno"]],
  hipertrofia: [["Creatina","5g/día"],["Proteína whey","2.2g/kg"],["BCAA/EAA","10g intra"],["Omega-3","3g/día"],["Vitamina D3","3000 UI"],["Zinc","30mg"],["Magnesio","400mg noche"]],
  resistencia: [["Proteína whey","1.6g/kg"],["Electrolitos","según sudor"],["Omega-3","2–3g/día"],["Vitamina D3","2000 UI"],["CoQ10","100mg"],["Vitamina B12","1000mcg"]],
};

// Nivel de actividad del día según lo que tenga programado — así las kcal objetivo
// no son un número fijo todos los días, sino que suben o bajan según cuánto te vas a mover.
function getDayActivityFactor({hasRoutine, hasCardio, isRest}){
  if(isRest) return 1.2;                         // descanso — sedentario ese día
  if(hasRoutine && hasCardio) return 1.725;       // entreno + cardio el mismo día — muy activo
  if(hasRoutine || hasCardio) return 1.55;        // solo entreno o solo cardio — moderado
  return 1.375;                                    // nada programado (sin marcar descanso) — ligero
}
function calcTargets(profile, dayActivityOverride) {
  const {peso=80,altura=178,edad=36,sexo="m",actividad=1.55,objetivo="hipertrofia"} = profile||{};
  const activityUsed = dayActivityOverride!=null ? dayActivityOverride : actividad;
  const tmb = sexo==="m" ? 10*peso+6.25*altura-5*edad+5 : 10*peso+6.25*altura-5*edad-161;
  const tdee = tmb*activityUsed;
  const cfg  = GOALS[objetivo]||GOALS.hipertrofia;
  const meta = rnd(tdee+cfg.adj);
  const protG= rnd(cfg.prot*peso);
  const grasK= rnd(meta*cfg.gras);
  const carbG= rnd((meta-protG*4-grasK)/4);
  return {meta, protG, carbG, grasG:rnd(grasK/9), tmb:rnd(tmb), tdee:rnd(tdee)};
}

// ─── Claude API (via Netlify Function proxy) ──────────────────────────────────
// 🔧 Mientras probás la app, dejá esto en false para que las funciones de IA
// no llamen a la API (evita gastar créditos). Cambiá a true cuando quieras
// probar escaneo de etiquetas, generación de recetas, rutinas con IA, etc.
const AI_ENABLED = false;

const API_PROXY = "/api/claude";

async function callClaude(messages, sys="") {
  if(!AI_ENABLED) {
    throw {code:"AI_DISABLED", message:"🔧 Las funciones de IA están desactivadas mientras se prueba la app. Podés activarlas cambiando AI_ENABLED a true en el código."};
  }
  const res = await fetch(API_PROXY, {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({
      model:"claude-haiku-4-5", max_tokens:1200,
      ...(sys?{system:sys}:{}), messages
    })
  });
  const d = await res.json();
  // Detectar errores específicos de Anthropic y propagarlos
  if(d.error) {
    const msg = d.error.message||"";
    if(msg.includes("credit balance") || msg.includes("billing"))
      throw {code:"NO_CREDITS", message:"💳 Sin créditos en la API. Recargá en console.anthropic.com → Billing."};
    if(msg.includes("invalid_api_key") || msg.includes("authentication"))
      throw {code:"BAD_KEY", message:"🔑 API key inválida. Verificá en console.anthropic.com."};
    if(msg.includes("overloaded"))
      throw {code:"OVERLOADED", message:"⏳ Servicio de IA temporalmente saturado. Intentá en unos minutos."};
    throw {code:"API_ERROR", message:"❌ Error de IA: "+msg};
  }
  return d.content?.find(b=>b.type==="text")?.text?.trim()||"";
}
async function genSvgExercise(name) {
  return callClaude([{role:"user",content:`Generá un SVG 300x200px de figura humana estilizada ejecutando el ejercicio "${name}". Fondo #12121a, figura en #e8ff00, estilo minimalista. SOLO el SVG, sin explicación ni markdown.`}]);
}

async function genSvgRecipe(name) {
  return callClaude([{role:"user",content:`Generá un SVG 300x200px ilustrativo y minimalista del plato "${name}" visto desde arriba (como un plato de comida sobre la mesa). Fondo #12121a, formas simples en tonos #e8ff00 y colores cálidos pastel, estilo icono plano minimalista. SOLO el SVG, sin explicación ni markdown.`}]);
}

// Genera lista de ejercicios por grupo muscular usando Claude directamente (sin web search)
async function searchExercisesByGroup(group, query="") {
  if(!AI_ENABLED) {
    throw {code:"AI_DISABLED", message:"🔧 La búsqueda con IA está desactivada mientras se prueba la app."};
  }
  const extra = query ? ` relacionados con "${query}"` : "";
  const prompt = `Dame una lista de 8 ejercicios de gimnasio para ${group}${extra}. Incluí variedad de equipamiento y niveles.
Respondé SOLO con este JSON (array, sin markdown):
[{"nombre":"nombre en español","grupoMuscular":"${group}","musculosPrimarios":["m1"],"musculosSecundarios":["m1"],"descripcion":"2 oraciones: cómo se ejecuta y qué trabaja","consejosTecnicos":"1-2 tips de técnica o seguridad","equipamiento":"Barra|Mancuernas|Máquina|Cable|Peso corporal|Kettlebell|Mixto","nivel":"Principiante|Intermedio|Avanzado"}]`;
  const res = await fetch(API_PROXY, {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({
      model:"claude-haiku-4-5", max_tokens:2000,
      system:"Sos experto en entrenamiento físico de gimnasio. Respondé SOLO con el JSON pedido, sin markdown ni texto extra. Usá nombres en español argentino.",
      messages:[{role:"user",content:prompt}]
    })
  });
  const d = await res.json();
  const txt = (d.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("").trim();
  try { return JSON.parse(txt.replace(/```json|```/g,"").trim()); }
  catch { return []; }
}
async function scanLabel(b64, mime) {
  const prompt = `Analizá esta tabla de información nutricional. Extraé los valores por porción Y por 100g.
Respondé SOLO con JSON válido sin markdown ni explicación:
{"nombre":"nombre del producto si está visible, sino Producto escaneado","por_porcion":{"porcion":"tamaño ej 32g","kcal":0,"carbos":0,"azucares":0,"proteinas":0,"grasas":0,"grasas_sat":0,"fibra":0,"sodio":0},"por_100g":{"porcion":"100g","kcal":0,"carbos":0,"azucares":0,"proteinas":0,"grasas":0,"grasas_sat":0,"fibra":0,"sodio":0},"porciones_envase":0}
Si algún valor no está disponible usá 0. Si no es tabla nutricional respondé: {"error":"no_label"}`;
  const txt = await callClaude([{role:"user",content:[
    {type:"image",source:{type:"base64",media_type:mime,data:b64}},
    {type:"text",text:prompt}
  ]}]);
  try{return JSON.parse(txt.replace(/```json|```/g,"").trim());}catch{return{error:"parse"};}
}
async function getSuggestions(totals, tgt, goalLabel) {
  const txt = await callClaude([{role:"user",content:
    `Objetivo "${goalLabel}". Déficit: ${rnd(tgt.meta-totals.kcal)} kcal, ${rnd(tgt.protG-totals.prot)}g prot, ${rnd(tgt.carbG-totals.carb)}g carb.
Alimentos hoy: ${totals.names.join(", ")||"ninguno"}.
Sugerí 3 alimentos concretos. SOLO JSON sin markdown:
[{"alimento":"...","porcion":"...","cubre":"...","kcal":0,"prot":0,"carb":0,"gras":0}]`}],
  "Sos nutricionista deportivo argentino. Respondé en español.");
  try{return JSON.parse(txt.replace(/```json|```/g,"").trim());}catch{return[];}
}

// Genera una receta para una comida específica usando solo alimentos permitidos
async function generateMealRecipe(mealType, allowedFoods, targetMacros, goalLabel, excludeIds=[]) {
  const foodList = allowedFoods.filter(f=>!excludeIds.includes(f.id)).map(f=>f.nombre).join(", ");
  const prompt = `Generá una receta de ${mealType} usando SOLO estos alimentos disponibles: ${foodList||"alimentos comunes argentinos"}.
Objetivo nutricional para esta comida: ${rnd(targetMacros.kcal)} kcal, ${rnd(targetMacros.prot)}g proteína, ${rnd(targetMacros.carb)}g carbohidratos, ${rnd(targetMacros.gras)}g grasas.
Contexto: dieta para objetivo "${goalLabel}".
Respondé SOLO JSON sin markdown:
{"nombre":"nombre de la receta","ingredientes":[{"item":"...","cantidad":"...","kcal":0,"prot":0,"carb":0,"gras":0}],"preparacion":"pasos breves en 2-3 oraciones","kcal_total":0,"prot_total":0,"carb_total":0,"gras_total":0}`;
  const txt = await callClaude([{role:"user",content:prompt}],
    "Sos chef y nutricionista deportivo argentino. Usá solo los alimentos indicados. Respondé en español, sin inventar alimentos fuera de la lista salvo condimentos básicos (sal, aceite, especias).");
  try{return JSON.parse(txt.replace(/```json|```/g,"").trim());}catch{return null;}
}

// Reemplaza un ingrediente puntual de una receta ya generada
async function replaceIngredient(recipe, ingredientName, allowedFoods, excludeIds=[]) {
  const foodList = allowedFoods.filter(f=>!excludeIds.includes(f.id)).map(f=>f.nombre).join(", ");
  const prompt = `Esta receta es: "${recipe.nombre}" con ingredientes: ${recipe.ingredientes.map(i=>`${i.item} (${i.cantidad})`).join(", ")}.
Necesito reemplazar "${ingredientName}" por otra opción de esta lista de alimentos disponibles: ${foodList}.
Mantené la cantidad de macros lo más similar posible.
Respondé SOLO JSON sin markdown:
{"item":"nuevo ingrediente","cantidad":"...","kcal":0,"prot":0,"carb":0,"gras":0}`;
  const txt = await callClaude([{role:"user",content:prompt}],
    "Sos nutricionista deportivo. Solo podés elegir de la lista de alimentos disponibles dada. Respondé en español.");
  try{return JSON.parse(txt.replace(/```json|```/g,"").trim());}catch{return null;}
}

// Genera un día completo de comidas (4 comidas) respetando alimentos permitidos y macros totales
async function generateFullDayPlan(allowedFoods, tgt, goalLabel, mealTimes) {
  const foodList = allowedFoods.map(f=>f.nombre).join(", ");
  const prompt = `Armá un plan de alimentación de UN DÍA con 4 comidas (Desayuno ${mealTimes.desayuno}, Almuerzo ${mealTimes.almuerzo}, Merienda ${mealTimes.merienda}, Cena ${mealTimes.cena}).
Usá SOLO estos alimentos disponibles: ${foodList||"alimentos comunes argentinos"}.
Meta total del día: ${tgt.meta} kcal, ${tgt.protG}g proteína, ${tgt.carbG}g carbohidratos, ${tgt.grasG}g grasas.
Objetivo: "${goalLabel}".
Distribuí las calorías de forma realista entre las 4 comidas.
Respondé SOLO JSON sin markdown:
{"desayuno":{"nombre":"...","ingredientes":[{"item":"...","cantidad":"...","kcal":0,"prot":0,"carb":0,"gras":0}],"preparacion":"..."},
"almuerzo":{"nombre":"...","ingredientes":[...],"preparacion":"..."},
"merienda":{"nombre":"...","ingredientes":[...],"preparacion":"..."},
"cena":{"nombre":"...","ingredientes":[...],"preparacion":"..."}}`;
  const txt = await callClaude([{role:"user",content:prompt}],
    "Sos nutricionista deportivo argentino. Usá solo los alimentos de la lista dada (más condimentos básicos). Respondé en español.");
  try{return JSON.parse(txt.replace(/```json|```/g,"").trim());}catch{return null;}
}

// Busca dietas fitness recomendadas en internet acordes al objetivo
async function searchFitnessDiets(goalLabel, tgt) {
  const txt = await callClaude([{role:"user",content:
    `Recomendame 3 enfoques de dieta fitness conocidos y respaldados (ej: dieta alta en proteína, dieta mediterránea adaptada a deporte, dieta basada en carb cycling, IIFYM, etc) que sean apropiados para el objetivo "${goalLabel}" con una meta de ${tgt.meta} kcal/día.
Para cada uno explicá brevemente en qué consiste y por qué encaja con este objetivo.
Respondé SOLO JSON sin markdown:
[{"nombre":"...","descripcion":"2-3 oraciones explicando el enfoque","macros_aprox":"ej: 40% prot / 35% carb / 25% gras"}]`}],
  "Sos nutricionista deportivo con conocimiento de enfoques dietéticos populares y su evidencia. Respondé en español, de forma objetiva.");
  try{return JSON.parse(txt.replace(/```json|```/g,"").trim());}catch{return [];}
}

// Genera rutinas localmente usando los ejercicios disponibles (sin API externa)
function generateRoutinesLocal(exercises, goalLabel, daysPerWeek) {
  // Agrupar ejercicios por grupo muscular
  const byGroup = {};
  exercises.forEach(ex => {
    const g = ex.muscleGroup || "General";
    if(!byGroup[g]) byGroup[g] = [];
    byGroup[g].push(ex);
  });
  const pick = (group, n) => (byGroup[group]||[]).slice(0,n);
  const pickMany = (groups, n) => {
    let pool = groups.flatMap(g=>byGroup[g]||[]);
    // mezclar
    pool = pool.sort(()=>Math.random()-.5);
    return pool.slice(0,n);
  };

  // Parámetros según objetivo
  const isStrength  = goalLabel.toLowerCase().includes("fuerza");
  const isEndurance = goalLabel.toLowerCase().includes("resistencia");
  const sets = isStrength ? 5 : 3;
  const reps = isStrength ? 5 : isEndurance ? 15 : 10;

  let templates = [];

  if(daysPerWeek <= 3) {
    // Full Body x3
    const days = ["A","B","C"].slice(0, daysPerWeek);
    const groups1 = ["Pecho","Hombros","Tríceps"];
    const groups2 = ["Espalda","Bíceps"];
    const groups3 = ["Piernas","Glúteos","Abdomen"];
    templates = days.map((d,i) => {
      const push = pickMany(groups1, 2);
      const pull = pickMany(groups2, 2);
      const legs = pickMany(groups3, 2);
      return {
        nombre: `Día ${d} - Full Body`,
        descripcion: `Entrenamiento full body con foco en variación de estímulo`,
        ejercicios: [...push,...pull,...legs].filter(e=>e).map(ex=>({
          nombre:ex.name, sets, reps, peso:0, notas:""
        }))
      };
    });
  } else if(daysPerWeek === 4) {
    // Upper / Lower x2
    const upperA = [...pickMany(["Pecho","Hombros","Tríceps"],3),...pickMany(["Espalda","Bíceps"],2)];
    const lowerA = [...pickMany(["Piernas","Glúteos"],3),...pickMany(["Abdomen"],2)];
    templates = [
      {nombre:"Upper A - Empuje + Jale", descripcion:"Tren superior: pecho, espalda, hombros y brazos",
        ejercicios:upperA.map(ex=>({nombre:ex.name,sets,reps,peso:0,notas:""}))},
      {nombre:"Lower A - Piernas", descripcion:"Tren inferior: cuádriceps, isquiotibiales y glúteos",
        ejercicios:lowerA.map(ex=>({nombre:ex.name,sets,reps,peso:0,notas:""}))},
      {nombre:"Upper B - Variación", descripcion:"Tren superior con variantes de los mismos grupos",
        ejercicios:[...pickMany(["Pecho","Hombros","Tríceps"],3),...pickMany(["Espalda","Bíceps"],2)]
          .map(ex=>({nombre:ex.name,sets,reps,peso:0,notas:""}))},
      {nombre:"Lower B - Variación", descripcion:"Tren inferior con variantes",
        ejercicios:[...pickMany(["Piernas","Glúteos"],3),...pickMany(["Abdomen"],1)]
          .map(ex=>({nombre:ex.name,sets,reps,peso:0,notas:""}))},
    ];
  } else {
    // PPL para 5-6 días
    const pplBase = [
      {nombre:"Push - Empuje", descripcion:"Pecho, hombros y tríceps",
        grupos:["Pecho","Hombros","Tríceps"], n:6},
      {nombre:"Pull - Jalón", descripcion:"Espalda y bíceps",
        grupos:["Espalda","Bíceps"], n:6},
      {nombre:"Legs - Piernas", descripcion:"Cuádriceps, isquiotibiales, glúteos y abdomen",
        grupos:["Piernas","Glúteos","Abdomen"], n:6},
    ];
    const days = daysPerWeek === 6
      ? [...pplBase, {nombre:"Push B - Variación", descripcion:"Segunda sesión de empuje con variantes",grupos:["Pecho","Hombros","Tríceps"],n:5},
          {nombre:"Pull B - Variación", descripcion:"Segunda sesión de jalón con variantes",grupos:["Espalda","Bíceps"],n:5},
          {nombre:"Legs B - Variación", descripcion:"Segunda sesión de piernas con variantes",grupos:["Piernas","Glúteos"],n:5}]
      : pplBase.slice(0, daysPerWeek);
    templates = days.map(d=>({
      nombre: d.nombre,
      descripcion: d.descripcion,
      ejercicios: pickMany(d.grupos, d.n).map(ex=>({nombre:ex.name,sets,reps,peso:0,notas:""}))
    }));
  }

  // Filtrar rutinas con al menos 2 ejercicios
  return templates.filter(r=>r.ejercicios.length>=2);
}


// ─── UI base ──────────────────────────────────────────────────────────────────
const Btn = ({children,onClick,v="primary",sz="md",full=false,disabled=false,style:sx={}}) => {
  const vs={primary:{background:T.accent,color:"#000"},ghost:{background:"transparent",color:T.text,border:`1px solid ${T.border}`},
    danger:{background:T.red,color:"#fff"},success:{background:T.green,color:"#000"},info:{background:T.blue,color:"#000"},
    subtle:{background:T.surface,color:T.text,border:`1px solid ${T.border}`}};
  return <button onClick={onClick} disabled={disabled}
    style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,
      borderRadius:8,fontWeight:600,border:"none",cursor:disabled?"not-allowed":"pointer",
      width:full?"100%":"auto",fontSize:sz==="sm"?12:14,padding:sz==="sm"?"6px 12px":"10px 20px",
      opacity:disabled?.6:1,...vs[v],...sx}}
    onMouseEnter={e=>{if(!disabled)e.currentTarget.style.opacity=".82"}}
    onMouseLeave={e=>e.currentTarget.style.opacity="1"}>{children}</button>;
};

function DateTimePickerModal({type, value, onChange, onClose, min, max}) {
  const [temp, setTemp] = useState(value||"");
  return <Modal title={type==="date"?"📅 ELEGIR FECHA":"🕐 ELEGIR HORA"} onClose={onClose} width={320}>
    <div style={{display:"flex",flexDirection:"column",gap:16,alignItems:"center"}}>
      <input type={type} value={temp} onChange={e=>setTemp(e.target.value)} min={min} max={max} autoFocus
        style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,
          padding:"14px 16px",fontSize:18,width:"100%",boxSizing:"border-box",textAlign:"center",colorScheme:"dark"}}/>
      <Btn full v="success" disabled={!temp} onClick={()=>{onChange(temp);onClose();}}>
        <Icon n="check" s={15}/>Listo
      </Btn>
    </div>
  </Modal>;
}

function Field({label,value,onChange,type="text",placeholder="",min,max,step,textarea=false,required=false}) {
  const [showPicker, setShowPicker] = useState(false);
  // Para campos numéricos: mientras el usuario está escribiendo (ej: "5," antes de completar "5,1"),
  // no queremos que el valor ya redondeado/convertido del padre le pise lo que está tipeando.
  // Guardamos el texto tal cual se escribe, y solo lo resincronizamos si el NÚMERO cambió desde afuera.
  const [numText, setNumText] = useState(()=>value==null?"":String(value));
  useEffect(()=>{
    if(type!=="number") return;
    if(toNum(numText) !== toNum(value)) setNumText(value==null?"":String(value));
  },[value, type]);

  // Fecha y hora: en vez del selector nativo (que en muchos celulares se desborda del recuadro),
  // se abre una ventana emergente propia con espacio de sobra para cargar el dato.
  if(type==="date" || type==="time"){
    const display = value
      ? (type==="date" ? fmtDateFull(value) : value)
      : (type==="date" ? "Elegir fecha..." : "Elegir hora...");
    return <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {label&&<label style={{fontSize:11,color:T.muted,fontWeight:600,letterSpacing:".05em",textTransform:"uppercase"}}>
        {label}{required&&<span style={{color:T.accent}}> *</span>}
      </label>}
      <button type="button" onClick={()=>setShowPicker(true)}
        style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
          color:value?T.text:T.muted,padding:"9px 12px",fontSize:13,height:38,width:"100%",
          boxSizing:"border-box",textAlign:"left",cursor:"pointer",
          display:"flex",alignItems:"center",gap:6}}>
        <span>{type==="date"?"📅":"🕐"}</span>
        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{display}</span>
      </button>
      {showPicker&&<DateTimePickerModal type={type} value={value} onChange={onChange}
        onClose={()=>setShowPicker(false)} min={min} max={max}/>}
    </div>;
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {label&&<label style={{fontSize:11,color:T.muted,fontWeight:600,letterSpacing:".05em",textTransform:"uppercase"}}>
        {label}{required&&<span style={{color:T.accent}}> *</span>}
      </label>}
      {textarea
        ? <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
            style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,
              padding:"9px 12px",fontSize:13,minHeight:70,resize:"vertical",width:"100%",boxSizing:"border-box"}}/>
        : <input type={type==="number"?"text":type} value={type==="number"?numText:value}
            onChange={e=>{
              if(type==="number"){ setNumText(e.target.value); onChange(e.target.value); }
              else onChange(e.target.value);
            }}
            placeholder={placeholder} min={min} max={max} step={step}
            inputMode={type==="number"?"decimal":undefined}
            pattern={type==="number"?"[0-9]*[.,]?[0-9]*":undefined}
            style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,
              padding:"9px 12px",fontSize:13,height:38,width:"100%",minWidth:0,boxSizing:"border-box"}}/>}
    </div>
  );
}

const Sel = ({label,value,onChange,options}) => (
  <div style={{display:"flex",flexDirection:"column",gap:4}}>
    {label&&<label style={{fontSize:11,color:T.muted,fontWeight:600,letterSpacing:".05em",textTransform:"uppercase"}}>{label}</label>}
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,
        padding:"9px 12px",fontSize:13,height:38,width:"100%",minWidth:0}}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

// ── Bloqueo de scroll de fondo — robusto para iOS también ──
// overflow:hidden en el body no alcanza en Safari/iOS (el "rebote" elástico igual se filtra).
// Por eso además interceptamos el touchmove a nivel de documento completo.
let _scrollLockCount = 0;
let _scrollLockPrevOverflow = "";
function _scrollLockTouchBlocker(e){
  if(e.target.closest('[data-scroll-lock-allow]')) return; // dejamos scrollear el contenido propio del modal
  e.preventDefault();
}
function useScrollLock(active){
  useEffect(()=>{
    if(!active) return;
    if(_scrollLockCount===0){
      _scrollLockPrevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("touchmove", _scrollLockTouchBlocker, {passive:false});
    }
    _scrollLockCount++;
    return () => {
      _scrollLockCount--;
      if(_scrollLockCount<=0){
        _scrollLockCount = 0;
        document.body.style.overflow = _scrollLockPrevOverflow;
        document.removeEventListener("touchmove", _scrollLockTouchBlocker);
      }
    };
  },[active]);
}

const Modal = ({title,onClose,children,width=480}) => {
  useScrollLock(true); // mientras el modal está montado, el fondo queda completamente bloqueado

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.8)",display:"flex",
      alignItems:"center",justifyContent:"center",zIndex:999,padding:16}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="fu" data-scroll-lock-allow style={{background:T.card,borderRadius:16,width:"100%",maxWidth:width,
        maxHeight:"90vh",overflowY:"auto",border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 0",marginBottom:16}}>
          <h2 style={{fontFamily:"'Bebas Neue'",fontSize:22,color:T.accent,letterSpacing:".05em"}}>{title}</h2>
          <button onClick={onClose} style={{background:"none",color:T.muted,border:"none",fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{padding:"0 20px 20px"}}>{children}</div>
      </div>
    </div>
  );
};

const Badge = ({children,color=T.accent}) => (
  <span style={{background:color+"22",color,fontSize:11,fontWeight:700,padding:"3px 8px",
    borderRadius:20,letterSpacing:".04em",whiteSpace:"nowrap"}}>{children}</span>
);

const Spinner = () => (
  <div style={{width:18,height:18,border:`2px solid ${T.border}`,borderTopColor:T.accent,
    borderRadius:"50%",animation:"spin .7s linear infinite",flexShrink:0}}/>
);

const MacroBar = ({label,value,target,color}) => {
  const pct=Math.min(100,rnd((value/(target||1))*100));
  const over=value>target;
  return <div style={{marginBottom:8}}>
    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
      <span style={{color:T.muted}}>{label}</span>
      <span><b style={{color:over?T.red:T.text}}>{rnd(value)}</b><span style={{color:T.muted}}>/{target}g</span></span>
    </div>
    <div style={{height:5,background:T.surface,borderRadius:3,overflow:"hidden"}}>
      <div style={{height:"100%",borderRadius:3,background:over?T.red:color,width:`${pct}%`,transition:"width .4s"}}/>
    </div>
  </div>;
};

const Avatar = ({user,size=36}) => {
  const name=(user?.name||"?");
  const initials=name.split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase();
  const colors=[T.accent,T.green,T.blue,T.orange,T.purple];
  const color=colors[name.charCodeAt(0)%colors.length];
  return <div style={{width:size,height:size,borderRadius:"50%",background:color+"33",
    border:`2px solid ${color}`,display:"flex",alignItems:"center",justifyContent:"center",
    fontFamily:"'Bebas Neue'",fontSize:size*.38,color,flexShrink:0}}>{initials}</div>;
};

const Icon = ({n,s=20,c="currentColor"}) => {
  const P={
    dumbbell:<><path d="M6 4v16M18 4v16M4 9h2M18 9h2M4 15h2M18 15h2M8 9h8v6H8z" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    layers:<><polygon points="12 2 2 7 12 12 22 7 12 2" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/><polyline points="2 17 12 22 22 17" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round"/><polyline points="2 12 12 17 22 12" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round"/></>,
    chart:<><path d="M4 20V14M9 20V9M14 20V13M19 20V5" stroke={c} strokeWidth="2" strokeLinecap="round"/></>,
    cardio:<><path d="M3 12h4l3-9 4 18 3-9h4" stroke={c} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    trend:<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" stroke={c} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    share:<><circle cx="18" cy="5" r="3" stroke={c} strokeWidth="1.8" fill="none"/><circle cx="6" cy="12" r="3" stroke={c} strokeWidth="1.8" fill="none"/><circle cx="18" cy="19" r="3" stroke={c} strokeWidth="1.8" fill="none"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke={c} strokeWidth="1.8"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke={c} strokeWidth="1.8"/></>,
    plus:<><path d="M12 5v14M5 12h14" stroke={c} strokeWidth="2" strokeLinecap="round"/></>,
    edit:<><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke={c} strokeWidth="1.8" fill="none"/></>,
    trash:<><path d="M3 6h18M19 6l-1 14H6L5 6M10 6V4h4v2" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    check:<><path d="M4 12l6 6L20 6" stroke={c} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    camera:<><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke={c} strokeWidth="1.8" fill="none"/><circle cx="12" cy="13" r="4" stroke={c} strokeWidth="1.8" fill="none"/></>,
    image:<><rect x="3" y="3" width="18" height="18" rx="2" stroke={c} strokeWidth="1.8" fill="none"/><circle cx="8.5" cy="8.5" r="1.5" fill={c}/><path d="M21 15l-5-5L5 21" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round"/></>,
    download:<><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    folder:<><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" stroke={c} strokeWidth="1.8" fill="none"/></>,
    moon:<><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round"/></>,
    home:<><path d="M3 11l9-8 9 8M5 10v10h14V10" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    today:<><rect x="3" y="4" width="18" height="18" rx="2" stroke={c} strokeWidth="1.8" fill="none"/><path d="M16 2v4M8 2v4M3 10h18" stroke={c} strokeWidth="1.8" strokeLinecap="round"/><circle cx="12" cy="15" r="2" fill={c}/></>,
  };
  return <svg width={s} height={s} viewBox="0 0 24 24" style={{flexShrink:0}}>{P[n]||null}</svg>;
};

// ─── Image Library ────────────────────────────────────────────────────────────
function ImageLibModal({onSelect,onClose}) {
  const [lib,setLib]=useState(loadLib);
  const [genName,setGenName]=useState("");
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");

  const generate=async()=>{
    if(!genName.trim())return;
    setLoading(true);setErr("");
    try{
      const svg=await genSvgExercise(genName.trim());
      if(!svg.startsWith("<svg")){setErr("No se pudo generar. Intentá de nuevo.");setLoading(false);return;}
      const url="data:image/svg+xml;charset=utf-8,"+encodeURIComponent(svg);
      const item={id:uid(),name:genName.trim(),url};
      const updated=[item,...lib]; setLib(updated); saveLib(updated); setGenName("");
    }catch{setErr("Error de conexión.");}
    setLoading(false);
  };

  return <Modal title="BIBLIOTECA IA" onClose={onClose} width={520}>
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{background:T.surface,borderRadius:10,padding:14,border:`1px solid ${T.accent}33`}}>
        <p style={{fontSize:13,color:T.muted,marginBottom:10}}>Escribí el nombre del ejercicio y la IA generará una ilustración.</p>
        <div style={{display:"flex",gap:8}}>
          <input value={genName} onChange={e=>setGenName(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&!loading&&generate()}
            placeholder="Ej: Sentadilla, Press banca..."
            style={{flex:1,background:T.card,border:`1px solid ${T.border}`,borderRadius:8,
              color:T.text,padding:"9px 12px",fontSize:13}}/>
          <Btn onClick={generate} disabled={loading}>
            {loading?<><Spinner/>Generando...</>:"✨ Generar"}
          </Btn>
        </div>
        {err&&<p style={{fontSize:12,color:T.red,marginTop:8}}>{err}</p>}
      </div>
      {lib.length===0
        ? <p style={{color:T.muted,fontSize:13,textAlign:"center",padding:20}}>No hay imágenes generadas aún.</p>
        : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8}}>
            {lib.map(img=>(
              <div key={img.id} style={{borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`,cursor:"pointer"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
                onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                <img src={img.url} alt={img.name} style={{width:"100%",height:90,objectFit:"cover",display:"block"}}/>
                <div style={{padding:"6px 8px",background:T.surface}}>
                  <div style={{fontSize:11,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginBottom:5}}>{img.name}</div>
                  <div style={{display:"flex",gap:4}}>
                    <button onClick={()=>onSelect(img.url)} style={{flex:1,background:T.accent,color:"#000",border:"none",borderRadius:5,padding:"4px 0",fontSize:11,fontWeight:700,cursor:"pointer"}}>✓ Usar</button>
                    <button onClick={()=>{const u=lib.filter(i=>i.id!==img.id);setLib(u);saveLib(u);}} style={{background:T.red+"33",color:T.red,border:"none",borderRadius:5,padding:"4px 6px",fontSize:11,cursor:"pointer"}}>🗑</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
      }
    </div>
  </Modal>;
}

// ─── Exercise Form ────────────────────────────────────────────────────────────
// ─── Exercise Search Modal (buscar por grupo muscular) ────────────────────────
function ExerciseSearchModal({onSaveAll, onClose}) {
  const GROUPS = ["Pecho","Espalda","Hombros","Bíceps","Tríceps","Piernas","Abdomen","Glúteos","Cardio"];
  const NIVEL_COLOR = {Principiante:T.green, Intermedio:T.orange, Avanzado:T.red};
  const EQUIP_ICON  = {"Barra":"🏋","Mancuernas":"💪","Máquina":"⚙","Cable":"🔗",
    "Peso corporal":"🤸","Kettlebell":"🫙","Mixto":"🔀"};

  const [group,    setGroup]    = useState("Pecho");
  const [query,    setQuery]    = useState("");
  const [results,  setResults]  = useState([]);
  const [selected, setSelected] = useState([]);  // índices seleccionados
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);
  const [error,    setError]    = useState("");

  const [loadingImgs, setLoadingImgs] = useState(false);

  const doSearch = async () => {
    setLoading(true); setError(""); setResults([]); setSelected([]); setSearched(false);
    try {
      const list = await searchExercisesByGroup(group, query.trim());
      if(!list || list.length===0) {
        setError("No se encontraron ejercicios. Intentá con otro grupo o término.");
        setSearched(true); setLoading(false); return;
      }
      // Mostrar resultados inmediatamente sin esperar imágenes
      setResults(list.map(ex=>({...ex, photo:""})));
      setSelected(list.map((_,i)=>i));
      setSearched(true); setLoading(false);

      // Generar imágenes SVG en paralelo (no bloquea la UI)
      setLoadingImgs(true);
      const withPhotos = await Promise.all(list.map(async ex => {
        try {
          const svg = await genSvgExercise(ex.nombre);
          const photo = svg && svg.trim().startsWith("<svg")
            ? "data:image/svg+xml;charset=utf-8,"+encodeURIComponent(svg)
            : "";
          return {...ex, photo};
        } catch { return {...ex, photo:""}; }
      }));
      setResults(withPhotos);
      setLoadingImgs(false);
    } catch(err) {
      setError(err?.message||"Error al buscar. Verificá tu conexión.");
      setLoading(false);
    }
  };

  const toggleSel = i => setSelected(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i]);
  const selectAll  = () => setSelected(results.map((_,i)=>i));
  const clearAll   = () => setSelected([]);

  const handleSave = () => {
    const toSave = selected.map(i=>{
      const ex = results[i];
      return {
        ...ex,
        id: uid(),
        name: ex.nombre,
        muscleGroup: ex.grupoMuscular||group,
        description: [
          ex.descripcion,
          ex.consejosTecnicos?"💡 "+ex.consejosTecnicos:"",
          ex.musculosPrimarios?.length?"Músculos: "+ex.musculosPrimarios.join(", "):"",
          ex.equipamiento?"Equipo: "+ex.equipamiento:"",
          ex.variantes?.length?"Variantes: "+ex.variantes.join(", "):"",
        ].filter(Boolean).join("\n"),
        photo: ex.photo||"",
      };
    });
    // También guardar las fotos generadas en la biblioteca de imágenes IA
    const newLibEntries = toSave
      .filter(e=>e.photo)
      .map(e=>({id:uid(), name:e.name, url:e.photo}));
    if(newLibEntries.length>0){
      const current = loadLib();
      saveLib([...newLibEntries,...current]);
    }
    onSaveAll(toSave);
  };

  return (
    <Modal title="🔍 BUSCAR EJERCICIOS" onClose={onClose} width={560}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>

        {/* Grupo muscular */}
        <div>
          <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
            Grupo muscular
          </label>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {GROUPS.map(g=>(
              <button key={g} onClick={()=>setGroup(g)}
                style={{background:group===g?T.accent:T.surface,color:group===g?"#000":T.muted,
                  border:`1px solid ${group===g?T.accent:T.border}`,borderRadius:20,
                  padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Búsqueda adicional opcional */}
        <div style={{display:"flex",gap:8}}>
          <input value={query} onChange={e=>setQuery(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&!loading&&doSearch()}
            placeholder={`Ej: "con mancuernas", "en máquina", "para principiantes"... (opcional)`}
            style={{flex:1,background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
              color:T.text,padding:"9px 12px",fontSize:13,height:38}}/>
          <Btn onClick={doSearch} disabled={loading}>
            {loading?<><Spinner/>Buscando...</>:"🔍 Buscar"}
          </Btn>
        </div>

        {error&&<div style={{background:T.red+"22",border:`1px solid ${T.red}44`,borderRadius:8,
          padding:"10px 12px",fontSize:13,color:T.red}}>⚠ {error}</div>}

        {loading&&<div style={{textAlign:"center",padding:"30px 0",color:T.muted}}>
          <Spinner/>
          <p style={{fontSize:12,marginTop:12}}>Buscando ejercicios de {group} en internet...</p>
        </div>}

        {/* Resultados */}
        {results.length>0&&<>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <span style={{fontSize:12,color:T.muted}}>
              {results.length} ejercicios encontrados · {selected.length} seleccionados
            </span>
            <div style={{display:"flex",gap:8}}>
              <button onClick={selectAll} style={{background:"none",color:T.accent,border:"none",
                fontSize:11,fontWeight:700,cursor:"pointer"}}>Seleccionar todos</button>
              <button onClick={clearAll} style={{background:"none",color:T.muted,border:"none",
                fontSize:11,cursor:"pointer"}}>Limpiar</button>
            </div>
          </div>

          {loadingImgs&&<div style={{fontSize:11,color:T.muted,display:"flex",alignItems:"center",gap:6,padding:"4px 0"}}>
            <Spinner/> Generando ilustraciones...
          </div>}

          <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:440,overflowY:"auto"}}>
            {results.map((ex,i)=>{
              const sel = selected.includes(i);
              return (
                <div key={i} onClick={()=>toggleSel(i)}
                  style={{background:sel?T.accent+"15":T.surface,borderRadius:12,
                    border:`2px solid ${sel?T.accent:T.border}`,
                    cursor:"pointer",transition:"all .15s",overflow:"hidden",display:"flex"}}>
                  {/* Imagen / placeholder */}
                  <div style={{width:90,flexShrink:0,background:T.card,position:"relative",
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {ex.photo
                      ? <img src={ex.photo} alt={ex.nombre}
                          style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                      : <div style={{fontSize:28,opacity:.4}}>💪</div>}
                    {/* Checkbox encima de la imagen */}
                    <div style={{position:"absolute",top:6,left:6,width:20,height:20,borderRadius:5,
                      background:sel?T.accent:"rgba(0,0,0,.5)",border:`2px solid ${sel?T.accent:"#fff4"}`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:11,fontWeight:700,color:"#000"}}>
                      {sel?"✓":""}
                    </div>
                  </div>
                  {/* Info */}
                  <div style={{padding:"10px 12px",flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:4}}>
                      <span style={{fontFamily:"'Bebas Neue'",fontSize:15,color:sel?T.accent:T.text,letterSpacing:".04em"}}>
                        {ex.nombre}
                      </span>
                      {ex.nivel&&<Badge color={NIVEL_COLOR[ex.nivel]||T.muted}>{ex.nivel}</Badge>}
                      {ex.equipamiento&&<span style={{fontSize:10,color:T.muted}}>
                        {EQUIP_ICON[ex.equipamiento]||"🔧"} {ex.equipamiento}
                      </span>}
                    </div>
                    {ex.musculosPrimarios?.length>0&&
                      <div style={{fontSize:11,color:T.blue,marginBottom:4}}>
                        💪 {ex.musculosPrimarios.join(" · ")}
                        {ex.musculosSecundarios?.length>0&&
                          <span style={{color:T.muted}}> + {ex.musculosSecundarios.join(", ")}</span>}
                      </div>}
                    <p style={{fontSize:11,color:T.muted,lineHeight:1.4}}>{ex.descripcion}</p>
                    {ex.consejosTecnicos&&
                      <p style={{fontSize:10,color:T.orange,marginTop:4}}>💡 {ex.consejosTecnicos}</p>}
                  </div>
                </div>
              );
            })}
          </div>

          <Btn full v="success" onClick={handleSave} disabled={selected.length===0}>
            <Icon n="check" s={15}/>
            Agregar {selected.length} ejercicio{selected.length!==1?"s":""} a mi biblioteca
          </Btn>
        </>}

        {searched&&results.length===0&&!loading&&!error&&
          <div style={{textAlign:"center",padding:"20px",color:T.muted}}>
            <p style={{fontSize:14}}>Sin resultados para "{group}".</p>
          </div>}
      </div>
    </Modal>
  );
}

function ExerciseForm({initial,onSave,onClose}) {
  const [form,setForm]=useState({name:"",description:"",photo:"",muscleGroup:"General",...initial});
  const [showLib,setShowLib]=useState(false);
  const fileRef=useRef();
  const s=(k,v)=>setForm(f=>({...f,[k]:v}));
  return <>
    {showLib&&<ImageLibModal onSelect={url=>{s("photo",url);setShowLib(false);}} onClose={()=>setShowLib(false)}/>}
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <Field label="Nombre" value={form.name} onChange={v=>s("name",v)} required placeholder="Ej: Press de banca"/>
      <Field label="Descripción / Tips" value={form.description} onChange={v=>s("description",v)} textarea placeholder="Músculos trabajados, consejos..."/>
      <Sel label="Grupo muscular" value={form.muscleGroup} onChange={v=>s("muscleGroup",v)}
        options={["General","Pecho","Espalda","Hombros","Bíceps","Tríceps","Piernas","Abdomen","Glúteos","Cardio"].map(x=>({value:x,label:x}))}/>
      <div style={{display:"flex",flexDirection:"column",gap:4}}>
        <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>Fotografía</label>
        <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
          {form.photo
            ? <img src={form.photo} alt="" style={{width:80,height:60,objectFit:"cover",borderRadius:8,border:`1px solid ${T.border}`,flexShrink:0}}/>
            : <div style={{width:80,height:60,background:T.surface,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,border:`1px dashed ${T.border}`,flexShrink:0}}>
                <Icon n="camera" s={22}/>
              </div>}
          <div style={{display:"flex",flexDirection:"column",gap:6,flex:1}}>
            <Btn sz="sm" onClick={()=>setShowLib(true)}>✨ Biblioteca IA</Btn>
            <Btn sz="sm" v="ghost" onClick={()=>fileRef.current?.click()}><Icon n="camera" s={13}/>Subir foto</Btn>
            <input type="url" value={form.photo.startsWith("data:")?"":form.photo} onChange={e=>s("photo",e.target.value)}
              placeholder="o pegar URL" style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,
                color:T.text,padding:"5px 9px",fontSize:11,height:28,width:"100%"}}/>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}}
            onChange={async e=>{const f=e.target.files?.[0];if(!f)return;
              try{ s("photo", await compressImageFile(f)); }catch{ alert("❌ No se pudo procesar la imagen."); }
              e.target.value="";}}/>
        </div>
      </div>
      <div style={{display:"flex",gap:8,marginTop:4}}>
        <Btn full onClick={()=>{if(!form.name.trim()){alert("Nombre obligatorio");return;}onSave({...form,id:form.id||uid()});}}>
          <Icon n="check" s={15}/>Guardar ejercicio
        </Btn>
        <Btn v="ghost" onClick={onClose}>Cancelar</Btn>
      </div>
    </div>
  </>;
}

// ─── Exercise Card ────────────────────────────────────────────────────────────
function ExerciseCard({ex,onEdit,onDelete}) {
  return <div className="fu" style={{background:T.card,borderRadius:14,border:`1px solid ${T.border}`,overflow:"hidden",display:"flex"}}>
    {ex.photo
      ? <img src={ex.photo} alt={ex.name} style={{width:72,height:72,objectFit:"cover",flexShrink:0}}/>
      : <div style={{width:72,height:72,background:T.surface,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,flexShrink:0}}>
          <Icon n="image" s={24}/>
        </div>}
    <div style={{padding:"10px 12px",display:"flex",flexDirection:"column",gap:6,flex:1,minWidth:0}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:6}}>
        <span style={{fontFamily:"'Bebas Neue'",fontSize:17,letterSpacing:".05em",lineHeight:1.1}}>{ex.name}</span>
        <Badge color={T.blue}>{ex.muscleGroup}</Badge>
      </div>
      {ex.description&&<p style={{fontSize:11,color:T.muted,lineHeight:1.4,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{ex.description}</p>}
      <div style={{display:"flex",gap:5,marginTop:"auto"}}>
        <Btn sz="sm" v="ghost" onClick={()=>onEdit(ex)}><Icon n="edit" s={13}/>Editar</Btn>
        <Btn sz="sm" v="danger" onClick={()=>onDelete(ex.id)}><Icon n="trash" s={13}/></Btn>
      </div>
    </div>
  </div>;
}

// ─── Routines View ────────────────────────────────────────────────────────────
// ─── Session Runner — Ejecución de rutina ────────────────────────────────────
function SessionRunner({slot, routine, exercises, onFinish, onClose}) {
  const rtExs = routine?.exercises || [];

  // Mientras se está ejecutando la rutina (pantalla completa), anular el scroll del fondo
  useScrollLock(true);

  // Normalizar bloques (compatibilidad con formatos viejos)
  const normalizeBlocks = item => {
    if(Array.isArray(item.blocks) && item.blocks.length>0) return item.blocks;
    if(Array.isArray(item.setLines) && item.setLines.length>0) {
      return item.setLines.map(l=>({id:l.id,type:"normal",sets:l.sets,reps:l.reps,weight:l.weight}));
    }
    return [{id:uid(),type:"normal",sets:item.sets||3,reps:item.reps||10,weight:item.weight||0}];
  };

  // Expandir cada ejercicio: cada bloque normal → 1 unidad marcable;
  // cada repetición de un bloque drop set → 1 unidad marcable (la secuencia completa de tramos)
  const buildItems = () => rtExs.map(item => {
    const ex = exercises.find(e => e.id === item.exId);
    const blocks = normalizeBlocks(item);
    const units = [];
    blocks.forEach(b => {
      if(b.type === "drop") {
        const reps = b.repeats || 1;
        for(let i=0;i<reps;i++){
          units.push({id:uid(), type:"drop", tramos:(b.tramos||[]).map(t=>({...t,origReps:t.reps,origWeight:t.weight})), done:false});
        }
      } else {
        units.push({id:uid(), type:"normal", sets:b.sets, reps:b.reps, weight:b.weight,
          origSets:b.sets, origReps:b.reps, origWeight:b.weight, done:false});
      }
    });
    return {
      id: item.id,
      exId: item.exId,
      exName: ex?.name || "Ejercicio",
      exPhoto: ex?.photo || null,
      muscleGroup: ex?.muscleGroup || "",
      notes: item.notes || "",
      units,
    };
  });

  const [items, setItems] = useState(buildItems);
  const [startTime] = useState(nowTime());
  const [saving, setSaving] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null); // {itemIdx, unitId}
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [addExFilter, setAddExFilter] = useState("");

  // Ejercicios que NO son parte de esta rutina (para poder agregar uno sobre la marcha)
  const usedExIds = new Set(items.map(it=>it.exId));
  const availableToAdd = exercises.filter(e=>!usedExIds.has(e.id) &&
    (!addExFilter || e.name.toLowerCase().includes(addExFilter.toLowerCase())));

  const addExtraExercise = (ex) => {
    const newUnit = {id:uid(), type:"normal", added:true, done:true, sets:1, reps:10, weight:0, origSets:1, origReps:10, origWeight:0};
    const newItem = {
      id: uid(), exId: ex.id, exName: ex.name, exPhoto: ex.photo || null,
      muscleGroup: ex.muscleGroup || "", notes: "", extraExercise: true, units: [newUnit],
    };
    setItems(p => [...p, newItem]);
    setShowAddExercise(false); setAddExFilter("");
    setEditingUnit({itemIdx: items.length, unitId: newUnit.id}); // abrir edición directo para cargar lo real
  };

  const toggleUnit = (itemIdx, unitId) => setItems(p => p.map((it,i) =>
    i!==itemIdx ? it : {...it, units: it.units.map(u => u.id===unitId ? {...u, done:!u.done} : u)}
  ));
  const updateUnit = (itemIdx, unitId, patch) => setItems(p => p.map((it,i) =>
    i!==itemIdx ? it : {...it, units: it.units.map(u => u.id===unitId ? {...u, ...patch} : u)}
  ));
  const updateTramoUnit = (itemIdx, unitId, tramoId, patch) => setItems(p => p.map((it,i) =>
    i!==itemIdx ? it : {...it, units: it.units.map(u => u.id!==unitId ? u :
      {...u, tramos:(u.tramos||[]).map(t=>t.id===tramoId?{...t,...patch}:t)})}
  ));
  // Agregar/quitar tramos de un drop set — no hay límite de cuántos tramos puede tener
  const addTramoToUnit = (itemIdx, unitId) => setItems(p => p.map((it,i) =>
    i!==itemIdx ? it : {...it, units: it.units.map(u => {
      if(u.id!==unitId) return u;
      const last = (u.tramos||[])[u.tramos.length-1];
      const nuevoReps = last?.reps || 6;
      const nuevoPeso = Math.max(0,(last?.weight||0)-5);
      return {...u, tramos:[...(u.tramos||[]), {id:uid(), reps:nuevoReps, weight:nuevoPeso, origReps:nuevoReps, origWeight:nuevoPeso}]};
    })}
  ));
  const removeTramoFromUnit = (itemIdx, unitId, tramoId) => setItems(p => p.map((it,i) =>
    i!==itemIdx ? it : {...it, units: it.units.map(u => u.id!==unitId ? u :
      {...u, tramos:(u.tramos||[]).filter(t=>t.id!==tramoId)})}
  ));

  // Agregar una serie no planeada (a veces no se puede seguir la rutina tal cual, y hay que registrar lo real)
  const addExtraUnit = (itemIdx, type) => {
    const it = items[itemIdx];
    const lastNormal = [...it.units].reverse().find(u=>u.type==="normal");
    const newUnit = type==="drop"
      ? {id:uid(), type:"drop", added:true, done:true,
          tramos:[{id:uid(),reps:8,weight:lastNormal?.weight||0,origReps:8,origWeight:lastNormal?.weight||0},
                  {id:uid(),reps:6,weight:Math.max(0,(lastNormal?.weight||0)-5),origReps:6,origWeight:Math.max(0,(lastNormal?.weight||0)-5)}]}
      : {id:uid(), type:"normal", added:true, done:true,
          sets:1, reps:lastNormal?.reps||10, weight:lastNormal?.weight||0,
          origSets:1, origReps:lastNormal?.reps||10, origWeight:lastNormal?.weight||0};
    setItems(p => p.map((x,i)=> i!==itemIdx ? x : {...x, units:[...x.units, newUnit]}));
    setEditingUnit({itemIdx, unitId:newUnit.id}); // abrir edición directo para ajustar los valores reales
  };
  const removeExtraUnit = (itemIdx, unitId) => setItems(p => p.map((it,i) =>
    i!==itemIdx ? it : {...it, units: it.units.filter(u=>u.id!==unitId)}
  ));

  const allUnits  = items.flatMap(it => it.units);
  const doneCount = allUnits.filter(u => u.done).length;
  const allDone   = doneCount === allUnits.length && allUnits.length > 0;

  const finish = () => {
    setSaving(true);
    const logs = items.flatMap(it => {
      const doneUnits = it.units.filter(u => u.done);
      if(doneUnits.length === 0) return [];
      return doneUnits.map(u => {
        if(u.type === "drop") {
          const tramosTxt = (u.tramos||[]).map(t=>`${t.reps}×${t.weight}kg`).join("→");
          const maxWeight  = Math.max(...(u.tramos||[{weight:0}]).map(t=>t.weight));
          const totalReps  = (u.tramos||[]).reduce((s,t)=>s+(+t.reps||0),0);
          const modified   = !u.added && (u.tramos||[]).some(t=>t.reps!==t.origReps || t.weight!==t.origWeight);
          return {
            id: uid(), exerciseId: it.exId, exerciseName: it.exName, muscleGroup: it.muscleGroup,
            date: nowISO(), time: startTime,
            sets: 1, reps: totalReps, weight: maxWeight,
            notes: `${it.notes ? it.notes+" — " : ""}${it.extraExercise?"Ejercicio extra (no planeado) — ":u.added?"Serie agregada — ":""}Drop set: ${tramosTxt}`,
            routineName: routine.name, modified, added: !!u.added, extraExercise: !!it.extraExercise,
          };
        }
        const modified = !u.added && (u.sets!==u.origSets || u.reps!==u.origReps || u.weight!==u.origWeight);
        return {
          id: uid(), exerciseId: it.exId, exerciseName: it.exName, muscleGroup: it.muscleGroup,
          date: nowISO(), time: startTime,
          sets: u.sets, reps: u.reps, weight: u.weight,
          notes: it.extraExercise ? `${it.notes?it.notes+" — ":""}Ejercicio extra (no planeado)`
               : u.added ? `${it.notes?it.notes+" — ":""}Serie agregada (no planeada)` : it.notes,
          routineName: routine.name, modified, added: !!u.added, extraExercise: !!it.extraExercise,
        };
      });
    });
    onFinish(logs);
  };

  return (
    <div style={{position:"fixed",inset:0,background:T.bg,zIndex:500,display:"flex",flexDirection:"column"}}>
      <style>{CSS}</style>

      <div style={{background:T.surface,borderBottom:`1px solid ${T.border}`,
        padding:"calc(12px + env(safe-area-inset-top)) 16px 12px",
        display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexShrink:0}}>
        <div>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:20,color:routine.color||T.accent,letterSpacing:".06em"}}>
            {routine.name}
          </div>
          <div style={{fontSize:11,color:T.muted}}>🕐 {startTime} · {doneCount}/{allUnits.length} completados</div>
        </div>
        <button onClick={onClose}
          style={{background:"none",color:T.muted,border:`1px solid ${T.border}`,borderRadius:8,
            padding:"6px 12px",cursor:"pointer",fontSize:13}}>✕ Salir</button>
      </div>

      <div style={{height:4,background:T.border,flexShrink:0}}>
        <div style={{height:"100%",background:routine.color||T.accent,
          width:`${allUnits.length?doneCount/allUnits.length*100:0}%`,transition:"width .3s"}}/>
      </div>

      <div data-scroll-lock-allow style={{flex:1,overflowY:"auto",padding:"14px 14px 100px"}}>
        {items.length === 0 && (
          <div style={{textAlign:"center",padding:"40px 20px",color:T.muted}}>
            <p style={{fontSize:14}}>Esta rutina no tiene ejercicios asignados.</p>
            <p style={{fontSize:12,marginTop:6}}>Agregalos desde la sección Rutinas.</p>
          </div>
        )}

        {items.map((item, itemIdx) => {
          const itemDone = item.units.every(u => u.done);
          return (
            <div key={item.id} className="fu" style={{
              background: itemDone ? T.green+"0c" : T.card,
              borderRadius:14, border:`1.5px solid ${itemDone ? T.green+"44" : T.border}`,
              marginBottom:12, overflow:"hidden"
            }}>
              <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px"}}>
                {item.exPhoto
                  ? <img src={item.exPhoto} alt="" style={{width:46,height:46,borderRadius:8,objectFit:"cover",flexShrink:0}}/>
                  : <div style={{width:46,height:46,background:T.surface,borderRadius:8,display:"flex",
                      alignItems:"center",justifyContent:"center",color:T.muted,flexShrink:0,fontSize:18}}>💪</div>
                }
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:14,
                    textDecoration:itemDone?"line-through":"none",
                    color:itemDone?T.muted:T.text}}>{item.exName}</div>
                  {item.muscleGroup && <div style={{fontSize:11,color:T.muted,marginTop:1}}>{item.muscleGroup}{item.extraExercise&&<span style={{color:T.purple,marginLeft:6}}>· extra</span>}</div>}
                  {!item.muscleGroup && item.extraExercise && <div style={{fontSize:11,color:T.purple,marginTop:1}}>· extra</div>}
                </div>
              </div>

              <div style={{padding:"0 14px 12px",display:"flex",flexDirection:"column",gap:6}}>
                {item.units.map((unit,uIdx) => {
                  const isEditingThis = editingUnit?.itemIdx===itemIdx && editingUnit?.unitId===unit.id;
                  const wasModified = unit.type==="normal"
                    ? (unit.sets!==unit.origSets || unit.reps!==unit.origReps || unit.weight!==unit.origWeight)
                    : (unit.tramos||[]).some(t=>t.reps!==t.origReps || t.weight!==t.origWeight);
                  return (
                  <div key={unit.id} style={{
                    background: unit.done ? T.green+"15" : T.surface,
                    borderRadius:10,
                    border:`1px solid ${unit.type==="drop"?T.orange+"55":(unit.done?T.green+"44":T.border)}`,
                    overflow:"hidden"
                  }}>
                    <div style={{padding:"8px 12px", display:"flex", alignItems:"center", gap:10}}>
                      <div style={{width:22,height:22,borderRadius:"50%",background:T.bg,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:11,fontWeight:700,color:T.muted,flexShrink:0}}>
                        {uIdx+1}
                      </div>
                      <div style={{flex:1,fontSize:13,minWidth:0,
                        textDecoration:unit.done?"line-through":"none",
                        color:unit.done?T.muted:T.text}}>
                        {unit.type === "drop"
                          ? <>
                              <span style={{color:T.orange,fontWeight:700,marginRight:6}}>🔻</span>
                              {(unit.tramos||[]).map(t=>`${t.reps}×${t.weight}kg`).join(" → ")}
                            </>
                          : <>{unit.sets}s × {unit.reps}r · <b>{unit.weight}kg</b></>
                        }
                        {wasModified&&<span style={{marginLeft:6,fontSize:10,color:T.blue}}>· editado</span>}
                        {unit.added&&<span style={{marginLeft:6,fontSize:10,color:T.purple}}>· agregada</span>}
                      </div>
                      <button onClick={()=>setEditingUnit(isEditingThis?null:{itemIdx,unitId:unit.id})}
                        style={{background:T.bg,color:T.muted,border:`1px solid ${T.border}`,
                          borderRadius:7,padding:"5px 9px",cursor:"pointer",fontSize:12,flexShrink:0}}>✏</button>
                      <button onClick={()=>{
                          if(!unit.added && !confirm("¿Eliminar esta serie planeada? Podés agregar otra en su lugar (ej: cambiarla por un drop set).")) return;
                          removeExtraUnit(itemIdx,unit.id);
                        }}
                        style={{background:T.bg,color:T.red,border:`1px solid ${T.border}`,
                          borderRadius:7,padding:"5px 9px",cursor:"pointer",fontSize:12,flexShrink:0}}>×</button>
                      <button onClick={()=>toggleUnit(itemIdx,unit.id)}
                        style={{
                          background: unit.done ? T.green : "transparent",
                          color:      unit.done ? "#000"  : T.muted,
                          border:     `2px solid ${unit.done ? T.green : T.border}`,
                          borderRadius:7, padding:"5px 12px", cursor:"pointer",
                          fontSize:14, fontWeight:700, minWidth:38, flexShrink:0
                        }}>
                        {unit.done ? "✓" : "○"}
                      </button>
                    </div>

                    {isEditingThis&&<div style={{background:T.bg,padding:"10px 12px",
                      borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:8}}>
                      <div style={{fontSize:11,color:T.blue,fontWeight:600}}>
                        Ajustar a lo realmente realizado
                      </div>
                      {unit.type==="normal" ? (
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                          <Field label="Series" type="number" value={unit.sets}
                            onChange={v=>updateUnit(itemIdx,unit.id,{sets:toNum(v)})} min={0}/>
                          <Field label="Reps" type="number" value={unit.reps}
                            onChange={v=>updateUnit(itemIdx,unit.id,{reps:toNum(v)})} min={0}/>
                          <Field label="Peso kg" type="number" value={unit.weight}
                            onChange={v=>updateUnit(itemIdx,unit.id,{weight:toNum(v)})} min={0} step={0.5}/>
                        </div>
                      ) : (
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {(unit.tramos||[]).map((tramo,tIdx)=>(
                            <div key={tramo.id} style={{display:"flex",gap:6,alignItems:"flex-end"}}>
                              <div style={{width:18,fontSize:11,color:T.orange,fontWeight:700,paddingBottom:8}}>{tIdx+1}</div>
                              <div style={{flex:1}}>
                                <Field label="Reps" type="number" value={tramo.reps}
                                  onChange={v=>updateTramoUnit(itemIdx,unit.id,tramo.id,{reps:toNum(v)})} min={0}/>
                              </div>
                              <div style={{flex:1}}>
                                <Field label="Peso kg" type="number" value={tramo.weight}
                                  onChange={v=>updateTramoUnit(itemIdx,unit.id,tramo.id,{weight:toNum(v)})} min={0} step={0.5}/>
                              </div>
                              {unit.tramos.length>1&&
                                <button onClick={()=>removeTramoFromUnit(itemIdx,unit.id,tramo.id)}
                                  style={{background:"none",color:T.red,border:`1px solid ${T.border}`,
                                    borderRadius:6,padding:"7px 9px",fontSize:12,cursor:"pointer",marginBottom:1}}>×</button>}
                            </div>
                          ))}
                          <button onClick={()=>addTramoToUnit(itemIdx,unit.id)}
                            style={{background:"none",color:T.orange,border:`1px dashed ${T.orange}55`,
                              borderRadius:8,padding:"6px 0",fontSize:11,fontWeight:600,cursor:"pointer",
                              display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                            <Icon n="plus" s={11}/>Agregar tramo
                          </button>
                        </div>
                      )}
                      {wasModified&&
                        <button onClick={()=>{
                          if(unit.type==="normal") updateUnit(itemIdx,unit.id,{sets:unit.origSets,reps:unit.origReps,weight:unit.origWeight});
                          else (unit.tramos||[]).forEach(t=>updateTramoUnit(itemIdx,unit.id,t.id,{reps:t.origReps,weight:t.origWeight}));
                        }} style={{background:"none",color:T.muted,border:`1px solid ${T.border}`,
                          borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",alignSelf:"flex-start"}}>
                          ↺ Restaurar valores planeados
                        </button>}
                      <Btn sz="sm" v="success" onClick={()=>setEditingUnit(null)}>
                        <Icon n="check" s={13}/>Listo
                      </Btn>
                    </div>}
                  </div>
                  );
                })}

                {/* Sumar una serie no planeada — a veces no se puede seguir la rutina tal cual */}
                <div style={{display:"flex",gap:6,marginTop:2}}>
                  <button onClick={()=>addExtraUnit(itemIdx,"normal")}
                    style={{flex:1,background:"none",color:T.blue,border:`1px dashed ${T.blue}55`,
                      borderRadius:8,padding:"7px 0",fontSize:11,fontWeight:600,cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                    <Icon n="plus" s={11}/>Serie normal
                  </button>
                  <button onClick={()=>addExtraUnit(itemIdx,"drop")}
                    style={{flex:1,background:"none",color:T.orange,border:`1px dashed ${T.orange}55`,
                      borderRadius:8,padding:"7px 0",fontSize:11,fontWeight:600,cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                    <Icon n="plus" s={11}/>Drop set
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Agregar un ejercicio completo que no estaba en la rutina planeada */}
        {!showAddExercise
          ? <button onClick={()=>setShowAddExercise(true)}
              style={{width:"100%",background:T.card,color:T.purple,border:`1px dashed ${T.purple}66`,
                borderRadius:12,padding:"14px 0",fontSize:13,fontWeight:700,cursor:"pointer",
                display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:4}}>
              <Icon n="plus" s={14}/>Agregar ejercicio extra
            </button>
          : <div className="fu" style={{background:T.card,borderRadius:14,border:`1px solid ${T.purple}55`,
              padding:14,marginTop:4}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.purple,letterSpacing:".04em"}}>
                  AGREGAR EJERCICIO EXTRA
                </span>
                <button onClick={()=>{setShowAddExercise(false);setAddExFilter("");}}
                  style={{background:"none",color:T.muted,border:"none",fontSize:18,cursor:"pointer"}}>×</button>
              </div>
              <input value={addExFilter} onChange={e=>setAddExFilter(e.target.value)} placeholder="Buscar ejercicio..."
                style={{width:"100%",background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
                  color:T.text,padding:"9px 12px",fontSize:13,marginBottom:10}}/>
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:280,overflowY:"auto"}}>
                {availableToAdd.length===0&&<p style={{fontSize:12,color:T.muted,textAlign:"center",padding:"10px 0"}}>
                  {addExFilter?"No se encontraron ejercicios.":"Ya agregaste todos tus ejercicios disponibles."}
                </p>}
                {availableToAdd.map(ex=>(
                  <button key={ex.id} onClick={()=>addExtraExercise(ex)}
                    style={{display:"flex",alignItems:"center",gap:10,background:T.surface,border:`1px solid ${T.border}`,
                      borderRadius:10,padding:"8px 10px",cursor:"pointer",textAlign:"left"}}>
                    {ex.photo
                      ? <img src={ex.photo} alt="" style={{width:32,height:32,borderRadius:6,objectFit:"cover",flexShrink:0}}/>
                      : <div style={{width:32,height:32,background:T.bg,borderRadius:6,display:"flex",
                          alignItems:"center",justifyContent:"center",color:T.muted,flexShrink:0,fontSize:14}}>💪</div>}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:T.text}}>{ex.name}</div>
                      {ex.muscleGroup&&<div style={{fontSize:10,color:T.muted}}>{ex.muscleGroup}</div>}
                    </div>
                  </button>
                ))}
              </div>
            </div>}
      </div>

      <div style={{position:"absolute",bottom:0,left:0,right:0,background:T.surface,
        borderTop:`1px solid ${T.border}`,padding:"14px 16px"}}>
        {allDone
          ? <Btn full v="success" onClick={finish} disabled={saving}>
              <Icon n="check" s={16}/>{saving?"Guardando...":"✅ Finalizar sesión — Todo completado"}
            </Btn>
          : <Btn full onClick={finish} disabled={saving || doneCount===0}>
              <Icon n="check" s={16}/>{saving?"Guardando...":`Finalizar (${doneCount}/${allUnits.length} completados)`}
            </Btn>}
        {doneCount===0&&
          <p style={{fontSize:11,color:T.muted,textAlign:"center",marginTop:8}}>
            Marcá al menos un set para registrar la sesión.
          </p>}
      </div>
    </div>
  );
}


function RoutinesView({routines,exercises,setRoutines,schedule,setSchedule,onExecuteSlot,logs,profile,goToTab,showNewRt,setShowNewRt,showGenAI,setShowGenAI,innerTab,setInnerTab}) {
  const [newName,setNewName]=useState("");
  const [newColor,setNewColor]=useState(T.accent);
  const [confirmDel,setConfirmDel]=useState(null);
  useScrollLock(!!confirmDel);
  const [editR,setEditR]=useState(null);
  const [editName,setEditName]=useState("");
  const [editColor,setEditColor]=useState(T.accent);
  const [genAIDays,  setGenAIDays]  = useState(4);
  const [genAILoad,  setGenAILoad]  = useState(false);
  const [genAIResult,setGenAIResult]= useState([]);
  const [genAISel,   setGenAISel]   = useState([]);
  const [genAIErr,   setGenAIErr]   = useState("");

  // Al abrirse desde el botón del header, arrancar limpio (sin resultados/errores de una vez anterior)
  useEffect(()=>{
    if(showGenAI){ setGenAIResult([]); setGenAISel([]); setGenAIErr(""); }
  },[showGenAI]);

  const [expandedRt,setExpandedRt]=useState({});
  const [addingExTo,setAddingExTo]=useState(null);  // rtId | null
  const [multiPick,setMultiPick]=useState([]);        // ids seleccionados
  const [multiStep,setMultiStep]=useState("pick");    // "pick" | "configure"
  const [multiSearch,setMultiSearch]=useState("");
  const [multiFilter,setMultiFilter]=useState("Todos");
  const [multiConfig,setMultiConfig]=useState([]);    // items a configurar
  const [editingRtEx,setEditingRtEx]=useState(null);
  const [rtExBlocks,setRtExBlocks]=useState([]);
  const [rtExNotes,setRtExNotes]=useState("");
  const [weekOffset,setWeekOffset]=useState(0);
  const [openDay,setOpenDay]=useState(null);
  const [addingSlot,setAddingSlot]=useState(null);
  const [slotRtId,setSlotRtId]=useState("");
  const [slotTime,setSlotTime]=useState("08:00");
  const [slotRepeat,setSlotRepeat]=useState(true);
  const [openSlot,setOpenSlot]=useState(null);
  const COLORS=[T.accent,T.green,T.blue,T.orange,T.purple,T.red];
  const today=nowISO();

  const COLORS_AI=["#e8ff00","#00e87a","#00b4ff","#ff9500","#c084fc","#ff4455"];

  const doGenAI = async () => {
    if(exercises.length===0){setGenAIErr("Necesitás tener ejercicios cargados en tu biblioteca.");return;}
    setGenAILoad(true);setGenAIErr("");setGenAIResult([]);setGenAISel([]);
    try {
      const goalLabel = GOALS[profile?.objetivo]?.label || "Hipertrofia";
      const list = generateRoutinesLocal(exercises, goalLabel, genAIDays);
      if(!list||list.length===0){
        setGenAIErr("No hay suficientes ejercicios clasificados por grupo muscular. Asegurate de que tus ejercicios tengan grupo muscular asignado.");
        setGenAILoad(false);return;
      }
      const colored = list.map((r,i)=>({...r,color:COLORS_AI[i%COLORS_AI.length]}));
      setGenAIResult(colored);
      setGenAISel(colored.map((_,i)=>i));
    } catch(e){
      setGenAIErr("Error al generar rutinas: "+(e?.message||"intentá de nuevo."));
    }
    setGenAILoad(false);
  };

  const saveGenRoutines = () => {
    const toSave = genAISel.map(i=>{
      const r = genAIResult[i];
      return {
        id: uid(),
        name: r.nombre,
        color: r.color,
        exercises: (r.ejercicios||[]).map(ej=>{
          // buscar por nombre exacto primero, luego parcial
          const found = exercises.find(e=>e.name===ej.nombre)
            || exercises.find(e=>e.name.toLowerCase()===ej.nombre.toLowerCase())
            || exercises.find(e=>e.name.toLowerCase().includes(ej.nombre.toLowerCase().slice(0,6)));
          if(!found) return null;
          return {
            id: uid(),
            exId: found.id,
            blocks:[{id:uid(),type:"normal",sets:ej.sets||3,reps:ej.reps||10,weight:ej.peso||0}],
            notes: ej.notas||"",
          };
        }).filter(Boolean),
      };
    });
    setRoutines(p=>[...p,...toSave]);
    setShowGenAI(false);setGenAIResult([]);setGenAISel([]);
  };

  const createRt=()=>{
    if(!newName.trim())return;
    const id=uid();
    setRoutines(p=>[...p,{id,name:newName.trim(),color:newColor,exercises:[]}]);
    setNewName("");setShowNewRt(false);
    setExpandedRt(e=>({...e,[id]:true}));
  };
  const delRt=id=>{setRoutines(p=>p.filter(r=>r.id!==id));setSchedule(p=>p.filter(s=>s.routineId!==id));setConfirmDel(null);};
  const saveEditRt=()=>{
    if(!editName.trim())return;
    setRoutines(p=>p.map(r=>r.id===editR.id?{...r,name:editName.trim(),color:editColor}:r));
    setEditR(null);
  };

  const getRtExs=rtId=>(routines.find(r=>r.id===rtId)?.exercises||[]);
  const openMultiPicker = rtId => {
    setAddingExTo(rtId);
    setMultiPick([]);
    setMultiStep("pick");
    setMultiSearch("");
    setMultiFilter("Todos");
    setMultiConfig([]);
  };

  const confirmMultiPick = rtId => {
    if(multiPick.length===0) return;
    const existing = routines.find(r=>r.id===rtId)?.exercises||[];
    const toAdd = multiPick.filter(id=>!existing.find(e=>e.exId===id));
    setMultiConfig(toAdd.map(exId=>({
      exId, sets:3, reps:10, weight:0
    })));
    setMultiStep("configure");
  };

  const addExToRt = rtId => {
    if(multiConfig.length===0) return;
    setRoutines(p=>p.map(r=>{
      if(r.id!==rtId) return r;
      const existing = r.exercises||[];
      const newItems = multiConfig.map(cfg=>({
        id:uid(), exId:cfg.exId,
        blocks:[{id:uid(),type:"normal",sets:cfg.sets,reps:cfg.reps,weight:cfg.weight}],
        notes:""
      }));
      return {...r, exercises:[...existing, ...newItems]};
    }));
    setAddingExTo(null);
    setMultiPick([]);
    setMultiConfig([]);
    setMultiStep("pick");
  };

  // Normalizar formato viejo (sets/reps/weight o setLines) a "blocks"
  const getBlocks = item => {
    if(Array.isArray(item.blocks) && item.blocks.length>0) return item.blocks;
    if(Array.isArray(item.setLines) && item.setLines.length>0) {
      return item.setLines.map(l=>({id:l.id,type:"normal",sets:l.sets,reps:l.reps,weight:l.weight}));
    }
    return [{id:uid(),type:"normal",sets:item.sets||3,reps:item.reps||10,weight:item.weight||0}];
  };
  const summarizeBlocks = blocks => blocks.map(b=>{
    if(b.type==="drop") {
      const tramos=(b.tramos||[]).map(t=>`${t.reps}×${t.weight}kg`).join("+");
      return `${b.repeats||1}×(${tramos}) 🔻`;
    }
    return `${b.sets}×${b.reps}·${b.weight}kg`;
  }).join("  ");

  const updateRtEx=(rtId,itemId,patch)=>{
    setRoutines(p=>p.map(r=>r.id!==rtId?r:{...r,exercises:(r.exercises||[]).map(e=>e.id===itemId?{...e,...patch}:e)}));
  };

  // ── Helpers de edición de bloques ──
  const addNormalBlock = () => setRtExBlocks(p=>[...p,{id:uid(),type:"normal",sets:3,reps:10,weight:p.length?lastWeight(p):0}]);
  const addDropBlock = () => setRtExBlocks(p=>[...p,{id:uid(),type:"drop",repeats:3,
    tramos:[{id:uid(),reps:6,weight:40},{id:uid(),reps:6,weight:30}]}]);
  const lastWeight = blocks => { const b=blocks[blocks.length-1]; return b.type==="drop" ? (b.tramos?.[0]?.weight||0) : (b.weight||0); };
  const updateBlock = (blockId,patch) => setRtExBlocks(p=>p.map(b=>b.id===blockId?{...b,...patch}:b));
  const removeBlock  = blockId => setRtExBlocks(p=>p.filter(b=>b.id!==blockId));
  const addTramo = blockId => setRtExBlocks(p=>p.map(b=>b.id!==blockId?b:
    {...b,tramos:[...(b.tramos||[]),{id:uid(),reps:b.tramos?.[b.tramos.length-1]?.reps||8,
      weight:Math.max(0,(b.tramos?.[b.tramos.length-1]?.weight||10)-10)}]}));
  const updateTramo = (blockId,tramoId,patch) => setRtExBlocks(p=>p.map(b=>b.id!==blockId?b:
    {...b,tramos:(b.tramos||[]).map(t=>t.id===tramoId?{...t,...patch}:t)}));
  const removeTramo = (blockId,tramoId) => setRtExBlocks(p=>p.map(b=>b.id!==blockId?b:
    {...b,tramos:(b.tramos||[]).filter(t=>t.id!==tramoId)}));

  const removeRtEx=(rtId,itemId)=>{
    setRoutines(p=>p.map(r=>r.id!==rtId?r:{...r,exercises:(r.exercises||[]).filter(e=>e.id!==itemId)}));
  };
  const moveRtEx=(rtId,itemId,dir)=>{
    setRoutines(p=>p.map(r=>{
      if(r.id!==rtId)return r;
      const exs=[...(r.exercises||[])];
      const idx=exs.findIndex(e=>e.id===itemId);
      const newIdx=idx+dir;
      if(idx<0||newIdx<0||newIdx>=exs.length)return r;
      [exs[idx],exs[newIdx]]=[exs[newIdx],exs[idx]];
      return {...r,exercises:exs};
    }));
  };

  const monday=getMondayOf(today);
  const baseDate=new Date(monday+"T12:00:00");
  baseDate.setDate(baseDate.getDate()+weekOffset*7);
  const weekMon=baseDate.toISOString().slice(0,10);
  const weekDates=getWeekDates(weekMon);

  const getSlotsForDate=dateISO=>{
    const wday=new Date(dateISO+"T12:00:00").getDay();
    return (schedule||[]).filter(s=>(s.repeat&&s.weekday===wday)||(!s.repeat&&s.date===dateISO))
      .sort((a,b)=>a.time.localeCompare(b.time));
  };
  const addSlot=(dateISO,wday)=>{
    if(!slotRtId)return;
    setSchedule(p=>[...p,{id:uid(),routineId:slotRtId,weekday:wday,time:slotTime,
      repeat:slotRepeat,...(!slotRepeat?{date:dateISO}:{})}]);
    setAddingSlot(null);setSlotRtId("");setSlotTime("08:00");setSlotRepeat(true);
  };
  const removeSlot=id=>setSchedule(p=>p.filter(s=>s.id!==id));

  // ── Estado de cumplimiento de un slot en una fecha dada ──
  // Cuenta cuántas "unidades" (sets normales + repeticiones de drop sets) tiene la rutina planeada,
  // y cuántas tienen un log real ese día para esa rutina, para saber si fue total/parcial/sin hacer.
  const countPlannedUnits = rt => {
    let count = 0;
    (rt.exercises||[]).forEach(item => {
      const blocks = getBlocks(item);
      blocks.forEach(b => count += (b.type==="drop" ? (b.repeats||1) : 1));
    });
    return count;
  };
  const getSlotStatus = (dateISO, slot, rt) => {
    if(!rt) return "none";
    const dayLogs = (logs||[]).filter(l => l.date===dateISO && l.routineName===rt.name);
    if(dayLogs.length === 0) return "none";
    const planned = countPlannedUnits(rt);
    if(planned > 0 && dayLogs.length >= planned) return "full";
    return "partial";
  };
  const STATUS_STYLE = {
    full:    {color:T.green,  label:"✅ Completa",  icon:"✅"},
    partial: {color:T.orange, label:"🟡 Parcial",   icon:"🟡"},
    none:    {color:T.border, label:"",             icon:""},
  };

  // ── Reprogramar un slot a otra fecha ──
  const [reschedSlot, setReschedSlot] = useState(null); // {slot, fromDate}
  const [reschedDate, setReschedDate] = useState("");
  const rescheduleSlot = () => {
    if(!reschedSlot || !reschedDate) return;
    const wday = new Date(reschedDate+"T12:00:00").getDay();
    setSchedule(p => p.map(s => s.id!==reschedSlot.slot.id ? s : {
      ...s, repeat:false, date:reschedDate, weekday:wday
    }));
    setReschedSlot(null); setReschedDate("");
  };

  return <div className="fu" style={{display:"flex",flexDirection:"column",gap:14}}>
    {/* Confirm delete */}
    {confirmDel&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
      <div className="fu" style={{background:T.card,borderRadius:16,padding:24,maxWidth:320,width:"100%",border:`1px solid ${T.red}66`,textAlign:"center"}}>
        <div style={{fontSize:32,marginBottom:8}}>🗑️</div>
        <h2 style={{fontFamily:"'Bebas Neue'",fontSize:20,color:T.red,marginBottom:8}}>ELIMINAR RUTINA</h2>
        <p style={{fontSize:13,color:T.muted,marginBottom:6}}>¿Eliminás la rutina</p>
        <p style={{fontFamily:"'Bebas Neue'",fontSize:17,color:T.accent,marginBottom:14}}>"{confirmDel.name}"</p>
        <div style={{display:"flex",gap:8,justifyContent:"center"}}>
          <Btn v="danger" onClick={()=>delRt(confirmDel.id)}><Icon n="trash" s={13}/>Eliminar</Btn>
          <Btn v="ghost" onClick={()=>setConfirmDel(null)}>Cancelar</Btn>
        </div>
      </div>
    </div>}

    {/* Edit rutina modal */}
    {editR&&<Modal title="EDITAR RUTINA" onClose={()=>setEditR(null)} width={340}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Field label="Nombre" value={editName} onChange={setEditName} required/>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>Color</label>
          <div style={{display:"flex",gap:8}}>{COLORS.map(col=><button key={col} onClick={()=>setEditColor(col)} style={{width:28,height:28,borderRadius:"50%",background:col,cursor:"pointer",border:col===editColor?"3px solid #fff":"2px solid transparent"}}/>)}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn full onClick={saveEditRt}><Icon n="check" s={14}/>Guardar</Btn>
          <Btn v="ghost" onClick={()=>setEditR(null)}>Cancelar</Btn>
        </div>
      </div>
    </Modal>}

    {/* TAB: Mis Rutinas */}
    {innerTab==="rutinas"&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* Modal generador IA */}
      {showGenAI&&<Modal title="✨ GENERAR RUTINAS CON IA" onClose={()=>setShowGenAI(false)} width={520}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <p style={{fontSize:13,color:T.muted,lineHeight:1.5}}>
            La IA va a analizar tus <b style={{color:T.text}}>{exercises.length} ejercicios</b> disponibles y generar rutinas basadas en metodologías fitness reconocidas (PPL, Upper/Lower, Full Body) adaptadas a tu objetivo.
          </p>

          {/* Días por semana */}
          <div>
            <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",
              letterSpacing:".05em",display:"block",marginBottom:8}}>
              Días de entrenamiento por semana
            </label>
            <div style={{display:"flex",gap:6}}>
              {[3,4,5,6].map(d=>(
                <button key={d} onClick={()=>setGenAIDays(d)}
                  style={{flex:1,padding:"10px 0",borderRadius:8,fontWeight:700,fontSize:15,
                    background:genAIDays===d?T.purple:"transparent",
                    color:genAIDays===d?"#fff":T.muted,
                    border:`2px solid ${genAIDays===d?T.purple:T.border}`,cursor:"pointer"}}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          {exercises.length===0&&<div style={{background:T.red+"22",border:`1px solid ${T.red}44`,
            borderRadius:8,padding:"10px 14px",fontSize:13,color:T.red}}>
            ⚠ No tenés ejercicios en tu biblioteca. Agregá ejercicios primero desde la sección Ejercicios.
          </div>}

          {genAIErr&&<div style={{background:T.red+"22",border:`1px solid ${T.red}44`,
            borderRadius:8,padding:"10px 14px",fontSize:13,color:T.red}}>⚠ {genAIErr}</div>}

          {!genAIResult.length&&<Btn full onClick={doGenAI}
            disabled={genAILoad||exercises.length===0}>
            {genAILoad?<><Spinner/>Generando rutinas...</>:"✨ Generar rutinas"}
          </Btn>}

          {/* Resultados */}
          {genAIResult.length>0&&<>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:12,color:T.muted}}>{genAIResult.length} rutinas generadas · {genAISel.length} seleccionadas</span>
              <Btn sz="sm" v="ghost" onClick={doGenAI} disabled={genAILoad}>
                {genAILoad?<><Spinner/>...</>:"🔄 Regenerar"}
              </Btn>
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:10,maxHeight:400,overflowY:"auto"}}>
              {genAIResult.map((r,i)=>{
                const sel=genAISel.includes(i);
                return <div key={i} onClick={()=>setGenAISel(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i])}
                  style={{background:sel?r.color+"15":T.surface,borderRadius:12,
                    border:`2px solid ${sel?r.color:T.border}`,cursor:"pointer",overflow:"hidden"}}>
                  <div style={{padding:"10px 14px",borderLeft:`4px solid ${r.color}`,
                    display:"flex",alignItems:"flex-start",gap:10}}>
                    <div style={{width:20,height:20,borderRadius:5,flexShrink:0,marginTop:1,
                      background:sel?r.color:"transparent",border:`2px solid ${sel?r.color:T.border}`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:11,fontWeight:700,color:"#000"}}>
                      {sel?"✓":""}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:r.color,letterSpacing:".04em"}}>
                        {r.nombre}
                      </div>
                      {r.descripcion&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>{r.descripcion}</p>}
                      <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:6}}>
                        {(r.ejercicios||[]).map((ej,j)=>{
                          const found=exercises.find(e=>e.name.toLowerCase()===ej.nombre.toLowerCase())
                            ||exercises.find(e=>e.name.toLowerCase().includes(ej.nombre.toLowerCase().slice(0,8)));
                          return <span key={j} style={{
                            background:found?r.color+"22":T.red+"22",
                            color:found?r.color:T.red,
                            border:`1px solid ${found?r.color+"44":T.red+"44"}`,
                            borderRadius:20,padding:"3px 9px",fontSize:10,fontWeight:600}}>
                            {ej.nombre} · {ej.sets}×{ej.reps}
                            {!found&&" ⚠"}
                          </span>;
                        })}
                      </div>
                    </div>
                  </div>
                </div>;
              })}
            </div>

            <p style={{fontSize:10,color:T.muted}}>
              ⚠ en rojo = ejercicio no encontrado en tu biblioteca (se omitirá al guardar).
            </p>

            <Btn full v="success" onClick={saveGenRoutines} disabled={genAISel.length===0}>
              <Icon n="check" s={15}/>Agregar {genAISel.length} rutina{genAISel.length!==1?"s":""} a mis rutinas
            </Btn>
          </>}
        </div>
      </Modal>}

      {showNewRt&&<div className="fu" style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.accent}44`}}>
        <h3 style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.accent,letterSpacing:".05em",marginBottom:12}}>NUEVA RUTINA</h3>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Field label="Nombre *" value={newName} onChange={setNewName} placeholder="Ej: Día A – Espalda + Bíceps"/>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>Color</label>
            <div style={{display:"flex",gap:8}}>{COLORS.map(col=><button key={col} onClick={()=>setNewColor(col)} style={{width:24,height:24,borderRadius:"50%",background:col,cursor:"pointer",border:col===newColor?"3px solid #fff":"2px solid transparent"}}/>)}</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={createRt}><Icon n="check" s={14}/>Crear</Btn>
            <Btn v="ghost" onClick={()=>setShowNewRt(false)}>Cancelar</Btn>
          </div>
        </div>
      </div>}

      {routines.length===0&&!showNewRt&&<div style={{textAlign:"center",padding:"40px 20px",color:T.muted}}>
        <Icon n="folder" s={44} c={T.border}/>
        <p style={{marginTop:10,fontSize:14}}>No hay rutinas. Creá una arriba.</p>
      </div>}

      {routines.map(rt=>{
        const rtExs=getRtExs(rt.id);
        const isOpen=expandedRt[rt.id];
        return <div key={rt.id} style={{background:T.card,borderRadius:14,border:`1px solid ${rt.color}55`,overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderLeft:`4px solid ${rt.color}`,cursor:"pointer"}}
            onClick={()=>setExpandedRt(e=>({...e,[rt.id]:!e[rt.id]}))}>
            <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
              <Icon n="folder" s={16} c={rt.color}/>
              <span style={{fontFamily:"'Bebas Neue'",fontSize:18,letterSpacing:".05em"}}>{rt.name}</span>
              <span style={{background:rt.color+"22",color:rt.color,fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,flexShrink:0}}>{rtExs.length} ej.</span>
            </div>
            <div style={{display:"flex",gap:6,flexShrink:0,marginLeft:8}}>
              <button onClick={e=>{e.stopPropagation();setEditR(rt);setEditName(rt.name);setEditColor(rt.color);}}
                style={{background:T.surface,color:T.muted,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>✏</button>
              <button onClick={e=>{e.stopPropagation();setConfirmDel({id:rt.id,name:rt.name});}}
                style={{background:T.red+"22",color:T.red,border:`1px solid ${T.red}44`,borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>🗑</button>
              <span style={{color:T.muted,fontSize:14,padding:"4px"}}>{isOpen?"▲":"▼"}</span>
            </div>
          </div>

          {isOpen&&<div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:8,background:T.surface}}>
            {rtExs.length===0&&<p style={{fontSize:13,color:T.muted,padding:"4px 0"}}>Sin ejercicios. Agregá uno abajo ↓</p>}
            {rtExs.map((item,itemIdx)=>{
              const ex=exercises.find(e=>e.id===item.exId);
              if(!ex)return null;
              const isEd=editingRtEx?.rtId===rt.id&&editingRtEx?.itemId===item.id;
              const blocks=getBlocks(item);
              return <div key={item.id} style={{background:T.card,borderRadius:10,border:`1px solid ${T.border}`,overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px"}}>
                  <div style={{display:"flex",flexDirection:"column",gap:2,flexShrink:0}}>
                    <button onClick={()=>moveRtEx(rt.id,item.id,-1)} disabled={itemIdx===0}
                      style={{background:"none",color:itemIdx===0?T.border:T.muted,border:"none",
                        cursor:itemIdx===0?"default":"pointer",fontSize:13,padding:0,lineHeight:1}}>▲</button>
                    <button onClick={()=>moveRtEx(rt.id,item.id,1)} disabled={itemIdx===rtExs.length-1}
                      style={{background:"none",color:itemIdx===rtExs.length-1?T.border:T.muted,border:"none",
                        cursor:itemIdx===rtExs.length-1?"default":"pointer",fontSize:13,padding:0,lineHeight:1}}>▼</button>
                  </div>
                  {ex.photo&&<img src={ex.photo} alt="" style={{width:38,height:38,borderRadius:6,objectFit:"cover",flexShrink:0}}/>}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:13}}>{itemIdx+1}. {ex.name}</div>
                    <div style={{fontSize:11,color:T.muted,marginTop:1,whiteSpace:"normal",lineHeight:1.5}}>
                      {summarizeBlocks(blocks)}{item.notes?` · ${item.notes}`:""}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:4,flexShrink:0}}>
                    <button onClick={()=>{setEditingRtEx({rtId:rt.id,itemId:item.id});setRtExBlocks(blocks.map(b=>({...b,tramos:b.tramos?b.tramos.map(t=>({...t})):undefined})));setRtExNotes(item.notes||"");}}
                      style={{background:"none",color:T.muted,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 7px",cursor:"pointer",fontSize:12}}>✏</button>
                    <button onClick={()=>removeRtEx(rt.id,item.id)} style={{background:"none",color:T.muted,border:"none",cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>
                  </div>
                </div>
                {isEd&&<div style={{background:T.bg,padding:"10px 12px",borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{fontSize:11,color:T.accent,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>
                    Bloques del ejercicio
                  </div>

                  {rtExBlocks.map((block,bIdx)=>(
                    <div key={block.id} style={{background:T.surface,borderRadius:8,padding:"10px",
                      display:"flex",flexDirection:"column",gap:8,
                      border: block.type==="drop"?`1px solid ${T.orange}55`:`1px solid ${T.border}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:11,fontWeight:700,
                          color: block.type==="drop"?T.orange:T.muted}}>
                          Bloque {bIdx+1} {block.type==="drop"?"🔻 Drop set":"· Normal"}
                        </span>
                        {rtExBlocks.length>1&&<button onClick={()=>removeBlock(block.id)}
                          style={{background:"none",color:T.muted,border:"none",cursor:"pointer",fontSize:15}}>×</button>}
                      </div>

                      {block.type==="normal" ? (
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                          <Field label="Series" type="number" value={block.sets} onChange={v=>updateBlock(block.id,{sets:toNum(v)})} min={1}/>
                          <Field label="Reps" type="number" value={block.reps} onChange={v=>updateBlock(block.id,{reps:toNum(v)})} min={1}/>
                          <Field label="Peso kg" type="number" value={block.weight} onChange={v=>updateBlock(block.id,{weight:toNum(v)})} min={0} step={0.5}/>
                        </div>
                      ) : (
                        <>
                          <Field label="Veces que se repite la secuencia completa" type="number"
                            value={block.repeats} onChange={v=>updateBlock(block.id,{repeats:toNum(v)})} min={1}/>
                          <div style={{fontSize:10,color:T.muted,marginTop:2}}>
                            Tramos en orden (sin descanso entre ellos):
                          </div>
                          {(block.tramos||[]).map((tramo,tIdx)=>(
                            <div key={tramo.id} style={{display:"flex",gap:6,alignItems:"flex-end"}}>
                              <div style={{width:20,fontSize:11,color:T.orange,fontWeight:700,paddingBottom:8}}>{tIdx+1}</div>
                              <div style={{flex:1}}>
                                <Field label="Reps" type="number" value={tramo.reps}
                                  onChange={v=>updateTramo(block.id,tramo.id,{reps:toNum(v)})} min={1}/>
                              </div>
                              <div style={{flex:1}}>
                                <Field label="Peso kg" type="number" value={tramo.weight}
                                  onChange={v=>updateTramo(block.id,tramo.id,{weight:toNum(v)})} min={0} step={0.5}/>
                              </div>
                              {(block.tramos||[]).length>1&&
                                <button onClick={()=>removeTramo(block.id,tramo.id)}
                                  style={{background:"none",color:T.muted,border:"none",cursor:"pointer",
                                    fontSize:15,paddingBottom:8}}>×</button>}
                            </div>
                          ))}
                          <Btn sz="sm" v="ghost" onClick={()=>addTramo(block.id)} style={{alignSelf:"flex-start"}}>
                            <Icon n="plus" s={12}/>Agregar tramo
                          </Btn>
                          <div style={{background:T.bg,borderRadius:6,padding:"6px 10px",fontSize:11,color:T.orange}}>
                            Vista previa: {block.repeats}×({(block.tramos||[]).map(t=>`${t.reps}×${t.weight}kg`).join("+")})
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  <div style={{display:"flex",gap:8}}>
                    <Btn sz="sm" v="ghost" onClick={addNormalBlock}>
                      <Icon n="plus" s={12}/>Bloque normal
                    </Btn>
                    <Btn sz="sm" v="ghost" onClick={addDropBlock} style={{color:T.orange,border:`1px solid ${T.orange}55`}}>
                      <Icon n="plus" s={12}/>🔻 Bloque drop set
                    </Btn>
                  </div>

                  <Field label="Notas" value={rtExNotes} onChange={setRtExNotes} placeholder="Observaciones del ejercicio..."/>
                  <div style={{display:"flex",gap:8}}>
                    <Btn sz="sm" v="success" onClick={()=>{updateRtEx(rt.id,item.id,{blocks:rtExBlocks,notes:rtExNotes});setEditingRtEx(null);}}>
                      <Icon n="check" s={13}/>Guardar
                    </Btn>
                    <Btn sz="sm" v="ghost" onClick={()=>setEditingRtEx(null)}>Cancelar</Btn>
                  </div>
                </div>}
              </div>;
            })}

            {addingExTo===rt.id&&multiStep==="pick"&&
              <div style={{background:T.bg,borderRadius:12,border:`2px solid ${rt.color}55`,overflow:"hidden"}}>
                <div style={{padding:"12px 16px",borderBottom:`1px solid ${T.border}`,
                  display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                  <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:rt.color,letterSpacing:".05em"}}>
                    SELECCIONAR EJERCICIOS
                  </span>
                  <span style={{fontSize:12,color:T.muted}}>{multiPick.length} seleccionados</span>
                </div>
                {/* Búsqueda + filtro */}
                <div style={{padding:"10px 14px",background:T.surface,display:"flex",flexDirection:"column",gap:8}}>
                  <input value={multiSearch} onChange={e=>setMultiSearch(e.target.value)}
                    placeholder="Buscar ejercicio..." autoFocus
                    style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,
                      color:T.text,padding:"8px 12px",fontSize:13,width:"100%"}}/>
                  <div style={{display:"flex",gap:5,overflowX:"auto",paddingBottom:2}}>
                    {["Todos","Pecho","Espalda","Hombros","Bíceps","Tríceps","Piernas","Abdomen","Glúteos","Cardio","General"].map(g=>{
                      const count = g==="Todos"
                        ? exercises.length
                        : exercises.filter(e=>e.muscleGroup===g).length;
                      return (
                        <button key={g} onClick={()=>setMultiFilter(g)}
                          style={{background:multiFilter===g?rt.color:T.card,
                            color:multiFilter===g?"#000":count===0?T.border:T.muted,
                            border:`1px solid ${multiFilter===g?rt.color:count===0?T.border:T.border}`,
                            borderRadius:20,padding:"4px 12px",fontSize:11,fontWeight:600,
                            whiteSpace:"nowrap",cursor:count===0&&g!=="Todos"?"default":"pointer",
                            flexShrink:0,opacity:count===0&&g!=="Todos"?.4:1}}>
                          {g}{count>0&&g!=="Todos"&&<span style={{fontSize:9,marginLeft:3,opacity:.7}}>({count})</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Grilla de ejercicios */}
                <div style={{maxHeight:340,overflowY:"auto",padding:"10px 12px",
                  display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:8}}>
                  {exercises.filter(e=>
                    (multiFilter==="Todos"||e.muscleGroup===multiFilter) &&
                    (!multiSearch||e.name.toLowerCase().includes(multiSearch.toLowerCase()))
                  ).map(ex=>{
                    const already=(routines.find(r=>r.id===rt.id)?.exercises||[]).some(e=>e.exId===ex.id);
                    const selected=multiPick.includes(ex.id);
                    return <button key={ex.id}
                      disabled={already}
                      onClick={()=>{
                        if(already)return;
                        setMultiPick(p=>p.includes(ex.id)?p.filter(x=>x!==ex.id):[...p,ex.id]);
                      }}
                      style={{
                        background:already?T.surface:selected?rt.color+"22":T.card,
                        border:`2px solid ${already?T.border:selected?rt.color:T.border}`,
                        borderRadius:10,padding:0,cursor:already?"default":"pointer",
                        overflow:"hidden",textAlign:"left",position:"relative",
                        opacity:already?.5:1
                      }}>
                      {ex.photo
                        ? <img src={ex.photo} alt="" style={{width:"100%",height:70,objectFit:"cover",display:"block"}}/>
                        : <div style={{width:"100%",height:70,background:T.surface,display:"flex",
                            alignItems:"center",justifyContent:"center",fontSize:24}}>💪</div>}
                      <div style={{padding:"6px 8px"}}>
                        <div style={{fontSize:11,fontWeight:700,color:selected?rt.color:T.text,
                          lineHeight:1.2}}>{ex.name}</div>
                        {ex.muscleGroup&&<div style={{fontSize:9,color:T.muted,marginTop:2}}>{ex.muscleGroup}</div>}
                        {already&&<div style={{fontSize:9,color:T.muted,fontStyle:"italic"}}>ya en rutina</div>}
                      </div>
                      {selected&&<div style={{position:"absolute",top:4,right:4,
                        background:rt.color,color:"#000",borderRadius:"50%",
                        width:18,height:18,display:"flex",alignItems:"center",
                        justifyContent:"center",fontSize:11,fontWeight:700}}>✓</div>}
                    </button>;
                  })}
                  {exercises.filter(e=>
                    (multiFilter==="Todos"||e.muscleGroup===multiFilter) &&
                    (!multiSearch||e.name.toLowerCase().includes(multiSearch.toLowerCase()))
                  ).length===0&&
                    <p style={{color:T.muted,fontSize:12,gridColumn:"1/-1",padding:10}}>
                      Sin resultados. Creá el ejercicio en la sección Ejercicios.
                    </p>}
                </div>
                {/* Acciones */}
                <div style={{padding:"12px 14px",borderTop:`1px solid ${T.border}`,
                  display:"flex",gap:8}}>
                  <Btn v="success" onClick={()=>confirmMultiPick(rt.id)} disabled={multiPick.length===0}>
                    <Icon n="check" s={14}/>{multiPick.length>0?`Continuar con ${multiPick.length}`:"Seleccioná al menos 1"}
                  </Btn>
                  <Btn v="ghost" onClick={()=>setAddingExTo(null)}>Cancelar</Btn>
                </div>
              </div>}

            {addingExTo===rt.id&&multiStep==="configure"&&
              <div style={{background:T.bg,borderRadius:12,border:`2px solid ${rt.color}55`,overflow:"hidden"}}>
                <div style={{padding:"12px 16px",borderBottom:`1px solid ${T.border}`}}>
                  <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:rt.color,letterSpacing:".05em"}}>
                    CONFIGURAR SERIES Y PESO
                  </span>
                  <p style={{fontSize:11,color:T.muted,marginTop:4}}>Ajustá los valores iniciales. Podés editarlos después desde la rutina.</p>
                </div>
                <div style={{padding:"10px 14px",display:"flex",flexDirection:"column",gap:10,maxHeight:400,overflowY:"auto"}}>
                  {multiConfig.map((cfg,idx)=>{
                    const ex=exercises.find(e=>e.id===cfg.exId);
                    if(!ex)return null;
                    return <div key={cfg.exId} style={{background:T.card,borderRadius:10,
                      border:`1px solid ${T.border}`,padding:"10px 12px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                        {ex.photo&&<img src={ex.photo} alt="" style={{width:40,height:40,borderRadius:7,objectFit:"cover",flexShrink:0}}/>}
                        <div>
                          <div style={{fontWeight:700,fontSize:13}}>{ex.name}</div>
                          {ex.muscleGroup&&<div style={{fontSize:10,color:T.muted}}>{ex.muscleGroup}</div>}
                        </div>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                        <Field label="Series" type="number" value={cfg.sets} min={1}
                          onChange={v=>setMultiConfig(p=>p.map((c,i)=>i===idx?{...c,sets:toNum(v)}:c))}/>
                        <Field label="Reps" type="number" value={cfg.reps} min={1}
                          onChange={v=>setMultiConfig(p=>p.map((c,i)=>i===idx?{...c,reps:toNum(v)}:c))}/>
                        <Field label="Peso kg" type="number" value={cfg.weight} min={0} step={0.5}
                          onChange={v=>setMultiConfig(p=>p.map((c,i)=>i===idx?{...c,weight:toNum(v)}:c))}/>
                      </div>
                    </div>;
                  })}
                </div>
                <div style={{padding:"12px 14px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8}}>
                  <Btn v="success" onClick={()=>addExToRt(rt.id)}>
                    <Icon n="check" s={14}/>Agregar {multiConfig.length} ejercicio{multiConfig.length!==1?"s":""}
                  </Btn>
                  <Btn v="ghost" onClick={()=>setMultiStep("pick")}>← Volver</Btn>
                  <Btn v="ghost" onClick={()=>setAddingExTo(null)}>Cancelar</Btn>
                </div>
              </div>}

            {addingExTo!==rt.id&&
              <Btn sz="sm" v="ghost" onClick={()=>openMultiPicker(rt.id)}
                style={{alignSelf:"flex-start",border:`1px solid ${rt.color}55`,color:rt.color}}>
                <Icon n="plus" s={13}/>Agregar ejercicios
              </Btn>}
          </div>}
        </div>;
      })}
    </div>}

    {/* TAB: Calendario */}
    {innerTab==="calendario"&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:T.card,borderRadius:12,padding:"10px 14px",border:`1px solid ${T.border}`}}>
        <button onClick={()=>setWeekOffset(v=>v-1)} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 12px",color:T.text,cursor:"pointer",fontSize:13}}>← Ant.</button>
        <div style={{textAlign:"center"}}>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:15,color:T.accent,letterSpacing:".06em"}}>SEMANA</div>
          <div style={{fontSize:11,color:T.muted}}>{fmtDateFull(weekDates[0])} — {fmtDateFull(weekDates[6])}</div>
          {weekOffset===0&&<div style={{fontSize:10,color:T.green,fontWeight:600}}>SEMANA ACTUAL</div>}
        </div>
        <button onClick={()=>setWeekOffset(v=>v+1)} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 12px",color:T.text,cursor:"pointer",fontSize:13}}>Sig. →</button>
      </div>

      <div style={{background:T.card,borderRadius:14,border:`1px solid ${T.border}`,overflow:"hidden"}}>
        {weekDates.map((dateISO,idx)=>{
          const wday=new Date(dateISO+"T12:00:00").getDay();
          const slots=getSlotsForDate(dateISO);
          const isToday=dateISO===today;
          const isOpen=openDay===dateISO;
          const dayMealsCal=(loadWeekMeals()[wday]||[]);
          const mealsWithFood=dayMealsCal.filter(m=>(m.items||[]).length>0);
          const dayMealKcal=dayMealsCal.reduce((s,m)=>s+(m.items||[]).reduce((a,i)=>a+(+i.kcal||0),0),0);
          return <div key={dateISO} style={{borderBottom:idx<6?`1px solid ${T.border}`:"none"}}>
            <div onClick={()=>setOpenDay(isOpen?null:dateISO)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer",
                background:isToday?T.accent+"12":"transparent",borderLeft:isToday?`3px solid ${T.accent}`:"3px solid transparent"}}>
              <div style={{width:40,textAlign:"center",flexShrink:0}}>
                <div style={{fontSize:10,color:T.muted,fontWeight:600,textTransform:"uppercase"}}>{WSHORT[wday]}</div>
                <div style={{fontFamily:"'Bebas Neue'",fontSize:20,color:isToday?T.accent:T.text,lineHeight:1}}>{dateISO.slice(8)}</div>
              </div>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:3,minWidth:0}}>
                <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                  {slots.length===0
                    ? <span style={{fontSize:11,color:T.border}}>Sin entrenamiento</span>
                    : slots.map(sl=>{const rt=routines.find(r=>r.id===sl.routineId);if(!rt)return null;
                      const status=getSlotStatus(dateISO,sl,rt);
                      const st=STATUS_STYLE[status];
                      return <span key={sl.id} style={{background:rt.color+"22",color:rt.color,border:`1px solid ${rt.color}44`,borderRadius:20,padding:"3px 9px",fontSize:10,fontWeight:700,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}>
                        {st.icon&&<span>{st.icon}</span>}{sl.time} · {rt.name}
                      </span>;
                    })}
                </div>
                {mealsWithFood.length>0&&<div style={{display:"flex",gap:5,alignItems:"center"}}>
                  <span style={{background:T.green+"18",color:T.green,border:`1px solid ${T.green}33`,borderRadius:20,
                    padding:"2px 8px",fontSize:9,fontWeight:700,whiteSpace:"nowrap"}}>
                    🍽 {mealsWithFood.length} comida{mealsWithFood.length!==1?"s":""} · {rnd(dayMealKcal)} kcal
                  </span>
                </div>}
              </div>
              <span style={{color:T.muted,fontSize:13,flexShrink:0}}>{isOpen?"▲":"▼"}</span>
            </div>

            {isOpen&&<div style={{background:T.surface,padding:"10px 14px",display:"flex",flexDirection:"column",gap:8}}>
              {slots.map(sl=>{
                const rt=routines.find(r=>r.id===sl.routineId);if(!rt)return null;
                const rtExs=getRtExs(rt.id);
                const isSlotOpen=openSlot===sl.id;
                const status=getSlotStatus(dateISO,sl,rt);
                const st=STATUS_STYLE[status];
                return <div key={sl.id} style={{background:T.card,borderRadius:10,border:`1px solid ${rt.color}44`,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 12px",borderLeft:`3px solid ${rt.color}`}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontFamily:"'Bebas Neue'",fontSize:15,color:rt.color,letterSpacing:".04em"}}>{rt.name}</span>
                        {status!=="none"&&<Badge color={st.color}>{st.label}</Badge>}
                      </div>
                      <div style={{fontSize:10,color:T.muted}}>🕐 {sl.time} · {sl.repeat?"Repite cada semana":"Solo este día"} · {rtExs.length} ejercicios</div>
                    </div>
                    <div style={{display:"flex",gap:5,flexShrink:0,flexWrap:"wrap"}}>
                      <button onClick={()=>onExecuteSlot(sl,rt)}
                        style={{background:T.green,color:"#000",border:"none",borderRadius:6,
                          padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",
                          display:"flex",alignItems:"center",gap:4}}>
                        ▶ Ejecutar
                      </button>
                      <button onClick={()=>{setReschedSlot({slot:sl,fromDate:dateISO});setReschedDate(dateISO);}}
                        style={{background:T.blue+"22",color:T.blue,border:`1px solid ${T.blue}44`,borderRadius:6,
                          padding:"5px 9px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                        📅 Reprogramar
                      </button>
                      <button onClick={()=>setOpenSlot(isSlotOpen?null:sl.id)}
                        style={{background:rt.color+"22",color:rt.color,border:`1px solid ${rt.color}44`,borderRadius:6,padding:"4px 9px",fontSize:11,fontWeight:700,cursor:"pointer"}}>{isSlotOpen?"▲":"▼"}</button>
                      <button onClick={()=>removeSlot(sl.id)} style={{background:"none",color:T.muted,border:"none",cursor:"pointer",fontSize:16}}>×</button>
                    </div>
                  </div>
                  {isSlotOpen&&rtExs.length>0&&<div style={{padding:"8px 12px",display:"flex",flexDirection:"column",gap:6,background:T.surface}}>
                    {rtExs.map((item,itemIdx)=>{
                      const ex=exercises.find(e=>e.id===item.exId);if(!ex)return null;
                      const blocks=getBlocks(item);
                      return <div key={item.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",background:T.card,borderRadius:8}}>
                        <div style={{width:16,fontSize:10,color:T.muted,fontWeight:700,flexShrink:0}}>{itemIdx+1}</div>
                        {ex.photo&&<img src={ex.photo} alt="" style={{width:30,height:30,borderRadius:5,objectFit:"cover",flexShrink:0}}/>}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:600,fontSize:12}}>{ex.name}</div>
                          <div style={{fontSize:10,color:T.muted}}>{summarizeBlocks(blocks)}</div>
                        </div>
                      </div>;
                    })}
                  </div>}
                </div>;
              })}

              {/* ── Alimentación del día — vinculada con Mi Dieta ── */}
              {dayMealsCal.length>0&&<div style={{marginTop:4}}>
                <div style={{fontSize:11,color:T.green,fontWeight:700,letterSpacing:".05em",
                  marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
                  🍽 ALIMENTACIÓN — {WLONG[wday]}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[...dayMealsCal].sort((a,b)=>(a.time||"").localeCompare(b.time||"")).map(meal=>{
                    const mKcal=(meal.items||[]).reduce((s,i)=>s+(+i.kcal||0),0);
                    const hasFood=(meal.items||[]).length>0;
                    return <div key={meal.id} style={{background:T.card,borderRadius:10,
                      border:`1px solid ${hasFood?T.green+"33":T.border}`,padding:"8px 12px",
                      display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:16}}>{hasFood?"✅":"⚪"}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:700,color:T.text}}>{meal.name}</div>
                        <div style={{fontSize:10,color:T.muted}}>
                          🕐 {meal.time}{hasFood?` · ${meal.items.length} alimento${meal.items.length!==1?"s":""}`:" · sin planificar"}
                        </div>
                      </div>
                      <span style={{fontSize:12,fontWeight:700,color:hasFood?T.green:T.border}}>
                        {rnd(mKcal)} kcal
                      </span>
                    </div>;
                  })}
                </div>
                <button onClick={()=>goToTab&&goToTab("nutrition")}
                  style={{background:"none",color:T.blue,border:"none",fontSize:11,cursor:"pointer",
                    fontWeight:600,marginTop:6}}>
                  ✏ Editar en Mi Dieta →
                </button>
              </div>}

              {addingSlot?.date===dateISO
                ? <div style={{background:T.card,borderRadius:10,padding:"12px 14px",border:`1px solid ${T.accent}44`,display:"flex",flexDirection:"column",gap:10}}>
                    <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>{WLONG[wday]} {fmtDateFull(dateISO)}</label>
                    {routines.length===0
                      ? <p style={{fontSize:12,color:T.muted}}>No hay rutinas. Creá una en "Mis Rutinas".</p>
                      : <>
                        <Sel label="Rutina" value={slotRtId} onChange={setSlotRtId}
                          options={[{value:"",label:"— Elegir rutina —"},...routines.map(r=>({value:r.id,label:r.name}))]}/>
                        <Field label="Hora" type="time" value={slotTime} onChange={setSlotTime}/>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <input type="checkbox" id={`rep_${dateISO}`} checked={slotRepeat} onChange={e=>setSlotRepeat(e.target.checked)} style={{width:16,height:16,cursor:"pointer"}}/>
                          <label htmlFor={`rep_${dateISO}`} style={{fontSize:12,color:T.text,cursor:"pointer"}}>Repetir todos los {WLONG[wday]}s automáticamente</label>
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <Btn sz="sm" v="success" onClick={()=>addSlot(dateISO,wday)} disabled={!slotRtId}><Icon n="check" s={13}/>Agregar</Btn>
                          <Btn sz="sm" v="ghost" onClick={()=>setAddingSlot(null)}>Cancelar</Btn>
                        </div>
                      </>}
                  </div>
                : <Btn sz="sm" v="ghost" onClick={()=>setAddingSlot({date:dateISO,weekday:wday})} style={{alignSelf:"flex-start"}}>
                    <Icon n="plus" s={13}/>Agregar rutina a este día
                  </Btn>}
            </div>}
          </div>;
        })}
      </div>
    </div>}

    {/* Modal reprogramar */}
    {reschedSlot&&<Modal title="📅 REPROGRAMAR" onClose={()=>setReschedSlot(null)} width={340}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <p style={{fontSize:13,color:T.muted}}>
          Elegí la nueva fecha para esta actividad. Pasará a ser solo para ese día (deja de repetirse semanalmente si lo hacía).
        </p>
        <Field label="Nueva fecha" type="date" value={reschedDate} onChange={setReschedDate}/>
        <div style={{display:"flex",gap:8}}>
          <Btn full v="info" onClick={rescheduleSlot} disabled={!reschedDate}>
            <Icon n="check" s={14}/>Confirmar
          </Btn>
          <Btn v="ghost" onClick={()=>setReschedSlot(null)}>Cancelar</Btn>
        </div>
      </div>
    </Modal>}
  </div>;
}

// ─── Cardio & Rest View ───────────────────────────────────────────────────────
function CardioView({cardioLogs,restDays,setCardioLogs,setRestDays,cardioSchedule,setCardioSchedule,profile}) {
  const [section,setSection]=useState("cardio"); // "cardio" | "descanso"
  const [showMenu,setShowMenu]=useState(false);   // menú del botón Cardio: Programar / Registrar
  const [showSchedule,setShowSchedule]=useState(false);
  const [showLog,setShowLog]=useState(false);     // registrar cardio directo (sin programar antes)
  const [showRest,setShowRest]=useState(false);

  const [scheduleForm,setScheduleForm]=useState({date:nowISO(),time:"08:00",duracion:30,type:"running"});
  const [logForm,setLogForm]=useState({date:nowISO(),durMin:30,durSeg:0,km:"",type:"running"});
  const [pendingConfirm,setPendingConfirm]=useState(null); // sesión programada vencida, esperando confirmación
  const [fetchingWeather,setFetchingWeather]=useState(false);

  const [restDate,setRestDate]=useState(nowISO());
  const [restNotes,setRestNotes]=useState("");
  const [restSleep,setRestSleep]=useState(3);
  const [editingCardio,setEditingCardio]=useState(null);
  const [cardioEditForm,setCardioEditForm]=useState(null);
  const [editingRest,setEditingRest]=useState(null);
  const [restEditForm,setRestEditForm]=useState(null);

  const TYPES=[{value:"running",label:"🏃 Running",color:T.orange},{value:"bici",label:"🚴 Bici",color:T.blue},
    {value:"caminata",label:"🚶 Caminata",color:T.green},{value:"natacion",label:"🏊 Natación",color:T.purple},{value:"eliptico",label:"⚙ Elíptico",color:T.muted}];
  const estKcal=(type,dur,peso=profile?.peso||80)=>{const mets={running:9.8,bici:7.5,caminata:3.5,natacion:8.0,eliptico:5.0};return rnd((mets[type]||5)*peso*(dur/60));};
  const minSegToDecimal = (min,seg) => (+min||0) + (+seg||0)/60;
  const decimalToMinSeg = (decimal) => {
    let min = Math.floor(decimal||0);
    let seg = Math.round(((decimal||0)-min)*60);
    if(seg>=60){ seg=0; min+=1; }
    return {min, seg};
  };
  const fmtMinSeg = (min,seg) => `${min}:${String(seg).padStart(2,"0")}`;

  // Clima automático — mismo patrón que la pestaña Hoy, para adjuntarlo al registro de cardio
  const fetchWeatherNow = () => new Promise(resolve=>{
    if(!navigator.geolocation){ resolve(null); return; }
    navigator.geolocation.getCurrentPosition(async pos=>{
      try {
        const {latitude:lat, longitude:lon} = pos.coords;
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&timezone=auto`;
        const res = await fetch(url);
        const d = await res.json();
        const code = d.current.weather_code;
        const icon = code===0?"☀️":code<=2?"🌤️":code<=3?"☁️":code<=59?"🌦️":code<=69?"🌧️":code<=79?"❄️":code<=99?"⛈️":"🌡️";
        const label = code===0?"Despejado":code<=2?"Parcial. nublado":code<=3?"Nublado":code<=59?"Llovizna":code<=69?"Lluvia":code<=79?"Nieve":"Tormenta";
        resolve({icon, label, temp:Math.round(d.current.temperature_2m)});
      } catch { resolve(null); }
    }, ()=>resolve(null));
  });

  // ── Detectar automáticamente cardio programado cuyo horario+duración ya pasó ──
  useEffect(()=>{
    const now = new Date();
    const overdue = (cardioSchedule||[]).find(s=>{
      const start = new Date(`${s.date}T${s.time}:00`);
      const end = new Date(start.getTime() + (s.duracion||30)*60000);
      return end <= now;
    });
    if(overdue && (!pendingConfirm || pendingConfirm.id!==overdue.id)) setPendingConfirm(overdue);
  },[cardioSchedule]);

  const addSchedule=()=>{
    if(scheduleForm.date<nowISO()){alert("Elegí una fecha de hoy en adelante para programar cardio.");return;}
    setCardioSchedule(p=>[...p,{...scheduleForm,id:uid()}]);
    setScheduleForm({date:nowISO(),time:"08:00",duracion:30,type:"running"});
    setShowSchedule(false); setShowMenu(false);
  };
  const deleteSchedule=id=>{
    setCardioSchedule(p=>p.filter(x=>x.id!==id));
    if(pendingConfirm?.id===id) setPendingConfirm(null);
  };

  const openLogFor = (entry) => {
    setLogForm({date:entry?.date||nowISO(), durMin:entry?.duracion||30, durSeg:0, km:"", type:entry?.type||"running"});
    setShowLog(true); setShowMenu(false); setPendingConfirm(null);
    if(entry) deleteSchedule(entry.id);
  };

  const submitLog = async () => {
    const km = parseFloat(logForm.km);
    const dur = minSegToDecimal(logForm.durMin, logForm.durSeg); // duración total, en minutos decimales
    if(!dur){ alert("Completá la duración."); return; }
    setFetchingWeather(true);
    const weather = await fetchWeatherNow();
    setFetchingWeather(false);
    const ritmoDecimal = km>0 ? dur/km : null;
    const ritmo = ritmoDecimal!=null ? (()=>{const {min,seg}=decimalToMinSeg(ritmoDecimal);return fmtMinSeg(min,seg);})() : null; // "min:seg" por km
    setCardioLogs(p=>[...p,{
      id:uid(), type:logForm.type, date:logForm.date, duration:Math.round(dur),
      durMin:+logForm.durMin||0, durSeg:+logForm.durSeg||0,
      distance:km||0, ritmo, weather, kcal:estKcal(logForm.type,dur),
    }]);
    setLogForm({date:nowISO(),durMin:30,durSeg:0,km:"",type:"running"});
    setShowLog(false);
  };

  const addRest=()=>{
    if(restDate>nowISO()){alert("No podés cargar un día de descanso en una fecha futura.");return;}
    setRestDays(p=>[...p,{id:uid(),date:restDate,notes:restNotes,sleep:restSleep}]);setRestDate(nowISO());setRestNotes("");setRestSleep(3);setShowRest(false);
  };

  const openCardioLog = log => { setEditingCardio(log); setCardioEditForm({...log}); };
  const saveCardioEdit = () => {
    if(cardioEditForm.date>nowISO()){alert("No podés poner una fecha futura para una sesión de cardio.");return;}
    const orig = editingCardio;
    const changed = orig.type!==cardioEditForm.type || orig.duration!==cardioEditForm.duration ||
      orig.distance!==cardioEditForm.distance || orig.kcal!==cardioEditForm.kcal ||
      orig.notes!==cardioEditForm.notes || orig.date!==cardioEditForm.date;
    setCardioLogs(prev => prev.map(l => l.id===orig.id ? {...cardioEditForm, modified: changed?true:!!l.modified} : l));
    setEditingCardio(null); setCardioEditForm(null);
  };
  const deleteCardioLog = () => {
    if(!confirm("¿Eliminar esta sesión de cardio?")) return;
    setCardioLogs(prev => prev.filter(l=>l.id!==editingCardio.id));
    setEditingCardio(null); setCardioEditForm(null);
  };

  const openRestDay = d => { setEditingRest(d); setRestEditForm({...d}); };
  const saveRestEdit = () => {
    if(restEditForm.date>nowISO()){alert("No podés poner una fecha futura para un día de descanso.");return;}
    const orig = editingRest;
    const changed = orig.sleep!==restEditForm.sleep || orig.notes!==restEditForm.notes || orig.date!==restEditForm.date;
    setRestDays(prev => prev.map(d => d.id===orig.id ? {...restEditForm, modified: changed?true:!!d.modified} : d));
    setEditingRest(null); setRestEditForm(null);
  };
  const deleteRestDay = () => {
    if(!confirm("¿Eliminar este día de descanso?")) return;
    setRestDays(prev => prev.filter(d=>d.id!==editingRest.id));
    setEditingRest(null); setRestEditForm(null);
  };

  const sortedC=[...cardioLogs].sort((a,b)=>b.date.localeCompare(a.date));
  const sortedR=[...restDays].sort((a,b)=>b.date.localeCompare(a.date));
  const totalKcal=cardioLogs.reduce((s,l)=>s+l.kcal,0);

  return <div className="fu" style={{display:"flex",flexDirection:"column",gap:16}}>

    {/* ── Botones grandes estilo Estadísticas: Cardio / Descanso ── */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
      {[["cardio","🏃 Cardio",cardioLogs.length+" ses."],["descanso","😴 Descanso",restDays.length+" días"]].map(([key,label,val])=>{
        const active = section===key;
        return <button key={key} onClick={()=>{setSection(key);setShowMenu(false);}}
          style={{background:active?T.accent:T.card,borderRadius:12,padding:"14px 10px",textAlign:"center",
            border:`1px solid ${active?T.accent:T.border}`,cursor:"pointer",transition:"all .15s"}}>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:active?"#000":T.text,letterSpacing:".04em"}}>{label}</div>
          <div style={{fontSize:11,color:active?"#000":T.muted,marginTop:2}}>{val}</div>
        </button>;
      })}
    </div>

    {/* ══ SECCIÓN: CARDIO ══ */}
    {section==="cardio"&&<>
      <div>
        <Btn onClick={()=>setShowMenu(v=>!v)} full v="success">
          <Icon n="plus" s={15}/>Cardio
        </Btn>
        {showMenu&&<div className="fu" style={{marginTop:8,
          background:T.card,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
          <button onClick={()=>{setShowSchedule(true);setShowMenu(false);}}
            style={{width:"100%",padding:"14px 16px",background:"none",border:"none",cursor:"pointer",
              display:"flex",alignItems:"center",gap:10,color:T.text,fontSize:13,fontWeight:600,textAlign:"left"}}>
            📅 Programar cardio
          </button>
          <div style={{height:1,background:T.border}}/>
          <button onClick={()=>openLogFor(null)}
            style={{width:"100%",padding:"14px 16px",background:"none",border:"none",cursor:"pointer",
              display:"flex",alignItems:"center",gap:10,color:T.text,fontSize:13,fontWeight:600,textAlign:"left"}}>
            ✏️ Registrar cardio
          </button>
        </div>}
      </div>

      {showSchedule&&<Modal title="📅 PROGRAMAR CARDIO" onClose={()=>setShowSchedule(false)} width={380}>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {TYPES.map(t=><button key={t.value} onClick={()=>setScheduleForm(f=>({...f,type:t.value}))}
              style={{background:scheduleForm.type===t.value?t.color+"33":"transparent",color:scheduleForm.type===t.value?t.color:T.muted,
                border:`1px solid ${scheduleForm.type===t.value?t.color:T.border}`,borderRadius:20,padding:"6px 12px",fontSize:12,cursor:"pointer",fontWeight:600}}>
              {t.label}
            </button>)}
          </div>
          <Field label="Fecha" type="date" value={scheduleForm.date} onChange={v=>setScheduleForm(f=>({...f,date:v}))} min={nowISO()}/>
          <Field label="Hora de inicio" type="time" value={scheduleForm.time} onChange={v=>setScheduleForm(f=>({...f,time:v}))}/>
          <Field label="Duración estimada (min)" type="number" value={scheduleForm.duracion} onChange={v=>setScheduleForm(f=>({...f,duracion:toNum(v)}))} min={1}/>
          <div style={{display:"flex",gap:8}}>
            <Btn full v="ghost" onClick={addSchedule} style={{border:`1px solid ${T.purple}55`,color:T.purple}}>
              <Icon n="check" s={15}/>Programar
            </Btn>
            <Btn v="ghost" onClick={()=>setShowSchedule(false)}>Cancelar</Btn>
          </div>
        </div>
      </Modal>}

      {showLog&&<Modal title="✏️ REGISTRAR CARDIO" onClose={()=>setShowLog(false)} width={380}>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {TYPES.map(t=><button key={t.value} onClick={()=>setLogForm(f=>({...f,type:t.value}))}
              style={{background:logForm.type===t.value?t.color+"33":"transparent",color:logForm.type===t.value?t.color:T.muted,
                border:`1px solid ${logForm.type===t.value?t.color:T.border}`,borderRadius:20,padding:"6px 12px",fontSize:12,cursor:"pointer",fontWeight:600}}>
              {t.label}
            </button>)}
          </div>
          <Field label="Fecha" type="date" value={logForm.date} onChange={v=>setLogForm(f=>({...f,date:v}))} max={nowISO()}/>
          <div>
            <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
              Tiempo registrado (min:seg)
            </label>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <input type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" value={logForm.durMin} min={0} placeholder="min"
                onChange={e=>setLogForm(f=>({...f,durMin:e.target.value}))}
                style={{flex:1,background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,padding:"9px 10px",fontSize:13,boxSizing:"border-box"}}/>
              <span style={{color:T.muted,fontWeight:700}}>:</span>
              <input type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" value={logForm.durSeg} min={0} max={59} placeholder="seg"
                onChange={e=>setLogForm(f=>({...f,durSeg:Math.min(59,+e.target.value||0)}))}
                style={{flex:1,background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,padding:"9px 10px",fontSize:13,boxSizing:"border-box"}}/>
            </div>
          </div>
          <Field label="Distancia (km)" type="number" value={logForm.km} onChange={v=>setLogForm(f=>({...f,km:v}))} min={0} step={0.01}/>
          {logForm.km&&parseFloat(logForm.km)>0&&(logForm.durMin||logForm.durSeg)&&<div style={{background:T.surface,borderRadius:10,padding:"8px 12px",textAlign:"center"}}>
            <span style={{fontSize:12,color:T.muted}}>Ritmo (min:seg/km): </span>
            <b style={{fontSize:15,color:T.green}}>
              {(()=>{const dur=minSegToDecimal(logForm.durMin,logForm.durSeg);const {min,seg}=decimalToMinSeg(dur/parseFloat(logForm.km));return fmtMinSeg(min,seg);})()}
            </b>
          </div>}
          <p style={{fontSize:11,color:T.muted}}>🌤 El clima y las calorías quemadas se calculan solos al guardar.</p>
          <div style={{display:"flex",gap:8}}>
            <Btn full v="success" onClick={submitLog} disabled={fetchingWeather}>
              {fetchingWeather?<><Spinner/>Guardando...</>:<><Icon n="check" s={15}/>Guardar</>}
            </Btn>
            <Btn v="ghost" onClick={()=>setShowLog(false)}>Cancelar</Btn>
          </div>
        </div>
      </Modal>}

      {/* ── CARDIO PROGRAMADO ── */}
      {cardioSchedule.length>0&&<>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:15,color:T.purple,letterSpacing:".08em"}}>CARDIO PROGRAMADO</div>
        {[...cardioSchedule].sort((a,b)=>a.date.localeCompare(b.date)).map(entry=>{
          const t=TYPES.find(x=>x.value===entry.type)||TYPES[0];
          return <div key={entry.id} style={{background:T.card,borderRadius:12,padding:"12px 14px",
            border:`1px solid ${T.border}`,display:"flex",gap:12,alignItems:"center"}}>
            <div style={{background:T.purple+"22",borderRadius:8,padding:"8px 10px",fontSize:20}}>{t.label.slice(0,2)}</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:13}}>{t.label}</div>
              <div style={{fontSize:11,color:T.muted}}>{weekday(entry.date)} {fmtDateFull(entry.date)} · 🕐 {entry.time} · {entry.duracion}min</div>
            </div>
            <button onClick={()=>deleteSchedule(entry.id)} style={{background:"none",color:T.muted,border:"none",fontSize:16,cursor:"pointer"}}>×</button>
          </div>;
        })}
      </>}

      {/* ── HISTORIAL CARDIO ── */}
      {sortedC.length>0&&<>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:15,color:T.muted,letterSpacing:".08em"}}>HISTORIAL CARDIO</div>
          <Badge color={T.red}>{totalKcal.toLocaleString()} kcal totales</Badge>
        </div>
        <div style={{background:T.card,borderRadius:12,border:`1px solid ${T.border}`,overflow:"hidden"}}>
          {sortedC.map((l,i)=>{
            const t=TYPES.find(x=>x.value===l.type)||TYPES[0];
            return <div key={l.id} onClick={()=>openCardioLog(l)}
              style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",cursor:"pointer",
                borderTop:i>0?`1px solid ${T.border}`:"none"}}>
              <span style={{fontSize:15,flexShrink:0}}>{t.label.slice(0,2)}</span>
              <span style={{fontSize:12,fontWeight:600,minWidth:64,flexShrink:0}}>{fmtDate(l.date)}</span>
              <span style={{fontSize:11,color:T.muted,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {l.duration}min{l.distance?` · ${l.distance}km`:""}{l.ritmo?` · ${l.ritmo}/km`:""} · {l.kcal}kcal{l.weather?` · ${l.weather.icon} ${l.weather.temp}°`:""}
              </span>
              {l.modified&&<span style={{color:T.blue,fontSize:12,flexShrink:0}}>✏</span>}
            </div>;
          })}
        </div>
      </>}
    </>}

    {/* ══ SECCIÓN: DESCANSO ══ */}
    {section==="descanso"&&<>
      <Btn full v="info" onClick={()=>setShowRest(v=>!v)}>
        <Icon n="moon" s={15}/>Registrar descanso
      </Btn>

      {showRest&&<div className="fu" style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.blue}44`}}>
        <h3 style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.blue,letterSpacing:".05em",marginBottom:12}}>DÍA DE DESCANSO</h3>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Field label="Fecha" type="date" value={restDate} onChange={setRestDate} max={nowISO()}/>
          <div>
            <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
              Calidad de sueño: <b style={{color:T.accent}}>{restSleep}/5</b>
            </label>
            <div style={{display:"flex",gap:8}}>
              {[1,2,3,4,5].map(n=><button key={n} onClick={()=>setRestSleep(n)}
                style={{width:36,height:36,borderRadius:"50%",fontWeight:700,fontSize:14,cursor:"pointer",
                  background:restSleep===n?T.accent+"33":"transparent",color:restSleep===n?T.accent:T.muted,
                  border:`1px solid ${restSleep===n?T.accent:T.border}`}}>{n}</button>)}
            </div>
          </div>
          <Field label="Notas" value={restNotes} onChange={setRestNotes} placeholder="Cómo te sentiste..."/>
          <div style={{display:"flex",gap:8}}>
            <Btn v="info" onClick={addRest}><Icon n="check" s={15}/>Guardar</Btn>
            <Btn v="ghost" onClick={()=>setShowRest(false)}>Cancelar</Btn>
          </div>
        </div>
      </div>}

      {sortedR.length>0&&<div style={{background:T.card,borderRadius:12,border:`1px solid ${T.blue}33`,overflow:"hidden"}}>
        {sortedR.map((d,i)=>(
          <div key={d.id} onClick={()=>openRestDay(d)}
            style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",cursor:"pointer",
              borderTop:i>0?`1px solid ${T.border}`:"none"}}>
            <span style={{fontSize:15,flexShrink:0}}>😴</span>
            <span style={{fontSize:12,fontWeight:600,minWidth:64,flexShrink:0}}>{fmtDate(d.date)}</span>
            <span style={{fontSize:11,color:T.muted,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              Sueño {d.sleep}/5{d.notes?` · ${d.notes}`:""}
            </span>
            {d.modified&&<span style={{color:T.blue,fontSize:12,flexShrink:0}}>✏</span>}
          </div>
        ))}
      </div>}
    </>}

    {/* ── POPUP AUTOMÁTICO: ¿REALIZASTE EL CARDIO PROGRAMADO? ── */}
    {pendingConfirm&&<Modal title="¿HICISTE CARDIO?" onClose={()=>setPendingConfirm(null)} width={360}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <p style={{fontSize:13,color:T.text}}>
          Tenías programado <b>{(TYPES.find(t=>t.value===pendingConfirm.type)||{label:pendingConfirm.type}).label}</b> el{" "}
          {weekday(pendingConfirm.date)} {fmtDateFull(pendingConfirm.date)} a las {pendingConfirm.time}. ¿Lo hiciste?
        </p>
        <div style={{display:"flex",gap:8}}>
          <Btn full v="success" onClick={()=>openLogFor(pendingConfirm)}>Sí, registrar</Btn>
          <Btn v="ghost" onClick={()=>{deleteSchedule(pendingConfirm.id);}}>No lo hice</Btn>
        </div>
      </div>
    </Modal>}

    {/* ── MODAL: EDITAR SESIÓN DE CARDIO ── */}
    {editingCardio&&cardioEditForm&&<Modal title="EDITAR SESIÓN DE CARDIO" onClose={()=>{setEditingCardio(null);setCardioEditForm(null);}} width={380}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:20,color:T.orange,letterSpacing:".04em"}}>
            {(TYPES.find(t=>t.value===cardioEditForm.type)||{label:cardioEditForm.type}).label}
          </div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>{weekday(cardioEditForm.date)} {fmtDateFull(cardioEditForm.date)}</div>
          {editingCardio.modified&&<Badge color={T.blue}>✏ editado</Badge>}
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {TYPES.map(t=><button key={t.value} onClick={()=>setCardioEditForm(f=>({...f,type:t.value}))}
            style={{background:cardioEditForm.type===t.value?t.color+"33":"transparent",color:cardioEditForm.type===t.value?t.color:T.muted,
              border:`1px solid ${cardioEditForm.type===t.value?t.color:T.border}`,borderRadius:20,padding:"5px 10px",fontSize:11,cursor:"pointer",fontWeight:600}}>
            {t.label}
          </button>)}
        </div>
        <Field label="Fecha" type="date" value={cardioEditForm.date} onChange={v=>setCardioEditForm(f=>({...f,date:v}))} max={nowISO()}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Field label="Duración (min)" type="number" value={cardioEditForm.duration} onChange={v=>setCardioEditForm(f=>({...f,duration:toNum(v)}))} min={0}/>
          <Field label="Distancia km" type="number" value={cardioEditForm.distance||0} onChange={v=>setCardioEditForm(f=>({...f,distance:toNum(v)}))} min={0} step={0.01}/>
        </div>
        <Field label="Calorías" type="number" value={cardioEditForm.kcal} onChange={v=>setCardioEditForm(f=>({...f,kcal:toNum(v)}))} min={0}/>
        <div style={{display:"flex",gap:8}}>
          <Btn full v="success" onClick={saveCardioEdit}><Icon n="check" s={14}/>Guardar cambios</Btn>
          <Btn v="danger" onClick={deleteCardioLog}><Icon n="trash" s={14}/></Btn>
        </div>
      </div>
    </Modal>}

    {/* ── MODAL: EDITAR DÍA DE DESCANSO ── */}
    {editingRest&&restEditForm&&<Modal title="EDITAR DÍA DE DESCANSO" onClose={()=>{setEditingRest(null);setRestEditForm(null);}} width={380}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:20,color:T.blue,letterSpacing:".04em"}}>😴 Descanso</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>{weekday(restEditForm.date)} {fmtDateFull(restEditForm.date)}</div>
          {editingRest.modified&&<Badge color={T.blue}>✏ editado</Badge>}
        </div>
        <Field label="Fecha" type="date" value={restEditForm.date} onChange={v=>setRestEditForm(f=>({...f,date:v}))} max={nowISO()}/>
        <div>
          <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
            Calidad de sueño: <b style={{color:T.accent}}>{restEditForm.sleep}/5</b>
          </label>
          <div style={{display:"flex",gap:8}}>
            {[1,2,3,4,5].map(n=><button key={n} onClick={()=>setRestEditForm(f=>({...f,sleep:n}))}
              style={{width:36,height:36,borderRadius:"50%",fontWeight:700,fontSize:14,cursor:"pointer",
                background:restEditForm.sleep===n?T.accent+"33":"transparent",color:restEditForm.sleep===n?T.accent:T.muted,
                border:`1px solid ${restEditForm.sleep===n?T.accent:T.border}`}}>{n}</button>)}
          </div>
        </div>
        <Field label="Notas" value={restEditForm.notes||""} onChange={v=>setRestEditForm(f=>({...f,notes:v}))} textarea placeholder="Cómo te sentiste..."/>
        <div style={{display:"flex",gap:8}}>
          <Btn full v="success" onClick={saveRestEdit}><Icon n="check" s={14}/>Guardar cambios</Btn>
          <Btn v="danger" onClick={deleteRestDay}><Icon n="trash" s={14}/></Btn>
        </div>
      </div>
    </Modal>}
  </div>;
}

// ─── Nutrition View ───────────────────────────────────────────────────────────
const PROD_KEY = "gd_products_v1";
const loadProducts = () => { const v=lsGet(PROD_KEY,[]); return Array.isArray(v)?v:[]; };
const saveProducts = v => lsSet(PROD_KEY, v);

// ── Base de alimentos comunes por categoría (valores aprox. por 100g) ──
const FOOD_DB = {
  "Proteínas": [
    {nombre:"Pollo (pechuga)",  kcal:165, prot:31, carb:0,  gras:3.6},
    {nombre:"Carne de vaca (magra)", kcal:217, prot:26, carb:0, gras:12},
    {nombre:"Pescado (merluza)",kcal:90,  prot:19, carb:0,  gras:1.3},
    {nombre:"Salmón",           kcal:208, prot:20, carb:0,  gras:13},
    {nombre:"Huevo",            kcal:155, prot:13, carb:1.1,gras:11},
    {nombre:"Atún (lata al agua)",kcal:116,prot:26, carb:0, gras:1},
    {nombre:"Cerdo (lomo)",     kcal:143, prot:26, carb:0,  gras:3.5},
    {nombre:"Tofu",             kcal:76,  prot:8,  carb:1.9,gras:4.8},
    {nombre:"Lentejas",         kcal:116, prot:9,  carb:20, gras:0.4},
    {nombre:"Garbanzos",        kcal:164, prot:8.9,carb:27, gras:2.6},
    {nombre:"Proteína whey (polvo)",kcal:400,prot:80,carb:8,gras:5},
    {nombre:"Queso fresco/cottage",kcal:98, prot:11, carb:3.4,gras:4.3},
    {nombre:"Yogur griego natural",kcal:97, prot:9,  carb:3.6,gras:5},
  ],
  "Carbohidratos": [
    {nombre:"Arroz blanco",     kcal:130, prot:2.7, carb:28, gras:0.3},
    {nombre:"Arroz integral",   kcal:123, prot:2.6, carb:26, gras:1},
    {nombre:"Avena",            kcal:389, prot:17,  carb:66, gras:7},
    {nombre:"Papa",             kcal:77,  prot:2,   carb:17, gras:0.1},
    {nombre:"Batata/boniato",   kcal:86,  prot:1.6, carb:20, gras:0.1},
    {nombre:"Pan integral",     kcal:247, prot:13,  carb:41, gras:3.4},
    {nombre:"Pasta",            kcal:131, prot:5,   carb:25, gras:1.1},
    {nombre:"Quinoa",           kcal:120, prot:4.4, carb:21, gras:1.9},
    {nombre:"Banana",           kcal:89,  prot:1.1, carb:23, gras:0.3},
    {nombre:"Tortillas de maíz",kcal:218, prot:5.7, carb:44, gras:2.8},
  ],
  "Grasas": [
    {nombre:"Palta/aguacate",   kcal:160, prot:2,   carb:9,  gras:15},
    {nombre:"Aceite de oliva",  kcal:884, prot:0,   carb:0,  gras:100},
    {nombre:"Almendras",        kcal:579, prot:21,  carb:22, gras:50},
    {nombre:"Nueces",           kcal:654, prot:15,  carb:14, gras:65},
    {nombre:"Maní/cacahuate",   kcal:567, prot:26,  carb:16, gras:49},
    {nombre:"Manteca de maní",  kcal:588, prot:25,  carb:20, gras:50},
    {nombre:"Chía",             kcal:486, prot:17,  carb:42, gras:31},
  ],
  "Vegetales": [
    {nombre:"Brócoli",          kcal:34,  prot:2.8, carb:7,  gras:0.4},
    {nombre:"Espinaca",         kcal:23,  prot:2.9, carb:3.6,gras:0.4},
    {nombre:"Tomate",           kcal:18,  prot:0.9, carb:3.9,gras:0.2},
    {nombre:"Lechuga",          kcal:15,  prot:1.4, carb:2.9,gras:0.2},
    {nombre:"Zanahoria",        kcal:41,  prot:0.9, carb:10, gras:0.2},
    {nombre:"Zapallo/calabaza", kcal:26,  prot:1,   carb:6.5,gras:0.1},
    {nombre:"Pimiento",         kcal:31,  prot:1,   carb:6,  gras:0.3},
    {nombre:"Cebolla",          kcal:40,  prot:1.1, carb:9.3,gras:0.1},
  ],
  "Frutas": [
    {nombre:"Manzana",          kcal:52,  prot:0.3, carb:14, gras:0.2},
    {nombre:"Naranja",          kcal:47,  prot:0.9, carb:12, gras:0.1},
    {nombre:"Frutillas",        kcal:32,  prot:0.7, carb:7.7,gras:0.3},
    {nombre:"Arándanos",        kcal:57,  prot:0.7, carb:14, gras:0.3},
    {nombre:"Uva",              kcal:69,  prot:0.7, carb:18, gras:0.2},
    {nombre:"Mandarina",        kcal:53,  prot:0.8, carb:13, gras:0.3},
  ],
  "Lácteos": [
    {nombre:"Leche entera",     kcal:61,  prot:3.2, carb:4.8,gras:3.3},
    {nombre:"Leche descremada", kcal:34,  prot:3.4, carb:5,  gras:0.1},
    {nombre:"Queso (cremoso)",  kcal:264, prot:18,  carb:2,  gras:21},
    {nombre:"Yogur entero",     kcal:61,  prot:3.5, carb:4.7,gras:3.3},
  ],
};

// ── Storage de preferencias y plan de dieta ──
const DIET_KEY = "gd_diet_v1";
const loadDiet = () => lsGet(DIET_KEY, {});
const saveDiet = d  => lsSet(DIET_KEY, d);

const RECIPES_KEY = "gd_recipes_v1";
const loadRecipes = () => { const v=lsGet(RECIPES_KEY,[]); return Array.isArray(v)?v:[]; };
const saveRecipes = v => lsSet(RECIPES_KEY, v);

// ── Calendario semanal de comidas — cada día (0=Dom...6=Sáb, igual que Date.getDay()) tiene sus propias comidas ──
const DEFAULT_MEALS_TEMPLATE = () => [
  {id:uid(),name:"Desayuno",time:"08:00",order:0,items:[]},
  {id:uid(),name:"Almuerzo",time:"13:00",order:1,items:[]},
  {id:uid(),name:"Merienda",time:"17:00",order:2,items:[]},
  {id:uid(),name:"Cena",    time:"21:00",order:3,items:[]},
];
const buildDefaultWeek = () => {
  const week = {};
  for(let d=0; d<7; d++) week[d] = DEFAULT_MEALS_TEMPLATE();
  return week;
};
// Lee el calendario semanal, migrando automáticamente el formato viejo (un solo día fijo) si existe
function loadWeekMeals() {
  const stored = loadDiet();
  if(stored.weekMeals) return stored.weekMeals;
  if(stored.dayMeals) {
    // Formato viejo: un solo plan de comidas aplicado a todos los días. Lo copiamos a los 7 días
    // para no perder lo ya cargado; a partir de ahora cada día se puede diferenciar.
    const week = {};
    for(let d=0; d<7; d++) week[d] = stored.dayMeals.map(m=>({
      ...m, id:uid(), items:(m.items||[]).map(i=>({...i,id:uid()}))
    }));
    return week;
  }
  return buildDefaultWeek();
}

// ── Equivalencias de gramos a unidades caseras (aproximado) ──
// Cada entrada: gramos que equivalen a 1 unidad de esa medida casera
const UNIT_HINTS = {
  // ── Proteínas ──
  "Pollo (pechuga)":       {unit:"filete pequeño", g:120},
  "Carne de vaca (magra)": {unit:"bife mediano", g:150},
  "Pescado (merluza)":     {unit:"filete", g:120},
  "Salmón":                {unit:"porción", g:150},
  "Huevo":                 {unit:"huevo", g:50},
  "Clara de huevo":        {unit:"clara", g:33},
  "Atún (lata al agua)":   {unit:"lata", g:120},
  "Cerdo (lomo)":          {unit:"porción", g:120},
  "Tofu":                  {unit:"porción", g:100},
  "Lentejas":              {unit:"cucharón (cocidas)", g:100},
  "Garbanzos":             {unit:"cucharón (cocidos)", g:100},
  "Proteína whey (polvo)": {unit:"scoop/medida", g:30},
  "Queso fresco/cottage":  {unit:"cucharada sopera", g:20},
  "Yogur griego natural":  {unit:"cucharada sopera", g:15},
  // ── Carbohidratos ──
  "Arroz blanco":          {unit:"cucharada sopera (crudo)", g:12},
  "Arroz integral":        {unit:"cucharada sopera (crudo)", g:12},
  "Avena":                 {unit:"cucharada sopera", g:9},
  "Papa":                  {unit:"papa mediana", g:150},
  "Batata/boniato":        {unit:"batata mediana", g:130},
  "Pan integral":          {unit:"rebanada", g:30},
  "Pasta":                 {unit:"cucharón (cocida)", g:80},
  "Quinoa":                {unit:"cucharada sopera (cruda)", g:12},
  "Banana":                {unit:"banana mediana", g:120},
  "Tortillas de maíz":     {unit:"tortilla", g:25},
  // ── Grasas ──
  "Palta/aguacate":        {unit:"palta mediana", g:150},
  "Aceite de oliva":       {unit:"cucharada sopera", g:13},
  "Almendras":             {unit:"puñado (10 unidades)", g:12},
  "Nueces":                {unit:"puñado (5 mitades)", g:15},
  "Maní/cacahuate":        {unit:"puñado", g:15},
  "Manteca de maní":       {unit:"cucharada sopera", g:16},
  "Chía":                  {unit:"cucharada sopera", g:12},
  // ── Vegetales ──
  "Brócoli":               {unit:"taza (floretes)", g:90},
  "Espinaca":              {unit:"puñado (hojas)", g:30},
  "Tomate":                {unit:"tomate mediano", g:120},
  "Lechuga":               {unit:"hoja", g:20},
  "Zanahoria":             {unit:"zanahoria mediana", g:60},
  "Zapallo/calabaza":      {unit:"taza (cubos)", g:120},
  "Pimiento":              {unit:"pimiento mediano", g:120},
  "Cebolla":               {unit:"cebolla mediana", g:110},
  // ── Frutas ──
  "Manzana":               {unit:"manzana mediana", g:150},
  "Naranja":               {unit:"naranja mediana", g:150},
  "Frutillas":             {unit:"puñado (5-6 unidades)", g:80},
  "Arándanos":             {unit:"puñado", g:60},
  "Uva":                   {unit:"racimo pequeño", g:50},
  "Mandarina":             {unit:"mandarina", g:80},
  // ── Lácteos ──
  "Leche entera":          {unit:"taza", g:240},
  "Leche descremada":      {unit:"taza", g:240},
  "Queso (cremoso)":       {unit:"cucharada sopera", g:20},
  "Yogur entero":          {unit:"pote individual", g:125},
};
// Devuelve algo como "150g (≈1 manzana mediana)" o "26g (≈2 cucharadas soperas)"
function approxUnits(nombre, gramos) {
  const hint = UNIT_HINTS[nombre];
  if(!hint || !gramos) return `${rnd(gramos)}g`;
  const n = gramos / hint.g;
  const nRound = n<1 ? Math.round(n*10)/10 : Math.round(n*2)/2; // media unidad de precisión
  if(nRound<=0) return `${rnd(gramos)}g`;
  return `${rnd(gramos)}g (≈${nRound} ${hint.unit}${nRound!==1&&!hint.unit.includes("(")?"s":""})`;
}
// Convierte una cantidad en "unidades" de un alimento a gramos
function unitsToGrams(nombre, unidades) {
  const hint = UNIT_HINTS[nombre];
  if(!hint) return 0;
  return Math.round(unidades*hint.g);
}

// ── Campo de cantidad "doble": gramos ↔ unidad natural, sincronizados ──
function QtyDualInput({foodName, grams, onChange, customHint}) {
  const hint = customHint || UNIT_HINTS[foodName];
  const unitVal = hint && grams ? +(grams/hint.g).toFixed(2) : "";
  return (
    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
      <div style={{display:"flex",alignItems:"center",gap:4}}>
        <input type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" value={grams||""} placeholder="0"
          onChange={e=>onChange(+e.target.value||0)}
          style={{width:64,background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
            color:T.text,padding:"7px 8px",fontSize:12,height:34}}/>
        <span style={{fontSize:11,color:T.muted}}>g</span>
      </div>
      {hint&&<>
        <span style={{fontSize:11,color:T.border}}>=</span>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <input type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" step="0.1" value={unitVal} placeholder="0"
            onChange={e=>onChange(Math.round((+e.target.value||0)*hint.g))}
            style={{width:56,background:T.surface,border:`1px solid ${T.accent}44`,borderRadius:8,
              color:T.accent,padding:"7px 8px",fontSize:12,height:34}}/>
          <span style={{fontSize:11,color:T.muted,whiteSpace:"nowrap"}}>
            {hint.unit}{unitVal!==1?"s":""}
          </span>
        </div>
      </>}
    </div>
  );
}

const MEDIDAS = [
  {value:"porcion",    singular:"porción",          plural:"porciones"},
  {value:"vaso",        singular:"vaso",              plural:"vasos"},
  {value:"taza",        singular:"taza",              plural:"tazas"},
  {value:"cucharada",   singular:"cucharada sopera",  plural:"cucharadas soperas"},
  {value:"cucharadita", singular:"cucharadita",       plural:"cucharaditas"},
  {value:"unidad",      singular:"unidad",            plural:"unidades"},
  {value:"rebanada",    singular:"rebanada",          plural:"rebanadas"},
  {value:"puñado",      singular:"puñado",            plural:"puñados"},
];

function ProductForm({initial, onSave, onCancel, title="GUARDAR PRODUCTO"}) {
  const hasBoth = initial?._por_porcion && initial?._por_100g;
  const [mode,  setMode]  = useState("porcion"); // "porcion" | "100g"

  const [form,  setForm]  = useState(()=>{
    const base = initial||{nombre:"",porcion:"100g",kcal:0,proteinas:0,carbos:0,azucares:0,grasas:0,grasas_sat:0,sodio:0,fibra:0};
    return {id:base.id, createdAt:base.createdAt,
      nombre:base.nombre||"",porcion:base.porcion||"100g",kcal:base.kcal||0,
      proteinas:base.proteinas||0,carbos:base.carbos||0,azucares:base.azucares||0,
      grasas:base.grasas||0,grasas_sat:base.grasas_sat||0,sodio:base.sodio||0,fibra:base.fibra||0,
      porciones_envase:base.porciones_envase||0,
      medida: base.medida||"porcion", medidaCantidad: base.medidaCantidad||1,
      equivaleCantidad: base.equivaleCantidad||"", equivaleUnidad: base.equivaleUnidad||"g"};
  });
  const s = (k,v) => setForm(f=>({...f,[k]:v}));

  // Cada vez que cambia la medida/cantidad, actualizamos el texto de "porción" automáticamente
  const setMedida = (k,v) => {
    setForm(f=>{
      const next = {...f,[k]:v};
      const m = MEDIDAS.find(x=>x.value===next.medida)||MEDIDAS[0];
      const cant = toNum(next.medidaCantidad)||1;
      next.porcion = `${cant} ${cant===1?m.singular:m.plural}`;
      return next;
    });
  };

  const switchMode = (m) => {
    const src = m==="100g" ? initial._por_100g : initial._por_porcion;
    if(!src) return;
    setMode(m);
    setForm(f=>({...f, porcion:src.porcion||f.porcion,
      kcal:src.kcal||0, proteinas:src.proteinas||0, carbos:src.carbos||0,
      azucares:src.azucares||0, grasas:src.grasas||0, grasas_sat:src.grasas_sat||0,
      fibra:src.fibra||0, sodio:src.sodio||0}));
  };

  return <div className="fu" style={{background:T.card,borderRadius:14,padding:16,
    border:`2px solid ${T.accent}66`,display:"flex",flexDirection:"column",gap:12}}>
    <h3 style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.accent,letterSpacing:".05em"}}>{title}</h3>

    {/* Selector porción vs 100g */}
    {hasBoth&&<div>
      <p style={{fontSize:12,color:T.muted,marginBottom:8}}>¿Guardar valores por porción o por 100g?</p>
      <div style={{display:"flex",background:T.surface,borderRadius:8,padding:3,gap:3}}>
        {[["porcion",`Por porción (${initial._por_porcion?.porcion||""})`],["100g","Por 100g"]].map(([m,l])=>(
          <button key={m} onClick={()=>switchMode(m)}
            style={{flex:1,padding:"7px 0",borderRadius:6,fontWeight:600,fontSize:12,
              background:mode===m?T.accent:"transparent",color:mode===m?"#000":T.muted,
              border:"none",cursor:"pointer"}}>
            {l}
          </button>
        ))}
      </div>
      {initial.porciones_envase>0&&<p style={{fontSize:11,color:T.muted,marginTop:6}}>
        📦 {initial.porciones_envase} porciones por envase
      </p>}
    </div>}

    <p style={{fontSize:12,color:T.muted}}>Revisá y corregí los valores si es necesario.</p>

    <div style={{gridColumn:"1/-1"}}>
      <Field label="Nombre del producto *" value={form.nombre} onChange={v=>s("nombre",v)} placeholder="Ej: Galletas integrales"/>
    </div>

    {/* Medida coloquial + cantidad */}
    <div>
      <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
        Medida
      </label>
      <div style={{display:"flex",gap:8}}>
        <input type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" value={form.medidaCantidad} min={0} step={0.5}
          onChange={e=>setMedida("medidaCantidad",e.target.value)}
          style={{width:70,background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
            color:T.text,padding:"9px 10px",fontSize:13}}/>
        <select value={form.medida} onChange={e=>setMedida("medida",e.target.value)}
          style={{flex:1,background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
            color:T.text,padding:"9px 10px",fontSize:13}}>
          {MEDIDAS.map(m=><option key={m.value} value={m.value}>{m.plural}</option>)}
        </select>
      </div>
      <p style={{fontSize:11,color:T.muted,marginTop:6}}>Se va a mostrar como: <b style={{color:T.accent}}>{form.porcion}</b></p>
    </div>

    {/* Equivalencia en gramos o mililitros */}
    <div>
      <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
        Equivale a
      </label>
      <div style={{display:"flex",gap:8}}>
        <input type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" value={form.equivaleCantidad} min={0}
          onChange={e=>s("equivaleCantidad",e.target.value)} placeholder="Ej: 200"
          style={{flex:1,background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
            color:T.text,padding:"9px 10px",fontSize:13}}/>
        <div style={{display:"flex",background:T.surface,borderRadius:8,padding:3,gap:3}}>
          {["g","ml"].map(u=>(
            <button key={u} onClick={()=>s("equivaleUnidad",u)}
              style={{padding:"7px 16px",borderRadius:6,fontWeight:700,fontSize:12,cursor:"pointer",
                background:form.equivaleUnidad===u?T.accent:"transparent",
                color:form.equivaleUnidad===u?"#000":T.muted,border:"none"}}>
              {u}
            </button>
          ))}
        </div>
      </div>
    </div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
      <Field label="Calorías (kcal)" type="number" value={form.kcal} onChange={v=>s("kcal",toNum(v))} min={0}/>
      <Field label="Proteínas (g)" type="number" value={form.proteinas} onChange={v=>s("proteinas",toNum(v))} min={0} step={0.1}/>
      <Field label="Carbohidratos (g)" type="number" value={form.carbos} onChange={v=>s("carbos",toNum(v))} min={0} step={0.1}/>
      <Field label="Azúcares (g)" type="number" value={form.azucares} onChange={v=>s("azucares",toNum(v))} min={0} step={0.1}/>
      <Field label="Grasas totales (g)" type="number" value={form.grasas} onChange={v=>s("grasas",toNum(v))} min={0} step={0.1}/>
      <Field label="Grasas saturadas (g)" type="number" value={form.grasas_sat} onChange={v=>s("grasas_sat",toNum(v))} min={0} step={0.1}/>
      <Field label="Fibra (g)" type="number" value={form.fibra} onChange={v=>s("fibra",toNum(v))} min={0} step={0.1}/>
      <Field label="Sodio (mg)" type="number" value={form.sodio} onChange={v=>s("sodio",toNum(v))} min={0}/>
    </div>

    {/* Preview macros */}
    <div style={{background:T.surface,borderRadius:10,padding:"10px 14px"}}>
      <div style={{fontSize:10,color:T.muted,marginBottom:8,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>
        Vista previa — {form.porcion}{form.equivaleCantidad?` (${form.equivaleCantidad}${form.equivaleUnidad})`:""}
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center"}}>
        {[["Kcal",form.kcal,T.orange],["Prot",`${form.proteinas}g`,T.blue],
          ["Carb",`${form.carbos}g`,T.accent],["Azúc",`${form.azucares}g`,T.muted],
          ["Gras",`${form.grasas}g`,T.green],["Fibra",`${form.fibra}g`,T.muted],
          ["Sodio",`${form.sodio}mg`,T.muted]].map(([l,v,c])=>(
          <div key={l} style={{textAlign:"center",minWidth:52}}>
            <div style={{fontSize:9,color:T.muted}}>{l}</div>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:c}}>{v}</div>
          </div>
        ))}
      </div>
    </div>

    <div style={{display:"flex",gap:8}}>
      <Btn full v="success" onClick={()=>onSave({...form,
          medidaCantidad: toNum(form.medidaCantidad)||1,
          equivaleCantidad: form.equivaleCantidad===""?"":toNum(form.equivaleCantidad),
        })}>
        <Icon n="check" s={15}/>Guardar en biblioteca
      </Btn>
      <Btn v="ghost" onClick={onCancel}>Descartar</Btn>
    </div>
  </div>;
}

function NutritionView({profile,logs,cardioLogs,nutriTab,setNutriTab,selectedWeekday,setSelectedWeekday,showCopyDay,setShowCopyDay,weekOffset,setWeekOffset,showScheduleMeals,setShowScheduleMeals,schedule,cardioSchedule,restDays,supplementLogs,setSupplementLogs}) {
  const today = nowISO();
  const [aiErr, setAiErr] = useState("");   // errores de funciones IA (generar receta, dieta, etc.)

  // ── Biblioteca de productos ────────────────────────────────
  const [products,  setProducts]  = useState(loadProducts);
  const [scanning,  setScanning]  = useState(false);
  const [scanErr,   setScanErr]   = useState("");
  const [scanResult,setScanResult]= useState(null);   // resultado de la IA antes de guardar
  const [editProd,  setEditProd]  = useState(null);   // producto en edición
  const fileRef = useRef();

  // ── Plan del día ───────────────────────────────────────────

  // Las kcal objetivo varían según lo que ese día de la semana suele tener programado
  const dayHasRoutine = (schedule||[]).some(s=>s.repeat && s.weekday===selectedWeekday);
  const dayHasCardio  = (cardioSchedule||[]).some(s=>new Date(s.date+"T12:00:00").getDay()===selectedWeekday);
  const dayIsRest     = (restDays||[]).some(r=>new Date(r.date+"T12:00:00").getDay()===selectedWeekday);
  const tgt   = calcTargets(profile, getDayActivityFactor({hasRoutine:dayHasRoutine, hasCardio:dayHasCardio, isRest:dayIsRest}));
  const goal  = GOALS[profile?.objetivo]||GOALS.hipertrofia;
  const supls = SUPLS[profile?.objetivo]||SUPLS.hipertrofia;

  // Convierte una dosis tipo "1.5–2g/kg" en gramos reales según tu peso actual.
  // Si la dosis no es por kg (ej: "2000 UI", "5g/día"), se deja el texto tal cual.
  const parseDoseForWeight = (doseStr, peso) => {
    const m = doseStr.match(/([\d.]+)(?:[–-]([\d.]+))?\s*g\/kg/);
    if(!m || !peso) return null;
    const low = parseFloat(m[1])*peso, high = m[2]?parseFloat(m[2])*peso:low;
    return high>low ? `${rnd(low)}–${rnd(high)}g/día` : `${rnd(low)}g/día`;
  };

  // ── Registro de tomas de suplementos ──
  const [showSupLog, setShowSupLog] = useState(false);
  const [supLogName, setSupLogName] = useState("");
  const [supLogQty,  setSupLogQty]  = useState("");
  const [supLogUnit, setSupLogUnit] = useState("g");
  const [supLogTime, setSupLogTime] = useState(nowTime());
  const [editingSupLog, setEditingSupLog] = useState(null); // id del log que se está editando

  const todaySupLogs = (supplementLogs||[]).filter(l=>l.date===today).sort((a,b)=>a.time.localeCompare(b.time));

  const openSupLogForm = (presetName="") => {
    setSupLogName(presetName); setSupLogQty(""); setSupLogUnit("g"); setSupLogTime(nowTime());
    setEditingSupLog(null); setShowSupLog(true);
  };
  const openEditSupLog = (log) => {
    setSupLogName(log.name); setSupLogQty(String(log.qty)); setSupLogUnit(log.unit); setSupLogTime(log.time);
    setEditingSupLog(log.id); setShowSupLog(true);
  };
  const saveSupLog = () => {
    if(!supLogName.trim()) return;
    if(editingSupLog){
      setSupplementLogs(p=>p.map(l=>l.id!==editingSupLog?l:{...l,name:supLogName.trim(),qty:toNum(supLogQty),unit:supLogUnit,time:supLogTime}));
    } else {
      setSupplementLogs(p=>[...p,{id:uid(),date:today,time:supLogTime,name:supLogName.trim(),qty:toNum(supLogQty),unit:supLogUnit}]);
    }
    setShowSupLog(false); setEditingSupLog(null);
  };
  const deleteSupLog = (id) => {
    if(confirm("¿Eliminar este registro de toma?")) setSupplementLogs(p=>p.filter(l=>l.id!==id));
  };

  // ── Mi Dieta: alimentos permitidos + recetas + calendario semanal de comidas (todo vinculado) ──
  const storedDiet = loadDiet();
  const [allowedFoods, setAllowedFoods] = useState(()=>storedDiet.allowedFoods||[]);
  const [recipes,      setRecipes]      = useState(loadRecipes);
  // weekMeals: objeto {0:[...],1:[...],...,6:[...]} — un array de comidas independiente por día de la semana.
  // Cada comida: {id, name, time, order, items:[{id,item,cantidad_g,kcal,prot,carb,gras,_fromRecipe}]}
  const [weekMeals, setWeekMealsState] = useState(loadWeekMeals);
  // Referencias siempre actualizadas — para poder guardar la versión más reciente sin depender
  // de closures viejas (importante en el guardado al desmontar, más abajo).
  const allowedFoodsRef = useRef(allowedFoods);
  useEffect(()=>{ allowedFoodsRef.current = allowedFoods; },[allowedFoods]);
  const weekMealsRef = useRef(weekMeals);
  useEffect(()=>{ weekMealsRef.current = weekMeals; },[weekMeals]);
  // Guarda en localStorage con una demora corta (no en cada tecla mientras escribís una cantidad),
  // pero mucho antes de que dé tiempo a cambiar de pestaña — así no se pierde nada ni se traba la app.
  const saveDietTimeoutRef = useRef(null);
  const setWeekMeals = (updater) => setWeekMealsState(prev => {
    const next = typeof updater==="function" ? updater(prev) : updater;
    clearTimeout(saveDietTimeoutRef.current);
    saveDietTimeoutRef.current = setTimeout(()=>{
      saveDiet({allowedFoods:allowedFoodsRef.current, weekMeals:next});
    }, 400);
    return next;
  });
  // Al desmontar (cambiar de pestaña), guardar ya mismo lo que haya quedado pendiente sin esperar la demora.
  useEffect(()=>()=>{
    if(saveDietTimeoutRef.current){
      clearTimeout(saveDietTimeoutRef.current);
      saveDiet({allowedFoods:allowedFoodsRef.current, weekMeals:weekMealsRef.current});
    }
  },[]);
  const [copyTargets, setCopyTargets] = useState([]);

  const [showFoodPicker, setShowFoodPicker] = useState(false);
  const [addingToMeal, setAddingToMeal] = useState(null); // id de comida donde se está agregando algo
  const [editingMealInfo, setEditingMealInfo] = useState(null); // mealId de la comida cuyo nombre/horario se está editando
  const [selectedMealId, setSelectedMealId] = useState(null); // qué comida está "abierta" para ver su detalle
  const [editMealName, setEditMealName] = useState("");
  const [editMealTime, setEditMealTime] = useState("");
  const [pickMealFood, setPickMealFood] = useState("");
  const [pickQty,       setPickQty]     = useState(100);
  const [genLoading,   setGenLoading]   = useState(null);
  const [dietSuggestions, setDietSuggestions] = useState([]);
  const [loadingDiets, setLoadingDiets] = useState(false);
  const [replacingIng, setReplacingIng] = useState(null);
  const [showNewMeal,  setShowNewMeal]  = useState(false);
  const [newMealName,  setNewMealName]  = useState("");
  const [newMealTime,  setNewMealTime]  = useState("12:00");
  // Editor de recetas
  const [showRecipeEditor, setShowRecipeEditor] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null); // receta en edición (o null = nueva)
  const [recipeForm, setRecipeForm] = useState(null);
  const [newIngFood, setNewIngFood] = useState("");
  const [newIngQty,  setNewIngQty]  = useState(100);
  const [editingIngId, setEditingIngId] = useState(null); // ingrediente cuya cantidad se está editando
  const [genImgLoading, setGenImgLoading] = useState(false);
  const recipeImgRef = useRef();

  useEffect(()=>{ saveDiet({allowedFoods,weekMeals}); },[allowedFoods,weekMeals]);
  useEffect(()=>{ saveRecipes(recipes); },[recipes]);
  useEffect(()=>{ saveProducts(products); },[products]);

  const toggleAllowedFood = food => {
    setAllowedFoods(p => p.find(f=>f.nombre===food.nombre)
      ? p.filter(f=>f.nombre!==food.nombre)
      : [...p,{...food,id:uid()}]);
  };
  const isFoodAllowed = nombre => allowedFoods.some(f=>f.nombre===nombre);

  // ── Comidas del día seleccionado: CRUD. Se ordenan siempre por horario (ascendente) ──
  const dayMeals = weekMeals[selectedWeekday]||[];
  const sortedMeals = [...dayMeals].sort((a,b)=>(a.time||"").localeCompare(b.time||""));
  const isToday = selectedWeekday === new Date().getDay() && (weekOffset||0)===0;

  const mealTotals = meal => (meal.items||[]).reduce((a,i)=>({
    kcal:a.kcal+(+i.kcal||0), prot:a.prot+(+i.prot||0), carb:a.carb+(+i.carb||0), gras:a.gras+(+i.gras||0)
  }),{kcal:0,prot:0,carb:0,gras:0});

  const dayMealTotals = dayMeals.reduce((acc,m)=>{
    const t=mealTotals(m);
    return {kcal:acc.kcal+t.kcal, prot:acc.prot+t.prot, carb:acc.carb+t.carb, gras:acc.gras+t.gras};
  },{kcal:0,prot:0,carb:0,gras:0});

  // ¿Esta comida está en su horario ahora mismo? (solo tiene sentido si estamos viendo el día de hoy)
  const isTodayBeingViewed = selectedWeekday===new Date().getDay() && (weekOffset||0)===0;
  const isMealInTimeNow = (m) => {
    if(!isTodayBeingViewed || !m.time) return false;
    const addMin = (hhmm,min) => { const [h,mm]=hhmm.split(":").map(Number); const t=h*60+mm+min; return `${String(Math.floor((t%1440)/60)).padStart(2,"0")}:${String(t%60).padStart(2,"0")}`; };
    const nowClk = nowTime();
    return nowClk>=m.time && nowClk<=addMin(m.time,45);
  };

  const addMeal = () => {
    if(!newMealName.trim()) return;
    setWeekMeals(p=>({...p,[selectedWeekday]:[...(p[selectedWeekday]||[]),
      {id:uid(),name:newMealName.trim(),time:newMealTime,order:(p[selectedWeekday]||[]).length,items:[]}]}));
    setNewMealName(""); setNewMealTime("12:00"); setShowNewMeal(false);
  };
  const removeMeal = id => setWeekMeals(p=>({...p,[selectedWeekday]:(p[selectedWeekday]||[]).filter(m=>m.id!==id)}));
  const updateMeal = (id, patch) => setWeekMeals(p=>({...p,[selectedWeekday]:(p[selectedWeekday]||[]).map(m=>m.id!==id?m:{...m,...patch})}));
  // Copiar las comidas del día seleccionado a otros días de la semana (reemplaza lo que tengan)
  const copyDayTo = (targetWeekdays) => {
    const source = (weekMeals[selectedWeekday]||[]).map(m=>({
      ...m, id:uid(), items:(m.items||[]).map(i=>({...i,id:uid()}))
    }));
    setWeekMeals(p=>{
      const next={...p};
      targetWeekdays.forEach(d=>{
        next[d] = source.map(m=>({...m, id:uid(), items:m.items.map(i=>({...i,id:uid()}))}));
      });
      return next;
    });
    setShowCopyDay(false); setCopyTargets([]);
  };

  const addFoodToMeal = (mealId, food, gramos=100) => {
    const factor = gramos/100;
    setWeekMeals(p=>({...p,[selectedWeekday]:(p[selectedWeekday]||[]).map(m=>m.id!==mealId?m:{...m,items:[...m.items,{
      id:uid(), item:food.nombre, cantidad_g:gramos,
      kcal:rnd(food.kcal*factor), prot:+(food.prot*factor).toFixed(1),
      carb:+(food.carb*factor).toFixed(1), gras:+(food.gras*factor).toFixed(1),
      _per100:{kcal:food.kcal, prot:food.prot, carb:food.carb, gras:food.gras},
    }]})}));
  };
  const addRecipeToMeal = (mealId, recipe) => {
    // Copiamos cada ingrediente de la receta como un ítem independiente y editable
    // (variación de ese día) — la receta original en tu biblioteca queda intacta.
    // Guardamos también el valor de referencia por 100g, para poder reescalar sin
    // depender del valor actual (que podría llegar a 0 en algún momento).
    const newItems = (recipe.ingredientes||[]).map(ing => {
      const base = ing.cantidad_g>0 ? 100/ing.cantidad_g : 0;
      return {
        id: uid(), item: ing.item, cantidad_g: ing.cantidad_g,
        kcal: ing.kcal, prot: ing.prot, carb: ing.carb, gras: ing.gras,
        _fromRecipe: recipe.id, _recipeName: recipe.nombre,
        _per100: {kcal:ing.kcal*base, prot:ing.prot*base, carb:ing.carb*base, gras:ing.gras*base},
      };
    });
    setWeekMeals(p=>({...p,[selectedWeekday]:(p[selectedWeekday]||[]).map(m=>m.id!==mealId?m:{...m,items:[...m.items,...newItems]})}));
  };
  const removeFoodFromMeal = (mealId, itemId) => {
    setWeekMeals(p=>({...p,[selectedWeekday]:(p[selectedWeekday]||[]).map(m=>m.id!==mealId?m:{...m,items:m.items.filter(i=>i.id!==itemId)})}));
  };
  const updateMealItemQty = (mealId, itemId, newGramos) => {
    setWeekMeals(p=>({...p,[selectedWeekday]:(p[selectedWeekday]||[]).map(m=>{
      if(m.id!==mealId) return m;
      return {...m, items:m.items.map(i=>{
        if(i.id!==itemId) return i;
        if(i._per100){
          const f = newGramos/100;
          return {...i, cantidad_g:newGramos,
            kcal:rnd(i._per100.kcal*f), prot:+(i._per100.prot*f).toFixed(1),
            carb:+(i._per100.carb*f).toFixed(1), gras:+(i._per100.gras*f).toFixed(1)};
        }
        // Ítems viejos sin referencia guardada (de antes de este arreglo): si no tienen
        // con qué reescalar, al menos actualizamos el número para no bloquear la edición.
        if(!i.cantidad_g) return {...i, cantidad_g:newGramos};
        const rate = 1/i.cantidad_g;
        return {...i, cantidad_g:newGramos,
          kcal:rnd(i.kcal*rate*newGramos), prot:+(i.prot*rate*newGramos).toFixed(1),
          carb:+(i.carb*rate*newGramos).toFixed(1), gras:+(i.gras*rate*newGramos).toFixed(1)};
      })};
    })}));
  };

  // ── Recetas: crear/editar/eliminar (independiente de las comidas del día) ──
  const openNewRecipe = () => {
    setEditingRecipe(null);
    setRecipeForm({id:uid(),nombre:"",ingredientes:[],preparacion:"",foto:""});
    setNewIngFood(""); setNewIngQty(100); setEditingIngId(null);
    setShowRecipeEditor(true);
  };
  const openEditRecipe = r => {
    setEditingRecipe(r);
    setRecipeForm({...r, ingredientes:r.ingredientes.map(i=>({...i}))});
    setNewIngFood(""); setNewIngQty(100); setEditingIngId(null);
    setShowRecipeEditor(true);
  };

  const generateRecipeImage = async () => {
    if(!recipeForm?.nombre?.trim()) return;
    setGenImgLoading(true);
    try {
      const svg = await genSvgRecipe(recipeForm.nombre.trim());
      if(svg && svg.trim().startsWith("<svg")) {
        const url = "data:image/svg+xml;charset=utf-8,"+encodeURIComponent(svg);
        setRecipeForm(f=>({...f, foto:url}));
      }
    } catch(err) { setAiErr(err?.message||"Error al generar la imagen."); }
    setGenImgLoading(false);
  };

  const [photoCompressing, setPhotoCompressing] = useState(false);
  const handleRecipePhotoUpload = async e => {
    const file = e.target.files?.[0]; if(!file) return;
    setPhotoCompressing(true);
    try{
      const compressed = await compressImageFile(file);
      setRecipeForm(f=>({...f, foto:compressed}));
    }catch{
      alert("❌ No se pudo procesar la imagen. Probá con otra foto.");
    }
    setPhotoCompressing(false);
    e.target.value = "";
  };
  const addIngredientToRecipe = (food, gramos=100) => {
    const factor = gramos/100;
    setRecipeForm(f=>({...f, ingredientes:[...f.ingredientes,{
      id:uid(), item:food.nombre, cantidad_g:gramos,
      kcal:rnd(food.kcal*factor), prot:+(food.prot*factor).toFixed(1),
      carb:+(food.carb*factor).toFixed(1), gras:+(food.gras*factor).toFixed(1),
      _per100:{kcal:food.kcal, prot:food.prot, carb:food.carb, gras:food.gras},
    }]}));
  };
  const removeIngredientFromRecipe = ingId => {
    setRecipeForm(f=>({...f, ingredientes:f.ingredientes.filter(i=>i.id!==ingId)}));
  };
  // Recalcula macros de un ingrediente cuando se edita su cantidad, manteniendo la proporción original (por 100g)
  const updateIngredientQty = (ingId, newGrams) => {
    setRecipeForm(f=>({...f, ingredientes:f.ingredientes.map(i=>{
      if(i.id!==ingId) return i;
      if(i._per100){
        const factor = newGrams/100;
        return {...i, cantidad_g:newGrams,
          kcal:rnd(i._per100.kcal*factor), prot:+(i._per100.prot*factor).toFixed(1),
          carb:+(i._per100.carb*factor).toFixed(1), gras:+(i._per100.gras*factor).toFixed(1)};
      }
      // Ingredientes viejos sin referencia guardada: al menos no bloqueamos la edición del número.
      if(!i.cantidad_g) return {...i, cantidad_g:newGrams};
      const rate = 1/i.cantidad_g;
      return {...i, cantidad_g:newGrams,
        kcal:+(i.kcal*rate*newGrams).toFixed(1), prot:+(i.prot*rate*newGrams).toFixed(1),
        carb:+(i.carb*rate*newGrams).toFixed(1), gras:+(i.gras*rate*newGrams).toFixed(1)};
    })}));
  };
  const recipeTotals = ing => (ing||[]).reduce((a,i)=>({
    kcal:a.kcal+(+i.kcal||0), prot:a.prot+(+i.prot||0), carb:a.carb+(+i.carb||0), gras:a.gras+(+i.gras||0)
  }),{kcal:0,prot:0,carb:0,gras:0});
  const saveRecipe = () => {
    if(!recipeForm.nombre.trim()||recipeForm.ingredientes.length===0) return;
    const t = recipeTotals(recipeForm.ingredientes);
    const toSave = {...recipeForm, kcal:rnd(t.kcal), prot:+t.prot.toFixed(1), carb:+t.carb.toFixed(1), gras:+t.gras.toFixed(1)};
    setRecipes(p=>p.find(r=>r.id===toSave.id)?p.map(r=>r.id===toSave.id?toSave:r):[...p,toSave]);
    setShowRecipeEditor(false); setRecipeForm(null); setEditingRecipe(null);
  };
  const deleteRecipe = id => {
    if(confirm("¿Eliminar esta receta?")) setRecipes(p=>p.filter(r=>r.id!==id));
  };

  // Generar receta con IA (crea una receta reutilizable en la biblioteca)
  const generateRecipeAI = async (targetName="comida") => {
    setGenLoading("recipe");
    try {
      const portion = {kcal:tgt.meta*0.25, prot:tgt.protG*0.25, carb:tgt.carbG*0.25, gras:tgt.grasG*0.25};
      const recipe = await generateMealRecipe(targetName, allowedFoods, portion, goal.label);
      if(recipe && recipe.ingredientes) {
        const ingredientes = recipe.ingredientes.map(ing=>({
          id:uid(), item:ing.item, cantidad_g: parseFloat(ing.cantidad)||100,
          kcal:ing.kcal||0, prot:ing.prot||0, carb:ing.carb||0, gras:ing.gras||0,
        }));
        const t = recipeTotals(ingredientes);
        const newRecipe = {id:uid(), nombre:recipe.nombre||targetName, ingredientes,
          preparacion:recipe.preparacion||"", kcal:rnd(t.kcal), prot:+t.prot.toFixed(1),
          carb:+t.carb.toFixed(1), gras:+t.gras.toFixed(1)};
        setRecipes(p=>[newRecipe,...p]);
        openEditRecipe(newRecipe);
      }
    } catch(err) { setAiErr(err?.message||"Error al generar la receta."); }
    setGenLoading(null);
  };

  const doReplaceIngredient = async (ingId) => {
    setReplacingIng(ingId);
    try {
      const recipe = {nombre:recipeForm.nombre, ingredientes:recipeForm.ingredientes.map(i=>({item:i.item,cantidad:i.cantidad_g+"g"}))};
      const ing = recipeForm.ingredientes.find(i=>i.id===ingId);
      const replacement = await replaceIngredient(recipe, ing.item, allowedFoods);
      if(replacement) {
        setRecipeForm(f=>({...f, ingredientes:f.ingredientes.map(i=>i.id===ingId?{
          ...i, item:replacement.item, cantidad_g:parseFloat(replacement.cantidad)||i.cantidad_g,
          kcal:replacement.kcal||0, prot:replacement.prot||0, carb:replacement.carb||0, gras:replacement.gras||0,
        }:i)}));
      }
    } catch(err) { setAiErr(err?.message||"Error al reemplazar el ingrediente."); }
    setReplacingIng(null);
  };

  const fetchDietSuggestions = async () => {
    setLoadingDiets(true); setDietSuggestions([]);
    try { const list = await searchFitnessDiets(goal.label, tgt); setDietSuggestions(list||[]); }
    catch(err) { setAiErr(err?.message||"Error al buscar dietas recomendadas."); }
    setLoadingDiets(false);
  };

  // ── Escanear etiqueta ─────────────────────────────────────
  const handleScan = async e => {
    const file = e.target.files?.[0]; if(!file) return;
    setScanErr(""); setScanning(true); setScanResult(null);
    const reader = new FileReader();
    reader.onload = async ev => {
      const b64  = ev.target.result.split(",")[1];
      const mime = file.type||"image/jpeg";
      try {
        const res = await scanLabel(b64, mime);
        if(res.error) {
          setScanErr(res.error==="no_label"
            ? "No se detectó una etiqueta nutricional. Intentá con otra foto más clara."
            : "No se pudo leer la etiqueta. Mejor iluminación y enfoque.");
        } else {
          // Nuevo formato con por_porcion y por_100g
          const hasPorcion = res.por_porcion && res.por_100g;
          const base = hasPorcion ? res.por_porcion : res;
          setScanResult({
            id: uid(),
            nombre: res.nombre||"Producto escaneado",
            porcion: base.porcion||"100g",
            kcal: base.kcal||0,
            proteinas: base.proteinas||0,
            carbos: base.carbos||0,
            azucares: base.azucares||0,
            grasas: base.grasas||0,
            grasas_sat: base.grasas_sat||0,
            fibra: base.fibra||0,
            sodio: base.sodio||0,
            porciones_envase: res.porciones_envase||0,
            // guardar ambas versiones para que el usuario elija
            _por_porcion: res.por_porcion||null,
            _por_100g: res.por_100g||null,
            createdAt: nowISO()
          });
          setNutriTab("biblioteca");
        }
      } catch(err) { setScanErr(err.message||"❌ Error al procesar la imagen. Verificá tu conexión."); }
      setScanning(false);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const saveProduct = prod => {
    if(!prod.nombre.trim()) return;
    setProducts(p => p.find(x=>x.id===prod.id)
      ? p.map(x=>x.id===prod.id?prod:x)
      : [...p,{...prod,id:prod.id||uid(),createdAt:nowISO()}]);
    setEditProd(null); setScanResult(null);
  };

  const deleteProduct = id => {
    if(confirm("¿Eliminar este producto de la biblioteca?"))
      setProducts(p=>p.filter(x=>x.id!==id));
  };

  const addToDay = prod => {
    // Agregar el producto escaneado/manual a "alimentos permitidos" para poder usarlo en comidas y recetas,
    // llevando también su medida propia (vaso, unidad, etc.) si la definió al cargarlo.
    // Si no cargó ninguna medida propia, usamos la tabla de equivalencias conocidas como respaldo
    // (por si el nombre coincide con un alimento común, ej: "Clara de huevo").
    let unitHint = null;
    if(prod.equivaleCantidad && toNum(prod.equivaleCantidad)>0){
      const m = MEDIDAS.find(x=>x.value===prod.medida)||MEDIDAS[0];
      unitHint = { unit: m.singular, g: toNum(prod.equivaleCantidad)/(toNum(prod.medidaCantidad)||1) };
    } else if(UNIT_HINTS[(prod.nombre||"").trim()]) {
      unitHint = UNIT_HINTS[(prod.nombre||"").trim()];
    }
    const nombreLimpio = (prod.nombre||"").trim();
    const asFood = {nombre:nombreLimpio, kcal:+prod.kcal||0, prot:+prod.proteinas||0, carb:+prod.carbos||0, gras:+prod.grasas||0, unitHint};
    const yaExiste = allowedFoods.some(f=>f.nombre.trim().toLowerCase()===nombreLimpio.toLowerCase());
    if(yaExiste){
      alert(`"${nombreLimpio}" ya estaba en tus alimentos permitidos.`);
    } else {
      setAllowedFoods(p => [...p,{...asFood,id:uid()}]);
      alert(`✅ "${nombreLimpio}" agregado a tus alimentos permitidos.\n\nYa lo podés usar en recetas y en Comidas del día.`);
    }
    setNutriTab("comida");
  };


  return <div className="fu" style={{display:"flex",flexDirection:"column",gap:14}}>

    {aiErr&&<div style={{background:T.red+"22",border:`1px solid ${T.red}55`,borderRadius:12,
      padding:"12px 16px",fontSize:13,color:T.red,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
      <span>⚠ {aiErr}</span>
      <button onClick={()=>setAiErr("")} style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:16}}>×</button>
    </div>}

    {/* Referencia del día que se está editando (los botones de días viven en el header) */}
    {nutriTab==="comidasdia"&&<div style={{fontSize:12,color:T.muted}}>
      Editando: <b style={{color:T.text}}>{WLONG[selectedWeekday]}</b>{isToday&&<span style={{color:T.accent}}> · Hoy</span>}
    </div>}

    {/* ══ BOTÓN 1: ALIMENTOS Y RECETAS ══ */}
    {nutriTab==="comida"&&<>
      {/* Alimentos permitidos */}
      <div style={{background:T.card,borderRadius:12,border:`1px solid ${T.border}`,padding:"10px 14px"}}>
        <button onClick={()=>setShowFoodPicker(v=>!v)} style={{width:"100%",background:"none",border:"none",
          cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:14,color:T.text,letterSpacing:".04em"}}>
            🍴 ALIMENTOS PERMITIDOS
            <span style={{background:T.accent,color:"#000",fontSize:9,fontWeight:700,
              borderRadius:10,padding:"1px 6px",marginLeft:6}}>{allowedFoods.length}</span>
          </span>
          <span style={{color:T.muted,fontSize:12}}>{showFoodPicker?"▲":"▼"}</span>
        </button>
        {!showFoodPicker&&<p style={{fontSize:11,color:T.muted,marginTop:3}}>
          Elegí qué alimentos consumís habitualmente. Vas a poder usarlos en recetas y en las comidas del día.
        </p>}

        {showFoodPicker&&<div style={{marginTop:12,display:"flex",flexDirection:"column",gap:14}}>
          {Object.entries(FOOD_DB).map(([category,foods])=>(
            <div key={category}>
              <div style={{fontSize:12,color:T.accent,fontWeight:700,marginBottom:6,letterSpacing:".04em"}}>{category}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {foods.map(food=>{
                  const active=isFoodAllowed(food.nombre);
                  return <button key={food.nombre} onClick={()=>toggleAllowedFood(food)}
                    style={{background:active?T.accent:T.surface,color:active?"#000":T.muted,
                      border:`1px solid ${active?T.accent:T.border}`,borderRadius:20,
                      padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer",
                      display:"flex",alignItems:"center",gap:4}}>
                    {active&&"✓ "}{food.nombre}
                  </button>;
                })}
              </div>
            </div>
          ))}
        </div>}
      </div>

      {/* ── MIS RECETAS — biblioteca reutilizable ── */}
      <div style={{background:T.card,borderRadius:14,border:`1px solid ${T.border}`,padding:"14px 16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.text,letterSpacing:".05em"}}>
            🍳 MIS RECETAS
            <span style={{background:T.purple,color:"#fff",fontSize:10,fontWeight:700,
              borderRadius:10,padding:"1px 7px",marginLeft:8}}>{recipes.length}</span>
          </span>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:recipes.length>0?12:0,flexWrap:"wrap"}}>
          <Btn sz="sm" onClick={openNewRecipe} disabled={allowedFoods.length===0}>
            <Icon n="plus" s={12}/>Nueva receta
          </Btn>
          <Btn sz="sm" v="ghost" onClick={()=>generateRecipeAI("comida")}
            disabled={genLoading!==null||allowedFoods.length===0}
            style={{border:`1px solid ${T.purple}55`,color:T.purple}}>
            {genLoading==="recipe"?<><Spinner/>Generando...</>:<>✨ Generar con IA</>}
          </Btn>
        </div>
        {allowedFoods.length===0&&<p style={{fontSize:11,color:T.orange}}>
          ⚠ Elegí alimentos permitidos arriba primero.
        </p>}
        {recipes.length>0&&<div style={{display:"flex",flexDirection:"column",gap:8}}>
          {recipes.map(r=>(
            <div key={r.id} style={{background:T.surface,borderRadius:10,padding:"10px 12px",
              display:"flex",alignItems:"center",gap:10}}>
              {r.foto
                ? <img src={r.foto} alt="" style={{width:44,height:44,objectFit:"cover",
                    borderRadius:8,flexShrink:0}}/>
                : <div style={{width:44,height:44,borderRadius:8,background:T.card,
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🍽</div>}
              <div onClick={()=>openEditRecipe(r)} style={{flex:1,minWidth:0,cursor:"pointer"}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>{r.nombre}</div>
                <div style={{fontSize:10,color:T.muted,marginTop:2}}>
                  {r.ingredientes.length} ingredientes · {rnd(r.kcal)}kcal · P{rnd(r.prot)} C{rnd(r.carb)} G{rnd(r.gras)}
                </div>
              </div>
              <button onClick={()=>openEditRecipe(r)}
                style={{background:T.card,color:T.muted,border:`1px solid ${T.border}`,
                  borderRadius:6,padding:"4px 8px",fontSize:11,cursor:"pointer",flexShrink:0}}>✏</button>
              <button onClick={()=>deleteRecipe(r.id)}
                style={{background:T.red+"22",color:T.red,border:`1px solid ${T.red}44`,
                  borderRadius:6,padding:"4px 8px",fontSize:11,cursor:"pointer",flexShrink:0}}>🗑</button>
            </div>
          ))}
        </div>}
      </div>

    </>}

    {/* ══ BOTÓN 2: COMIDAS DEL DÍA ══ */}
    {nutriTab==="comidasdia"&&<>
      {/* Resumen del día vs meta */}
      <div style={{background:T.card,borderRadius:14,border:`1px solid ${T.border}`,padding:"14px 16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.text,letterSpacing:".05em"}}>TOTAL DEL DÍA</span>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:20,color:dayMealTotals.kcal>tgt.meta?T.red:T.accent}}>
            {rnd(dayMealTotals.kcal)} / {tgt.meta} kcal
          </span>
        </div>
        <MacroBar label="Proteínas"     value={dayMealTotals.prot} target={tgt.protG} color={T.blue}/>
        <MacroBar label="Carbohidratos" value={dayMealTotals.carb} target={tgt.carbG} color={T.orange}/>
        <MacroBar label="Grasas"        value={dayMealTotals.gras} target={tgt.grasG} color={T.green}/>
      </div>

      {/* ── COMIDAS DEL DÍA — botones compactos, el detalle se abre al tocar uno ── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.text,letterSpacing:".05em"}}>
          📋 COMIDAS DE HOY
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
        {sortedMeals.map(meal=>{
          const totals = mealTotals(meal);
          const active = selectedMealId===meal.id;
          const inTimeNow = isMealInTimeNow(meal);
          const highlightColor = inTimeNow ? T.orange : T.accent;
          return <button key={meal.id} onClick={()=>setSelectedMealId(active?null:meal.id)}
            style={{background:active?highlightColor:(inTimeNow?T.orange+"22":T.card),borderRadius:12,padding:"12px 10px",textAlign:"center",
              border:`1px solid ${active||inTimeNow?highlightColor:T.border}`,cursor:"pointer",transition:"all .15s",
              boxShadow:inTimeNow&&!active?`0 0 0 2px ${T.orange}55`:"none"}}>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:15,color:active?"#000":(inTimeNow?T.orange:T.accent),letterSpacing:".03em"}}>
              {inTimeNow&&!active&&"🔔 "}{meal.name}
            </div>
            <div style={{fontSize:10,color:active?"#000":T.muted,marginTop:1}}>🕐 {meal.time}</div>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:19,color:active?"#000":T.text,marginTop:5}}>{rnd(totals.kcal)} kcal</div>
            <div style={{fontSize:9,color:active?"#000":T.muted,marginTop:3,fontWeight:600}}>
              P{rnd(totals.prot)} · C{rnd(totals.carb)} · G{rnd(totals.gras)}
            </div>
          </button>;
        })}

        {!showNewMeal&&<button onClick={()=>setShowNewMeal(true)}
          style={{background:"none",borderRadius:12,padding:"12px 10px",textAlign:"center",
            border:`1px dashed ${T.accent}66`,cursor:"pointer",color:T.accent,
            display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,minHeight:88}}>
          <Icon n="plus" s={20}/>
          <span style={{fontSize:12,fontWeight:700}}>Agregar comida</span>
        </button>}
      </div>

      {showNewMeal&&<Modal title="+ NUEVA COMIDA" onClose={()=>setShowNewMeal(false)} width={360}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="Nombre" value={newMealName} onChange={setNewMealName} placeholder="Ej: Colación, Post-entreno..."/>
          <Field label="Hora" type="time" value={newMealTime} onChange={setNewMealTime}/>
          <div style={{display:"flex",gap:8}}>
            <Btn full v="success" onClick={addMeal} disabled={!newMealName.trim()}>
              <Icon n="check" s={13}/>Agregar
            </Btn>
            <Btn v="ghost" onClick={()=>setShowNewMeal(false)}>Cancelar</Btn>
          </div>
        </div>
      </Modal>}

      {/* ── Detalle de la comida seleccionada — ahora en ventana emergente ── */}
      {selectedMealId&&sortedMeals.filter(m=>m.id===selectedMealId).map(meal=>{
        const totals = mealTotals(meal);
        const pctProt = tgt.protG>0 ? Math.round((totals.prot/tgt.protG)*100) : 0;
        const pctCarb = tgt.carbG>0 ? Math.round((totals.carb/tgt.carbG)*100) : 0;
        const pctGras = tgt.grasG>0 ? Math.round((totals.gras/tgt.grasG)*100) : 0;
        return <Modal key={meal.id} title={`${meal.name} · ${rnd(totals.kcal)} kcal`} onClose={()=>setSelectedMealId(null)} width={420}>
          <div style={{display:"flex",flexDirection:"column",gap:0,margin:"-4px -20px -20px",overflow:"hidden",borderRadius:"0 0 16px 16px"}}>
          <div style={{padding:"0 16px 12px",borderBottom:`1px solid ${T.border}`,
            display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:11,color:T.muted}}>🕐 {meal.time}</span>
            <button onClick={()=>{setEditingMealInfo(meal.id);setEditMealName(meal.name);setEditMealTime(meal.time);}}
              style={{background:"none",color:T.muted,border:"none",cursor:"pointer",fontSize:13,flexShrink:0}}>✏ Editar nombre/hora</button>
            <button onClick={()=>{
                if(confirm(`¿Eliminar "${meal.name}" definitivamente? Se van a borrar todos los alimentos que tiene cargados. Esta acción no se puede deshacer.`)){
                  removeMeal(meal.id); setSelectedMealId(null);
                }
              }}
              style={{background:"none",color:T.red,border:"none",cursor:"pointer",fontSize:12,flexShrink:0,marginLeft:"auto"}}>🗑 Eliminar comida</button>
          </div>

          {editingMealInfo===meal.id&&<div style={{padding:"12px 16px",background:T.surface,
            borderBottom:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:8}}>
            <Field label="Nombre" value={editMealName} onChange={setEditMealName}/>
            <Field label="Hora" type="time" value={editMealTime} onChange={setEditMealTime}/>
            <div style={{display:"flex",gap:8}}>
              <Btn sz="sm" v="success" disabled={!editMealName.trim()} onClick={()=>{
                updateMeal(meal.id,{name:editMealName.trim(),time:editMealTime});
                setEditingMealInfo(null);
              }}>
                <Icon n="check" s={13}/>Guardar
              </Btn>
              <Btn sz="sm" v="ghost" onClick={()=>setEditingMealInfo(null)}>Cancelar</Btn>
            </div>
          </div>}

          {/* Porcentaje de aporte de esta comida respecto a la meta diaria */}
          {meal.items.length>0&&<div style={{padding:"8px 16px",display:"flex",gap:14,
            background:T.surface,borderBottom:`1px solid ${T.border}`}}>
            {[["P",pctProt,T.blue],["C",pctCarb,T.orange],["G",pctGras,T.green]].map(([l,pct,col])=>(
              <div key={l} style={{flex:1,display:"flex",flexDirection:"column",gap:3}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:10}}>
                  <span style={{color:T.muted,fontWeight:700}}>{l}</span>
                  <span style={{color:col,fontWeight:700}}>{pct}%</span>
                </div>
                <div style={{height:4,background:T.border,borderRadius:2,overflow:"hidden"}}>
                  <div style={{height:"100%",background:col,width:`${Math.min(100,pct)}%`,transition:"width .3s"}}/>
                </div>
              </div>
            ))}
          </div>}

          {meal.items.length>0&&<div style={{padding:"8px 16px",display:"flex",flexDirection:"column",gap:6}}>
            {(()=>{
              // Agrupar ítems consecutivos que vienen de la misma receta (solo para el encabezado visual)
              const groups = [];
              meal.items.forEach(item=>{
                const last = groups[groups.length-1];
                if(item._fromRecipe && last?.recipeId===item._fromRecipe) last.items.push(item);
                else groups.push({recipeId:item._fromRecipe||null, recipeName:item._recipeName||item.item||null, items:[item]});
              });
              return groups.map((group,gIdx)=><div key={(group.recipeId||"solo")+gIdx}>
                {group.recipeId&&<div style={{fontSize:11,color:T.orange,fontWeight:700,marginBottom:5,marginTop:gIdx>0?6:0,
                  display:"flex",alignItems:"center",gap:6}}>
                  {(group.recipeName&&group.recipeName.trim().toLowerCase()===meal.name.trim().toLowerCase())
                    ? "🍳 Ingredientes de la receta"
                    : <>🍳 {group.recipeName||"Receta"}</>}
                  <span style={{color:T.muted,fontWeight:500,fontSize:10}}>· variación de hoy</span>
                </div>}
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {group.items.map(item=>{
                    return <div key={item.id} style={{background:T.surface,borderRadius:10,padding:"10px 12px",
                      border:group.recipeId?`1px solid ${T.orange}22`:`1px solid ${T.border}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:8}}>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600}}>{item.item}</div>
                          <div style={{fontSize:10,color:T.muted,marginTop:1}}>
                            {rnd(item.kcal)}kcal · P{rnd(item.prot)}g C{rnd(item.carb)}g G{rnd(item.gras)}g
                          </div>
                        </div>
                        <button onClick={()=>{
                            if(confirm(`¿Eliminar "${item.item}" de esta comida? Esta acción no se puede deshacer.`)) removeFoodFromMeal(meal.id,item.id);
                          }}
                          style={{background:"none",color:T.red,border:"none",cursor:"pointer",fontSize:17,flexShrink:0,lineHeight:1}}>🗑</button>
                      </div>
                      <QtyDualInput foodName={item.item} grams={item.cantidad_g}
                        customHint={allowedFoods.find(f=>f.nombre===item.item)?.unitHint}
                        onChange={g=>updateMealItemQty(meal.id,item.id,g)}/>
                    </div>;
                  })}
                  {group.recipeId&&<button onClick={()=>{setAddingToMeal(meal.id);setPickMealFood("");setPickQty(100);}}
                    style={{background:"none",color:T.orange,border:`1px dashed ${T.orange}44`,borderRadius:8,
                      padding:"6px 0",fontSize:11,fontWeight:600,cursor:"pointer",marginTop:2}}>
                    + Agregar otro ingrediente a esta variación
                  </button>}
                </div>
              </div>);
            })()}
          </div>}

          <div style={{padding:"10px 16px",display:"flex",gap:8,flexWrap:"wrap"}}>
            {addingToMeal===meal.id
              ? <div style={{display:"flex",flexDirection:"column",gap:8,width:"100%"}}>
                  <select value={pickMealFood} onChange={e=>{setPickMealFood(e.target.value);setPickQty(100);}}
                    style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
                      color:T.text,padding:"7px 10px",fontSize:12,height:34,width:"100%"}}>
                    <option value="">— Elegir alimento —</option>
                    {allowedFoods.map(f=><option key={f.id} value={f.nombre}>{f.nombre}</option>)}
                  </select>
                  {pickMealFood&&<QtyDualInput foodName={pickMealFood} grams={pickQty} onChange={setPickQty}
                    customHint={allowedFoods.find(f=>f.nombre===pickMealFood)?.unitHint}/>}
                  {recipes.length>0&&<select value="" onChange={e=>{
                      const r=recipes.find(x=>x.id===e.target.value);
                      if(r){addRecipeToMeal(meal.id,r);setAddingToMeal(null);}
                    }}
                    style={{background:T.surface,border:`1px solid ${T.purple}55`,borderRadius:8,
                      color:T.purple,padding:"7px 10px",fontSize:12,height:34}}>
                    <option value="">— o agregar receta guardada —</option>
                    {recipes.map(r=><option key={r.id} value={r.id}>{r.nombre} ({rnd(r.kcal)}kcal)</option>)}
                  </select>}
                  <div style={{display:"flex",gap:8}}>
                    <Btn sz="sm" v="success" disabled={!pickMealFood} onClick={()=>{
                      const food=allowedFoods.find(f=>f.nombre===pickMealFood);
                      if(food){addFoodToMeal(meal.id,food,pickQty);setPickMealFood("");setPickQty(100);setAddingToMeal(null);}
                    }}>+ Agregar</Btn>
                    <Btn sz="sm" v="ghost" onClick={()=>setAddingToMeal(null)}>Cancelar</Btn>
                  </div>
                </div>
              : <Btn sz="sm" v="ghost" onClick={()=>{setAddingToMeal(meal.id);setPickMealFood("");setPickQty(100);}}
                  disabled={allowedFoods.length===0}>
                  <Icon n="plus" s={12}/>Agregar alimento o receta
                </Btn>}
          </div>

          <div style={{padding:"12px 16px 16px",borderTop:`1px solid ${T.border}`}}>
            <Btn full v="success" onClick={()=>setSelectedMealId(null)}>
              <Icon n="check" s={15}/>Guardar y salir
            </Btn>
          </div>
          </div>
        </Modal>;
      })}

    </>}

    {/* ══ BOTÓN 3: SUPLEMENTOS DEL DÍA ══ */}
    {nutriTab==="suplementos"&&<>
      {/* Suplementos recomendados — dosis calculada según TU peso, no una tabla genérica */}
      <div style={{background:T.card,borderRadius:12,padding:"12px 16px",border:`1px solid ${T.border}`}}>
        <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.text,letterSpacing:".05em"}}>
          💊 SUPLEMENTOS RECOMENDADOS
        </span>
        <p style={{fontSize:11,color:T.muted,marginTop:4,marginBottom:10}}>
          Calculado para tu peso actual ({profile?.peso||"—"}kg) y tu objetivo "{goal.label}".
        </p>
        <div>
          {supls.map(([name,dose],i)=>{
            const personalized = parseDoseForWeight(dose, profile?.peso);
            return <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,
              padding:"9px 0",borderBottom:i<supls.length-1?`1px solid ${T.border}`:"none",fontSize:13}}>
              <div style={{minWidth:0}}>
                <span style={{fontWeight:600}}>{name}</span>
                {personalized&&<div style={{fontSize:10,color:T.muted}}>tabla: {dose}</div>}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                <Badge color={T.blue}>{personalized||dose}</Badge>
                <button onClick={()=>openSupLogForm(name)}
                  style={{background:T.accent,color:"#000",border:"none",borderRadius:8,
                    padding:"5px 9px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  + Tomé
                </button>
              </div>
            </div>;
          })}
        </div>
      </div>

      {/* Comparación con tu dieta — cuánta proteína ya te da la comida vs. cuánta necesitarías del suplemento */}
      {(()=>{
        const proteinaWheyDose = supls.find(([n])=>n.toLowerCase().includes("proteína whey")||n.toLowerCase().includes("proteina whey"));
        if(!proteinaWheyDose || !profile?.peso) return null;
        const fromFood = dayMealTotals.prot||0;
        const target = tgt.protG||0;
        const gap = Math.max(0, target - fromFood);
        return <div style={{background:`linear-gradient(135deg,${T.blue}15,${T.purple}10)`,
          borderRadius:14,padding:"14px 16px",border:`1px solid ${T.blue}33`}}>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:14,color:T.blue,letterSpacing:".04em",marginBottom:8}}>
            🥤 PROTEÍNA: DIETA VS. OBJETIVO
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:6}}>
            <span style={{color:T.muted}}>Ya conseguís con la comida planificada</span>
            <b style={{color:T.text}}>{rnd(fromFood)}g</b>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:6}}>
            <span style={{color:T.muted}}>Tu objetivo diario</span>
            <b style={{color:T.text}}>{target}g</b>
          </div>
          <div style={{height:6,background:T.surface,borderRadius:3,overflow:"hidden",marginBottom:10}}>
            <div style={{height:"100%",borderRadius:3,background:T.blue,width:`${Math.min(100,(fromFood/(target||1))*100)}%`}}/>
          </div>
          {gap>5
            ? <p style={{fontSize:12,color:T.text,margin:0,lineHeight:1.4}}>
                Te faltarían <b style={{color:T.orange}}>{rnd(gap)}g</b> de proteína para llegar a tu objetivo —
                un batido de proteína whey de esa cantidad te ayudaría a cubrirlo.
              </p>
            : <p style={{fontSize:12,color:T.green,margin:0,lineHeight:1.4}}>
                ✓ Con tu dieta planificada ya cubrís (o casi) tu objetivo de proteína — el suplemento sería un extra, no una necesidad.
              </p>}
        </div>;
      })()}

      {/* Registro de tomas de hoy */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.text,letterSpacing:".05em"}}>
          📋 TOMAS DE HOY
        </div>
        <Btn sz="sm" v="success" onClick={()=>openSupLogForm()}>
          <Icon n="plus" s={13}/>Registrar toma
        </Btn>
      </div>

      {todaySupLogs.length===0
        ? <p style={{fontSize:12,color:T.muted,textAlign:"center",padding:"14px 0"}}>
            Todavía no registraste ninguna toma hoy.
          </p>
        : <div style={{background:T.card,borderRadius:12,border:`1px solid ${T.border}`,overflow:"hidden"}}>
            {todaySupLogs.map((log,i)=>(
              <div key={log.id} onClick={()=>openEditSupLog(log)}
                style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer",
                  borderTop:i>0?`1px solid ${T.border}`:"none"}}>
                <span style={{fontSize:11,color:T.muted,minWidth:42,flexShrink:0}}>{log.time}</span>
                <span style={{fontSize:13,fontWeight:600,flex:1,minWidth:0}}>{log.name}</span>
                <span style={{fontSize:12,color:T.blue,fontWeight:700,flexShrink:0}}>{log.qty}{log.unit}</span>
                <button onClick={e=>{e.stopPropagation();deleteSupLog(log.id);}}
                  style={{background:"none",color:T.muted,border:"none",fontSize:16,cursor:"pointer",flexShrink:0}}>×</button>
              </div>
            ))}
          </div>}

      {/* Dietas fitness recomendadas */}
      <div style={{background:T.card,borderRadius:14,border:`1px solid ${T.purple}44`,padding:"14px 16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.purple,letterSpacing:".05em"}}>
            🔍 DIETAS FITNESS RECOMENDADAS
          </span>
          <Btn sz="sm" onClick={fetchDietSuggestions} disabled={loadingDiets}
            style={{background:T.purple,color:"#fff"}}>
            {loadingDiets?<><Spinner/>Buscando...</>:"Buscar"}
          </Btn>
        </div>
        <p style={{fontSize:11,color:T.muted,marginBottom:dietSuggestions.length?10:0}}>
          Enfoques conocidos acordes a tu objetivo "{goal.label}" y tu meta de {tgt.meta} kcal/día.
        </p>
        {dietSuggestions.map((d,i)=>(
          <div key={i} style={{background:T.surface,borderRadius:10,padding:"10px 12px",marginTop:8}}>
            <div style={{fontWeight:700,fontSize:13,color:T.text}}>{d.nombre}</div>
            <p style={{fontSize:12,color:T.muted,marginTop:4,lineHeight:1.4}}>{d.descripcion}</p>
            {d.macros_aprox&&<Badge color={T.purple}>{d.macros_aprox}</Badge>}
          </div>
        ))}
      </div>

      {/* ── MODAL: REGISTRAR/EDITAR TOMA DE SUPLEMENTO ── */}
      {showSupLog&&<Modal title={editingSupLog?"EDITAR TOMA":"+ REGISTRAR TOMA"} onClose={()=>setShowSupLog(false)} width={360}>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Field label="Suplemento" value={supLogName} onChange={setSupLogName} placeholder="Ej: Creatina"/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <Field label="Cantidad" type="number" value={supLogQty} onChange={setSupLogQty} placeholder="5" step={0.1}/>
            <div>
              <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
                Unidad
              </label>
              <div style={{display:"flex",background:T.surface,borderRadius:8,padding:3,gap:3}}>
                {["g","mg","UI","cáps"].map(u=>(
                  <button key={u} onClick={()=>setSupLogUnit(u)}
                    style={{flex:1,padding:"7px 0",borderRadius:6,fontWeight:700,fontSize:11,cursor:"pointer",
                      background:supLogUnit===u?T.accent:"transparent",color:supLogUnit===u?"#000":T.muted,border:"none"}}>
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Field label="Hora" type="time" value={supLogTime} onChange={setSupLogTime}/>
          <div style={{display:"flex",gap:8}}>
            <Btn full v="success" disabled={!supLogName.trim()} onClick={saveSupLog}>
              <Icon n="check" s={14}/>{editingSupLog?"Guardar cambios":"Registrar"}
            </Btn>
            {editingSupLog&&<Btn v="danger" onClick={()=>{deleteSupLog(editingSupLog);setShowSupLog(false);}}>
              <Icon n="trash" s={14}/>
            </Btn>}
          </div>
        </div>
      </Modal>}

    </>}

    {/* ══ BOTÓN 4: MIS PRODUCTOS ══ */}
    {nutriTab==="biblioteca"&&<>
    <div style={{display:"flex",flexDirection:"column",gap:12}}>

      {/* Botón escanear — siempre visible y prominente */}
      <button onClick={()=>fileRef.current?.click()} disabled={scanning}
        style={{width:"100%",background:T.accent,color:"#000",border:"none",borderRadius:12,
          padding:"16px",fontSize:15,fontWeight:700,cursor:scanning?"not-allowed":"pointer",
          display:"flex",alignItems:"center",justifyContent:"center",gap:10,
          opacity:scanning?.6:1}}>
        {scanning?<><Spinner/>Analizando etiqueta...</>
          :<>📷 Escanear tabla nutricional</>}
      </button>
      <input ref={fileRef} type="file" accept="image/*" capture="environment"
        style={{display:"none"}} onChange={handleScan}/>

      {scanErr&&<div style={{background:T.red+"22",border:`1px solid ${T.red}55`,borderRadius:10,
        padding:"12px 14px",fontSize:13,color:T.red}}>⚠ {scanErr}</div>}

      {/* Resultado del escaneo — confirmar antes de guardar */}
      {scanResult&&<ProductForm
        initial={scanResult}
        title="✅ ETIQUETA LEÍDA — CONFIRMÁ LOS DATOS"
        onSave={prod=>saveProduct({...prod,id:scanResult.id})}
        onCancel={()=>setScanResult(null)}/>}

      {/* Editar producto existente */}
      {editProd&&<ProductForm
        initial={editProd}
        title="EDITAR PRODUCTO"
        onSave={prod=>saveProduct(prod)}
        onCancel={()=>setEditProd(null)}/>}

      {/* Agregar manual */}
      {!scanResult&&!editProd&&<Btn v="ghost" onClick={()=>setEditProd({id:uid(),nombre:"",porcion:"100g",kcal:0,proteinas:0,carbos:0,grasas:0,sodio:0,fibra:0,createdAt:nowISO()})}
        style={{alignSelf:"flex-start"}}>
        <Icon n="plus" s={14}/>Agregar manualmente
      </Btn>}

      {/* Lista de productos */}
      {!scanResult&&!editProd&&<>
        {products.length===0
          ? <div style={{textAlign:"center",padding:"30px 20px",color:T.muted}}>
              <div style={{fontSize:40,marginBottom:10}}>📦</div>
              <p style={{fontSize:14}}>No hay productos en tu biblioteca.</p>
              <p style={{fontSize:12,marginTop:6}}>Escaneá una etiqueta nutricional para agregar el primero.</p>
            </div>
          : <>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:14,color:T.muted,letterSpacing:".06em"}}>
              {products.length} PRODUCTO{products.length!==1?"S":""} GUARDADO{products.length!==1?"S":""}
            </div>
            {products.map(prod=>(
              <div key={prod.id} className="fu" style={{background:T.card,borderRadius:12,
                border:`1px solid ${T.border}`,padding:"12px 14px"}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:8}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:14}}>{prod.nombre}</div>
                    <div style={{fontSize:11,color:T.muted,marginTop:2}}>Porción: {prod.porcion}</div>
                  </div>
                  <div style={{display:"flex",gap:5,flexShrink:0}}>
                    <button onClick={()=>setEditProd(prod)}
                      style={{background:T.surface,color:T.muted,border:`1px solid ${T.border}`,
                        borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>✏</button>
                    <button onClick={()=>deleteProduct(prod.id)}
                      style={{background:T.red+"22",color:T.red,border:`1px solid ${T.red}44`,
                        borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>🗑</button>
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                  <Badge color={T.orange}>{rnd(prod.kcal)} kcal</Badge>
                  <Badge color={T.blue}>P {rnd(prod.proteinas)}g</Badge>
                  <Badge color={T.accent}>C {rnd(prod.carbos)}g</Badge>
                  <Badge color={T.green}>G {rnd(prod.grasas)}g</Badge>
                  {prod.sodio>0&&<Badge color={T.muted}>Na {rnd(prod.sodio)}mg</Badge>}
                  {prod.fibra>0&&<Badge color={T.muted}>Fibra {rnd(prod.fibra)}g</Badge>}
                </div>
                {allowedFoods.some(f=>f.nombre.trim().toLowerCase()===prod.nombre.trim().toLowerCase())
                  ? <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.green,fontWeight:600}}>
                      ✓ Ya está en Mi Dieta
                    </div>
                  : <Btn sz="sm" v="success" onClick={()=>addToDay(prod)}>
                      + Usar en Mi Dieta
                    </Btn>}
              </div>
            ))}
          </>}
      </>}
    </div>

    </>}

    {/* ── MODAL: EDITOR DE RECETA ── */}
    {/* ── MODAL: COPIAR DÍA A OTROS DÍAS ── */}
    {showCopyDay&&<Modal title="📋 COPIAR DÍA" onClose={()=>setShowCopyDay(false)} width={380}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <p style={{fontSize:13,color:T.muted}}>
          Copiar las comidas de <b style={{color:T.text}}>{WLONG[selectedWeekday]}</b> a los días que elijas.
          Se reemplaza lo que ya tengan configurado esos días.
        </p>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {[1,2,3,4,5,6,0].filter(d=>d!==selectedWeekday).map(d=>{
              const sel = copyTargets.includes(d);
              return <button key={d} onClick={()=>setCopyTargets(p=>p.includes(d)?p.filter(x=>x!==d):[...p,d])}
                style={{background:sel?T.blue:T.surface,color:sel?"#fff":T.muted,
                  border:`1px solid ${sel?T.blue:T.border}`,borderRadius:10,padding:"8px 14px",
                  fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {WSHORT[d]}
              </button>;
            })}
          </div>
        </div>
        <button onClick={()=>{
            const todos = [1,2,3,4,5,6,0].filter(d=>d!==selectedWeekday);
            setCopyTargets(p=>p.length===todos.length?[]:todos);
          }}
          style={{background:"none",color:T.blue,border:"none",fontSize:12,fontWeight:600,cursor:"pointer",textAlign:"left",padding:0}}>
          {copyTargets.length===[1,2,3,4,5,6,0].filter(d=>d!==selectedWeekday).length?"✓ Todos seleccionados — tocá para deseleccionar":"☐ Seleccionar todos los días"}
        </button>
        <div style={{display:"flex",gap:8}}>
          <Btn full v="info" onClick={()=>copyDayTo(copyTargets)} disabled={copyTargets.length===0}>
            <Icon n="check" s={14}/>Copiar a {copyTargets.length} día{copyTargets.length!==1?"s":""}
          </Btn>
          <Btn v="ghost" onClick={()=>setShowCopyDay(false)}>Cancelar</Btn>
        </div>
      </div>
    </Modal>}

    {/* ── MODAL: PROGRAMAR COMIDAS — calendario para elegir el día a editar ── */}
    {showScheduleMeals&&(()=>{
      const baseMonday = getMondayOf(today);
      const shiftedMonday = (() => { const d=new Date(baseMonday+"T12:00:00"); d.setDate(d.getDate()+(weekOffset||0)*7); return d.toISOString().slice(0,10); })();
      const weekDatesForOffset = getWeekDates(shiftedMonday);
      const weekOrderIdx = [1,2,3,4,5,6,0];
      return <Modal title="📅 PROGRAMAR COMIDAS" onClose={()=>setShowScheduleMeals(false)} width={380}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <p style={{fontSize:13,color:T.muted}}>Elegí qué día querés editar.</p>

          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
            <button onClick={()=>setWeekOffset(v=>v-1)}
              style={{background:T.surface,color:T.muted,border:`1px solid ${T.border}`,borderRadius:8,
                padding:"4px 12px",fontSize:14,cursor:"pointer"}}>‹</button>
            <span style={{fontSize:12,color:T.muted,fontWeight:600,minWidth:100,textAlign:"center"}}>
              {(weekOffset||0)===0?"Esta semana":(weekOffset||0)>0?`+${weekOffset} semanas`:`${weekOffset} semanas`}
            </span>
            <button onClick={()=>setWeekOffset(v=>v+1)}
              style={{background:T.surface,color:T.muted,border:`1px solid ${T.border}`,borderRadius:8,
                padding:"4px 12px",fontSize:14,cursor:"pointer"}}>›</button>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
            {weekOrderIdx.map((d,i)=>{
              const active = d===selectedWeekday;
              const dateForDay = weekDatesForOffset[i];
              const realToday = dateForDay===today;
              const mealsCount = (loadWeekMeals()[d]||[]).filter(m=>(m.items||[]).length>0).length;
              return <button key={d} onClick={()=>{setSelectedWeekday(d);setShowScheduleMeals(false);}}
                style={{background:active?T.accent:T.card,color:active?"#000":T.text,
                  border:`1px solid ${active?T.accent:realToday?T.accent+"66":T.border}`,
                  borderRadius:12,padding:"12px 6px",fontSize:12,fontWeight:700,cursor:"pointer",textAlign:"center"}}>
                <div style={{fontSize:13}}>{WSHORT[d]}</div>
                <div style={{fontSize:10,opacity:.8,marginTop:2}}>{dateForDay.slice(8)}/{dateForDay.slice(5,7)}</div>
                {mealsCount>0&&<div style={{fontSize:9,marginTop:4,opacity:.9}}>🍽 {mealsCount}</div>}
                {realToday&&<div style={{fontSize:9,marginTop:2,color:active?"#000":T.accent,fontWeight:800}}>HOY</div>}
              </button>;
            })}
          </div>

          <div style={{paddingTop:8,borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:8}}>
            <p style={{fontSize:12,color:T.muted,margin:0}}>
              Editando ahora: <b style={{color:T.text}}>{WLONG[selectedWeekday]}</b>
            </p>
            <div style={{display:"flex",gap:8}}>
              <Btn full v="success" onClick={()=>setShowScheduleMeals(false)}>
                <Icon n="check" s={14}/>Ir a {WLONG[selectedWeekday]}
              </Btn>
              <Btn v="ghost" onClick={()=>{setShowScheduleMeals(false);setShowCopyDay(true);setCopyTargets([]);}}>
                📋 Copiar
              </Btn>
            </div>
          </div>
        </div>
      </Modal>;
    })()}

    {showRecipeEditor&&recipeForm&&<Modal
      title={editingRecipe?"EDITAR RECETA":"NUEVA RECETA"}
      onClose={()=>{setShowRecipeEditor(false);setRecipeForm(null);}}
      width={560}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Field label="Nombre de la receta" value={recipeForm.nombre}
          onChange={v=>setRecipeForm(f=>({...f,nombre:v}))} placeholder="Ej: Tortilla de claras con espinaca"/>

        {/* Imagen de la receta */}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>
            Imagen del plato
          </label>
          <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            {recipeForm.foto
              ? <img src={recipeForm.foto} alt="" style={{width:90,height:66,objectFit:"cover",
                  borderRadius:8,border:`1px solid ${T.border}`,flexShrink:0}}/>
              : <div style={{width:90,height:66,background:T.surface,borderRadius:8,
                  display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,
                  border:`1px dashed ${T.border}`,flexShrink:0,fontSize:22}}>🍽</div>}
            <div style={{display:"flex",flexDirection:"column",gap:6,flex:1}}>
              <Btn sz="sm" onClick={generateRecipeImage} disabled={genImgLoading||!recipeForm.nombre?.trim()}>
                {genImgLoading?<><Spinner/>Generando...</>:<>✨ Generar con IA</>}
              </Btn>
              <Btn sz="sm" v="ghost" onClick={()=>recipeImgRef.current?.click()} disabled={photoCompressing}>
                {photoCompressing?<><Spinner/>Procesando...</>:<><Icon n="camera" s={13}/>Subir foto</>}
              </Btn>
              {recipeForm.foto&&<button onClick={()=>setRecipeForm(f=>({...f,foto:""}))}
                style={{background:"none",color:T.red,border:"none",fontSize:11,cursor:"pointer",textAlign:"left"}}>
                × Quitar imagen
              </button>}
            </div>
            <input ref={recipeImgRef} type="file" accept="image/*" style={{display:"none"}}
              onChange={handleRecipePhotoUpload}/>
          </div>
        </div>

        {/* Totales en vivo */}
        {recipeForm.ingredientes.length>0&&(()=>{
          const t=recipeTotals(recipeForm.ingredientes);
          return <div style={{background:T.surface,borderRadius:10,padding:"10px 14px",
            display:"flex",gap:14,flexWrap:"wrap",justifyContent:"center"}}>
            {[["Kcal",rnd(t.kcal),T.orange],["Prot",t.prot.toFixed(1)+"g",T.blue],
              ["Carb",t.carb.toFixed(1)+"g",T.accent],["Gras",t.gras.toFixed(1)+"g",T.green]].map(([l,v,c])=>(
              <div key={l} style={{textAlign:"center"}}>
                <div style={{fontSize:10,color:T.muted}}>{l}</div>
                <div style={{fontFamily:"'Bebas Neue'",fontSize:18,color:c}}>{v}</div>
              </div>
            ))}
          </div>;
        })()}

        {/* Lista de ingredientes — editable */}
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {recipeForm.ingredientes.map(ing=>{
            const isEditing = editingIngId===ing.id;
            return <div key={ing.id} style={{background:T.surface,borderRadius:8,
              padding:"8px 10px",display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600}}>{ing.item}</div>
                  <div style={{fontSize:10,color:T.muted}}>
                    {approxUnits(ing.item,ing.cantidad_g)} · {rnd(ing.kcal)}kcal
                  </div>
                </div>
                <button onClick={()=>setEditingIngId(isEditing?null:ing.id)}
                  style={{background:T.card,color:T.muted,border:`1px solid ${T.border}`,
                    borderRadius:6,padding:"4px 8px",fontSize:11,cursor:"pointer",flexShrink:0}}>
                  {isEditing?"✓":"✏"}
                </button>
                <button onClick={()=>doReplaceIngredient(ing.id)}
                  disabled={replacingIng===ing.id}
                  style={{background:T.blue+"22",color:T.blue,border:`1px solid ${T.blue}44`,
                    borderRadius:6,padding:"4px 8px",fontSize:10,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                  {replacingIng===ing.id?"...":"🔄"}
                </button>
                <button onClick={()=>removeIngredientFromRecipe(ing.id)}
                  style={{background:"none",color:T.muted,border:"none",cursor:"pointer",fontSize:16,flexShrink:0}}>×</button>
              </div>
              {isEditing&&<div style={{paddingTop:4,borderTop:`1px solid ${T.border}`}}>
                <QtyDualInput foodName={ing.item} grams={ing.cantidad_g}
                  customHint={allowedFoods.find(f=>f.nombre===ing.item)?.unitHint}
                  onChange={g=>updateIngredientQty(ing.id,g)}/>
              </div>}
            </div>;
          })}
          {recipeForm.ingredientes.length===0&&
            <p style={{fontSize:12,color:T.muted,textAlign:"center",padding:10}}>
              Sin ingredientes. Agregá desde tu lista de alimentos permitidos.
            </p>}
        </div>

        {/* Agregar ingrediente */}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <select value={newIngFood} onChange={e=>{setNewIngFood(e.target.value);setNewIngQty(100);}}
            style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
              color:T.text,padding:"9px 10px",fontSize:13,height:38,width:"100%"}}>
            <option value="">— Elegir alimento —</option>
            {allowedFoods.map(f=><option key={f.id} value={f.nombre}>{f.nombre}</option>)}
          </select>
          {newIngFood&&<div style={{display:"flex",gap:8,alignItems:"center"}}>
            <QtyDualInput foodName={newIngFood} grams={newIngQty} onChange={setNewIngQty}
              customHint={allowedFoods.find(f=>f.nombre===newIngFood)?.unitHint}/>
            <Btn sz="sm" onClick={()=>{
              const food=allowedFoods.find(f=>f.nombre===newIngFood);
              if(food){addIngredientToRecipe(food,newIngQty||100);setNewIngFood("");setNewIngQty(100);}
            }}>+ Agregar</Btn>
          </div>}
        </div>

        <Field label="Preparación" value={recipeForm.preparacion} textarea
          onChange={v=>setRecipeForm(f=>({...f,preparacion:v}))}
          placeholder="Pasos breves de preparación..."/>

        <div style={{display:"flex",gap:8}}>
          <Btn full v="success" onClick={saveRecipe} disabled={!recipeForm.nombre.trim()||recipeForm.ingredientes.length===0}>
            <Icon n="check" s={15}/>Guardar receta
          </Btn>
          <Btn v="ghost" onClick={()=>{setShowRecipeEditor(false);setRecipeForm(null);}}>Cancelar</Btn>
        </div>
      </div>
    </Modal>}


  </div>;
}


function StatsView({logs,exercises,cardioLogs,profile,setLogs,setCardioLogs,statTab,setStatTab}) {
  const [selEx,setSelEx]=useState("");
  const [editingLog,setEditingLog]=useState(null);
  const [editForm,setEditForm]=useState(null);
  const [editingCardio,setEditingCardio]=useState(null);
  const [cardioEditForm,setCardioEditForm]=useState(null);

  const sorted=[...logs].sort((a,b)=>b.date.localeCompare(a.date));
  const historyList = selEx ? sorted.filter(l=>l.exerciseName===selEx) : sorted;

  const totalTon=logs.reduce((s,l)=>s+l.sets*l.reps*l.weight,0);
  const groups={};logs.forEach(l=>{groups[l.exerciseName]=(groups[l.exerciseName]||0)+1;});
  const top=Object.entries(groups).sort((a,b)=>b[1]-a[1]).slice(0,5);
  const cardioKcal=cardioLogs.reduce((s,l)=>s+l.kcal,0);
  const exNames=[...new Set(logs.map(l=>l.exerciseName))].sort();
  const progData=logs.filter(l=>l.exerciseName===selEx).sort((a,b)=>a.date.localeCompare(b.date));

  const weekOrder=[1,2,3,4,5,6,0]; // Lun→Dom

  // ── Días de entrenamiento (gym) — cuántos días distintos entrenaste cada día de la semana ──
  const trainDaysCount = weekOrder.map(wd=>{
    const dates=new Set(logs.filter(l=>new Date(l.date+"T12:00:00").getDay()===wd).map(l=>l.date));
    return dates.size;
  });
  // ── Horarios de entrenamiento — distribución mañana/tarde/noche ──
  const timeBuckets={mañana:0,tarde:0,noche:0};
  logs.forEach(l=>{
    const h=parseInt((l.time||"00:00").split(":")[0],10)||0;
    if(h<12) timeBuckets.mañana++; else if(h<18) timeBuckets.tarde++; else timeBuckets.noche++;
  });
  const totalTimeLogs=logs.length||1;

  // ── Tonelaje por fecha (gráfico de Progreso de pesos, si no hay ejercicio elegido) ──
  const tonByDate={}; logs.forEach(l=>{tonByDate[l.date]=(tonByDate[l.date]||0)+l.sets*l.reps*l.weight;});
  const tonEntries=Object.entries(tonByDate).sort((a,b)=>a[0].localeCompare(b[0])).slice(-10);

  // ── Cardio: días de la semana con más sesiones, km y tiempos totales ──
  const cardioDaysCount = weekOrder.map(wd=>{
    const dates=new Set(cardioLogs.filter(l=>new Date(l.date+"T12:00:00").getDay()===wd).map(l=>l.date));
    return dates.size;
  });
  const totalKm = cardioLogs.reduce((s,l)=>s+(+l.distance||0),0);
  const totalMin = cardioLogs.reduce((s,l)=>s+(+l.duration||0),0);
  const kcalByDate={}; cardioLogs.forEach(l=>{kcalByDate[l.date]=(kcalByDate[l.date]||0)+l.kcal;});
  const kcalEntries=Object.entries(kcalByDate).sort((a,b)=>a[0].localeCompare(b[0])).slice(-10);
  const sortedCardio=[...cardioLogs].sort((a,b)=>b.date.localeCompare(a.date));
  const CARDIO_TIPOS={running:{icon:"🏃",label:"Running"},bici:{icon:"🚴",label:"Bici"},
    caminata:{icon:"🚶",label:"Caminata"},natacion:{icon:"🏊",label:"Natación"},eliptico:{icon:"⚙",label:"Elíptico"}};

  // ── Nutrición: comparar plan semanal vs objetivo diario (3 macros) ──
  const tgt = calcTargets(profile);
  const goal = GOALS[profile?.objetivo]||GOALS.hipertrofia;
  const weekMealsData = loadWeekMeals();
  const dayTotals = weekOrder.map(wd=>{
    const meals = weekMealsData[wd]||[];
    return meals.reduce((acc,m)=>{
      (m.items||[]).forEach(i=>{
        acc.kcal += (+i.kcal||0); acc.prot += (+i.prot||0);
        acc.carb += (+i.carb||0); acc.gras += (+i.gras||0);
      });
      return acc;
    },{kcal:0,prot:0,carb:0,gras:0});
  });
  const avgKcal = dayTotals.reduce((s,d)=>s+d.kcal,0)/7;
  const avgProt = dayTotals.reduce((s,d)=>s+d.prot,0)/7;
  const avgCarb = dayTotals.reduce((s,d)=>s+d.carb,0)/7;
  const avgGras = dayTotals.reduce((s,d)=>s+d.gras,0)/7;
  const pctOf = (val,target) => target>0 ? Math.round((val/target)*100) : 0;

  const exportCSV=()=>{
    const h=["Fecha","Ejercicio","Series","Reps","Peso kg","Notas"];
    const rows=sorted.map(l=>[fmtDateFull(l.date),l.exerciseName,l.sets,l.reps,l.weight,l.notes||""]);
    const csv=[h,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const blob=new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");a.href=url;a.download=`gymdraffin_${nowISO()}.csv`;a.click();URL.revokeObjectURL(url);
  };

  const openLog = log => { setEditingLog(log); setEditForm({...log}); };
  const saveLogEdit = () => {
    const orig = editingLog;
    const changed = orig.sets!==editForm.sets || orig.reps!==editForm.reps ||
      orig.weight!==editForm.weight || orig.notes!==editForm.notes;
    setLogs(prev => prev.map(l => l.id===orig.id ? {...editForm, modified: changed?true:!!l.modified} : l));
    setEditingLog(null); setEditForm(null);
  };
  const deleteLog = () => {
    if(!confirm("¿Eliminar este registro del historial?")) return;
    setLogs(prev => prev.filter(l=>l.id!==editingLog.id));
    setEditingLog(null); setEditForm(null);
  };

  const openCardioLog = log => { setEditingCardio(log); setCardioEditForm({...log}); };
  const saveCardioEdit = () => {
    if(cardioEditForm.date>nowISO()){alert("No podés poner una fecha futura para una sesión de cardio.");return;}
    const orig = editingCardio;
    const changed = orig.type!==cardioEditForm.type || orig.duration!==cardioEditForm.duration ||
      orig.distance!==cardioEditForm.distance || orig.kcal!==cardioEditForm.kcal ||
      orig.notes!==cardioEditForm.notes || orig.date!==cardioEditForm.date;
    setCardioLogs(prev => prev.map(l => l.id===orig.id ? {...cardioEditForm, modified: changed?true:!!l.modified} : l));
    setEditingCardio(null); setCardioEditForm(null);
  };
  const deleteCardioLog = () => {
    if(!confirm("¿Eliminar esta sesión de cardio?")) return;
    setCardioLogs(prev => prev.filter(l=>l.id!==editingCardio.id));
    setEditingCardio(null); setCardioEditForm(null);
  };

  // Mini gráfico de barras por día de la semana (Pareto Lun-Dom)
  const WeekdayBarChart = ({values, color}) => {
    const maxVal = Math.max(...values, 1);
    const barW=28, gap=(300-barW*7)/6;
    return <svg width="100%" viewBox="0 0 320 90" style={{overflow:"visible"}}>
      {values.map((v,i)=>{
        const h=Math.max(2,(v/maxVal)*60);
        const x=10+i*(barW+gap), y=76-h;
        return <g key={i}>
          <rect x={x} y={y} width={barW} height={h} rx="4" fill={color} opacity={v===0?0.25:0.85}/>
          <text x={x+barW/2} y="88" textAnchor="middle" fontSize="9" fill={T.muted}>{WSHORT[weekOrder[i]]}</text>
          {v>0&&<text x={x+barW/2} y={y-4} textAnchor="middle" fontSize="9" fill={color}>{v}</text>}
        </g>;
      })}
    </svg>;
  };

  // Mini gráfico de barras por fecha (evolución cronológica)
  const DateBarChart = ({entries, color}) => {
    if(entries.length===0) return <p style={{fontSize:12,color:T.muted,textAlign:"center",padding:16}}>Sin datos todavía.</p>;
    const maxVal = Math.max(...entries.map(([,v])=>v), 1);
    const barW = Math.min(32, 280/entries.length - 6);
    const gap = entries.length>1 ? (280-barW*entries.length)/(entries.length-1) : 0;
    return <svg width="100%" viewBox="0 0 300 100" style={{overflow:"visible"}}>
      {entries.map(([date,val],i)=>{
        const h=Math.max(2,(val/maxVal)*70);
        const x=10+i*(barW+gap), y=82-h;
        return <g key={date}>
          <rect x={x} y={y} width={barW} height={h} rx="4" fill={color} opacity="0.85"/>
          <text x={x+barW/2} y="95" textAnchor="middle" fontSize="8" fill={T.muted}>{fmtDate(date)}</text>
          <text x={x+barW/2} y={y-4} textAnchor="middle" fontSize="8" fill={color}>{rnd(val)}</text>
        </g>;
      })}
    </svg>;
  };

  const STAT_CARDS = [
    ["gym","Progreso ejercicios gym",logs.length+" ses.","⚡"],
    ["pesos","Progreso de pesos",rnd(totalTon).toLocaleString()+" kg","🏋"],
    ["cardio","Progreso Cardio",cardioLogs.length+" ses.","🏃"],
    ["nutricion","Progreso nutrición",rnd(avgKcal)+" kcal/d","🥗"],
  ];

  return <div className="fu" style={{display:"flex",flexDirection:"column",gap:16}}>

    {logs.length>0&&<div style={{display:"flex",justifyContent:"flex-end"}}>
      <Btn sz="sm" v="info" onClick={exportCSV}><Icon n="download" s={14}/>Exportar CSV</Btn>
    </div>}

    {/* ── Tarjetas-botón: eligen qué estadística ver abajo ── */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
      {STAT_CARDS.map(([key,l,v,i])=>{
        const active = statTab===key;
        return <button key={key} onClick={()=>setStatTab(key)}
          style={{background:active?T.accent:T.card,borderRadius:12,padding:"12px 10px",textAlign:"center",
            border:`1px solid ${active?T.accent:T.border}`,cursor:"pointer",transition:"all .15s"}}>
          <div style={{fontSize:20}}>{i}</div>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:18,color:active?"#000":T.accent}}>{v}</div>
          <div style={{fontSize:10,color:active?"#000":T.muted,marginTop:2,fontWeight:active?700:400,lineHeight:1.2}}>{l}</div>
        </button>;
      })}
    </div>

    {/* ══ TAB 1: PROGRESO EJERCICIOS GYM — más entrenados, días y horarios ══ */}
    {statTab==="gym"&&<>
      {top.length>0&&<div style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.border}`}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".05em",marginBottom:12}}>MÁS ENTRENADOS</div>
        {top.map(([name,count])=>(
          <div key={name} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <div style={{flex:1,fontSize:13}}>{name}</div>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.accent,minWidth:24}}>{count}</div>
            <div style={{width:70,height:5,background:T.surface,borderRadius:3,overflow:"hidden"}}>
              <div style={{height:"100%",background:T.accent,borderRadius:3,width:`${rnd((count/logs.length)*100)}%`}}/>
            </div>
          </div>
        ))}
      </div>}

      <div style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.border}`}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".05em",marginBottom:4}}>📅 DÍAS DE ENTRENAMIENTO</div>
        <p style={{fontSize:11,color:T.muted,marginBottom:10}}>Cantidad de días distintos que entrenaste, por día de la semana.</p>
        <WeekdayBarChart values={trainDaysCount} color={T.accent}/>
      </div>

      <div style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.border}`}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".05em",marginBottom:12}}>🕐 HORARIOS DE ENTRENAMIENTO</div>
        {[["Mañana (antes de 12h)",timeBuckets.mañana,T.orange],["Tarde (12-18h)",timeBuckets.tarde,T.blue],["Noche (después de 18h)",timeBuckets.noche,T.purple]].map(([l,v,col])=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <div style={{flex:1,fontSize:12,color:T.muted}}>{l}</div>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:col,minWidth:20,textAlign:"right"}}>{v}</div>
            <div style={{width:80,height:5,background:T.surface,borderRadius:3,overflow:"hidden"}}>
              <div style={{height:"100%",background:col,borderRadius:3,width:`${rnd((v/totalTimeLogs)*100)}%`}}/>
            </div>
          </div>
        ))}
      </div>
    </>}

    {/* ══ TAB 2: PROGRESO DE PESOS — selector, gráfico de peso, historial filtrado ══ */}
    {statTab==="pesos"&&<>
      <div style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.border}`}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".05em",marginBottom:10}}>📈 PROGRESO DE PESO POR EJERCICIO</div>
        <Sel label="" value={selEx} onChange={setSelEx}
          options={[{value:"",label:"— Elegir ejercicio —"},...exNames.map(n=>({value:n,label:n}))]}/>
        {selEx&&progData.length>1&&<>
          <svg width="100%" viewBox="0 0 320 80" style={{marginTop:14,overflow:"visible"}}>
            <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.green} stopOpacity=".3"/><stop offset="100%" stopColor={T.green} stopOpacity="0"/></linearGradient></defs>
            {(()=>{
              const vals=progData.map(d=>d.weight),mn=Math.min(...vals),mx=Math.max(...vals),rng=mx-mn||1;
              const pts=progData.map((d,i)=>({x:(i/(progData.length-1))*300+10,y:70-((d.weight-mn)/rng)*60,w:d.weight}));
              const poly=pts.map(p=>`${p.x},${p.y}`).join(" ");
              const area=`${pts[0].x},75 `+pts.map(p=>`${p.x},${p.y}`).join(" ")+` ${pts[pts.length-1].x},75`;
              return <><polygon points={area} fill="url(#g)"/><polyline points={poly} fill="none" stroke={T.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                {pts.map((p,i)=><g key={i}><circle cx={p.x} cy={p.y} r={4} fill={T.green}/><text x={p.x} y={p.y-8} textAnchor="middle" fontSize={9} fill={T.muted}>{p.w}</text></g>)}</>;
            })()}
          </svg>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <Badge color={T.green}>Mín: {Math.min(...progData.map(d=>d.weight))} kg</Badge>
            <Badge color={T.accent}>Máx: {Math.max(...progData.map(d=>d.weight))} kg</Badge>
          </div>
        </>}
        {selEx&&progData.length<2&&<p style={{fontSize:12,color:T.muted,marginTop:10}}>Necesitás al menos 2 sesiones de este ejercicio para ver el gráfico.</p>}
        {!selEx&&<>
          <p style={{fontSize:12,color:T.muted,margin:"10px 0"}}>Sin ejercicio elegido — mostrando tonelaje total por sesión:</p>
          <DateBarChart entries={tonEntries} color={T.accent}/>
        </>}
      </div>

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".08em"}}>
          HISTORIAL {selEx&&<span style={{color:T.accent}}>· {selEx}</span>}
        </div>
        {selEx&&<button onClick={()=>setSelEx("")} style={{background:"none",color:T.muted,border:"none",fontSize:11,cursor:"pointer"}}>Ver todos ×</button>}
      </div>
      {historyList.length===0
        ? <p style={{color:T.muted,fontSize:13,textAlign:"center",padding:20}}>
            {selEx?"Sin sesiones registradas de este ejercicio.":"Sin sesiones aún."}
          </p>
        : <div style={{background:T.card,borderRadius:12,border:`1px solid ${T.border}`,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 0.7fr",gap:4,padding:"8px 10px",
              background:T.surface,fontSize:9,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:".04em"}}>
              <span>Ejercicio</span><span>Fecha</span><span>Series</span><span></span>
            </div>
            {historyList.map(log=>(
              <div key={log.id} onClick={()=>openLog(log)}
                style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 0.7fr",gap:4,padding:"9px 10px",
                  borderTop:`1px solid ${T.border}`,fontSize:12,alignItems:"center",cursor:"pointer"}}>
                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:600}}>
                  {log.exerciseName}
                </span>
                <span style={{color:T.muted,fontSize:11}}>{fmtDate(log.date)}</span>
                <span style={{color:T.muted,fontSize:11}}>{log.sets}×{log.reps}·{log.weight}kg</span>
                <span style={{textAlign:"right"}}>{log.modified&&<span style={{color:T.blue,fontSize:14}}>✏</span>}</span>
              </div>
            ))}
          </div>}
    </>}

    {/* ══ TAB 3: PROGRESO CARDIO — evolución, días, km, tiempos ══ */}
    {statTab==="cardio"&&<>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
        {[["Sesiones",cardioLogs.length,"⚡"],["Km totales",totalKm.toFixed(1),"📍"],["Minutos",rnd(totalMin),"⏱"]].map(([l,v,i])=>(
          <div key={l} style={{background:T.card,borderRadius:12,padding:"10px 8px",textAlign:"center",border:`1px solid ${T.border}`}}>
            <div style={{fontSize:16}}>{i}</div>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.orange}}>{v}</div>
            <div style={{fontSize:9,color:T.muted}}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.border}`}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".05em",marginBottom:4}}>🔥 EVOLUCIÓN DE KCAL QUEMADAS</div>
        <p style={{fontSize:11,color:T.muted,marginBottom:12}}>Últimas {kcalEntries.length} fechas con cardio registrado.</p>
        <DateBarChart entries={kcalEntries} color={T.red}/>
        <div style={{marginTop:10,textAlign:"center"}}><Badge color={T.red}>Total: {cardioKcal.toLocaleString()} kcal</Badge></div>
      </div>

      <div style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.border}`}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".05em",marginBottom:4}}>📅 DÍAS CON MÁS CARDIO</div>
        <p style={{fontSize:11,color:T.muted,marginBottom:10}}>Cantidad de días distintos con cardio, por día de la semana.</p>
        <WeekdayBarChart values={cardioDaysCount} color={T.orange}/>
      </div>

      <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".08em"}}>SESIONES</div>
      {sortedCardio.length===0
        ? <p style={{color:T.muted,fontSize:13,textAlign:"center",padding:20}}>Sin sesiones de cardio aún.</p>
        : sortedCardio.map(l=>{
            const t=CARDIO_TIPOS[l.type]||{icon:"🏃",label:l.type};
            return <div key={l.id} onClick={()=>openCardioLog(l)} style={{background:T.card,borderRadius:12,padding:"12px 14px",
              border:`1px solid ${T.border}`,display:"flex",gap:12,alignItems:"center",cursor:"pointer"}}>
              <div style={{background:T.orange+"22",borderRadius:8,padding:"8px 10px",fontSize:20}}>{t.icon}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:13,display:"flex",alignItems:"center",gap:6}}>
                  {t.label}{l.modified&&<span style={{color:T.blue,fontSize:12}}>✏</span>}
                </div>
                <div style={{fontSize:11,color:T.muted}}>{weekday(l.date)} {fmtDateFull(l.date)}</div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                <Badge color={T.orange}>{l.duration} min</Badge>
                {l.distance>0&&<Badge color={T.blue}>{l.distance} km</Badge>}
                <Badge color={T.red}>{l.kcal} kcal</Badge>
              </div>
            </div>;
          })}
    </>}

    {/* ══ TAB 4: PROGRESO NUTRICIÓN — objetivos y desviaciones de las 3 macros ══ */}
    {statTab==="nutricion"&&<div style={{background:T.card,borderRadius:12,padding:16,border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
        <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.muted,letterSpacing:".05em"}}>🥗 OBJETIVOS Y DESVIACIONES</div>
        <Badge color={goal.color}>{goal.label}</Badge>
      </div>
      <p style={{fontSize:11,color:T.muted,marginBottom:14}}>
        Proteínas, carbohidratos y grasas planificadas cada día de la semana, comparadas con tu objetivo diario.
      </p>
      {[
        ["Proteínas",dayTotals.map(d=>d.prot),tgt.protG,T.blue,avgProt],
        ["Carbohidratos",dayTotals.map(d=>d.carb),tgt.carbG,T.orange,avgCarb],
        ["Grasas",dayTotals.map(d=>d.gras),tgt.grasG,T.green,avgGras],
      ].map(([label,vals,target,color,avg])=>{
        const pct = pctOf(avg,target);
        const maxVal = Math.max(target, ...vals, 1);
        const barW = 28, gap = (300-barW*7)/6;
        const targetY = 70 - (target/maxVal)*58;
        return <div key={label} style={{marginBottom:18}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
            <span style={{fontSize:12,color:color,fontWeight:700}}>{label}</span>
            <span style={{fontSize:11,color:T.muted}}>
              prom. {rnd(avg)}g / meta {target}g ·{" "}
              <b style={{color:pct>110?T.red:pct<80?T.orange:T.green}}>{pct}%</b>
            </span>
          </div>
          <svg width="100%" viewBox="0 0 320 88" style={{overflow:"visible"}}>
            <line x1="10" y1={targetY} x2="310" y2={targetY} stroke={color} strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7"/>
            {vals.map((v,i)=>{
              const h = Math.max(2,(v/maxVal)*58);
              const x = 10 + i*(barW+gap);
              const y = 70-h;
              return <g key={i}>
                <rect x={x} y={y} width={barW} height={h} rx="4" fill={color} opacity={v===0?0.25:0.85}/>
                <text x={x+barW/2} y="82" textAnchor="middle" fontSize="9" fill={T.muted}>{WSHORT[weekOrder[i]]}</text>
              </g>;
            })}
          </svg>
        </div>;
      })}
      <div style={{background:T.surface,borderRadius:10,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:12,color:T.muted}}>Promedio calórico diario planificado</span>
        <span style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.accent}}>{rnd(avgKcal)} / {tgt.meta} kcal</span>
      </div>
    </div>}

    {/* ── MODAL: DETALLE / EDITAR REGISTRO ── */}
    {editingLog&&editForm&&<Modal title="DETALLE DEL REGISTRO" onClose={()=>{setEditingLog(null);setEditForm(null);}} width={380}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:20,color:T.accent,letterSpacing:".04em"}}>{editForm.exerciseName}</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>
            {weekday(editForm.date)} {fmtDateFull(editForm.date)} · {editForm.time}
            {editForm.routineName&&` · ${editForm.routineName}`}
          </div>
          {editingLog.modified&&<Badge color={T.blue}>✏ editado</Badge>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          <Field label="Series" type="number" value={editForm.sets} onChange={v=>setEditForm(f=>({...f,sets:toNum(v)}))} min={0}/>
          <Field label="Reps" type="number" value={editForm.reps} onChange={v=>setEditForm(f=>({...f,reps:toNum(v)}))} min={0}/>
          <Field label="Peso kg" type="number" value={editForm.weight} onChange={v=>setEditForm(f=>({...f,weight:toNum(v)}))} min={0} step={0.5}/>
        </div>
        <Field label="Notas" value={editForm.notes||""} onChange={v=>setEditForm(f=>({...f,notes:v}))} textarea placeholder="Observaciones..."/>
        <div style={{display:"flex",gap:8}}>
          <Btn full v="success" onClick={saveLogEdit}><Icon n="check" s={14}/>Guardar cambios</Btn>
          <Btn v="danger" onClick={deleteLog}><Icon n="trash" s={14}/></Btn>
        </div>
      </div>
    </Modal>}

    {/* ── MODAL: DETALLE / EDITAR SESIÓN DE CARDIO ── */}
    {editingCardio&&cardioEditForm&&<Modal title="EDITAR SESIÓN DE CARDIO" onClose={()=>{setEditingCardio(null);setCardioEditForm(null);}} width={380}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:20,color:T.orange,letterSpacing:".04em"}}>
            {(CARDIO_TIPOS[cardioEditForm.type]||{label:cardioEditForm.type}).label}
          </div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>{weekday(cardioEditForm.date)} {fmtDateFull(cardioEditForm.date)}</div>
          {editingCardio.modified&&<Badge color={T.blue}>✏ editado</Badge>}
        </div>
        <Sel label="Tipo" value={cardioEditForm.type} onChange={v=>setCardioEditForm(f=>({...f,type:v}))}
          options={Object.entries(CARDIO_TIPOS).map(([k,v])=>({value:k,label:v.label}))}/>
        <Field label="Fecha" type="date" value={cardioEditForm.date} onChange={v=>setCardioEditForm(f=>({...f,date:v}))} max={nowISO()}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Field label="Duración (min)" type="number" value={cardioEditForm.duration} onChange={v=>setCardioEditForm(f=>({...f,duration:toNum(v)}))} min={0}/>
          <Field label="Distancia km" type="number" value={cardioEditForm.distance||0} onChange={v=>setCardioEditForm(f=>({...f,distance:toNum(v)}))} min={0} step={0.1}/>
        </div>
        <Field label="Calorías" type="number" value={cardioEditForm.kcal} onChange={v=>setCardioEditForm(f=>({...f,kcal:toNum(v)}))} min={0}/>
        <Field label="Notas" value={cardioEditForm.notes||""} onChange={v=>setCardioEditForm(f=>({...f,notes:v}))} textarea placeholder="Sensaciones..."/>
        <div style={{display:"flex",gap:8}}>
          <Btn full v="success" onClick={saveCardioEdit}><Icon n="check" s={14}/>Guardar cambios</Btn>
          <Btn v="danger" onClick={deleteCardioLog}><Icon n="trash" s={14}/></Btn>
        </div>
      </div>
    </Modal>}
  </div>;
}

function ShareView({profile,routines,exercises}) {
  const [selRoutine,setSelRoutine]=useState("");
  const exportRoutine=()=>{
    if(!selRoutine)return;
    const rt=routines.find(r=>r.id===selRoutine);
    const data={routine:rt,exercises:rt?.exercises||[],exportedBy:profile?.name,exportedAt:nowISO()};
    const blob=new Blob([JSON.stringify(data,null,2)],{type:"application/json"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");a.href=url;a.download=`rutina_${rt?.name||"export"}.json`;a.click();URL.revokeObjectURL(url);
  };
  return <div className="fu" style={{display:"flex",flexDirection:"column",gap:16}}>

    <div style={{background:T.card,borderRadius:14,padding:16,border:`1px solid ${T.border}`}}>
      <h3 style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.text,letterSpacing:".05em",marginBottom:12}}>📤 EXPORTAR RUTINA (JSON)</h3>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <Sel label="Rutina a exportar" value={selRoutine} onChange={setSelRoutine}
          options={[{value:"",label:"— Elegir rutina —"},...routines.map(r=>({value:r.id,label:r.name}))]}/>
        <Btn v="info" onClick={exportRoutine} disabled={!selRoutine}><Icon n="download" s={14}/>Exportar JSON</Btn>
        <p style={{fontSize:11,color:T.muted}}>Compartí el archivo por WhatsApp o mail. El otro usuario lo importa desde el perfil.</p>
      </div>
    </div>
  </div>;
}

// ─── Main App ─────────────────────────────────────────────────────────────────
const DEFAULT_PROFILE = {name:"Alan Draffin",peso:86,altura:189,edad:36,sexo:"m",objetivo:"hipertrofia",actividad:1.55};

// ─── Home / Dashboard View ────────────────────────────────────────────────────
const MOTIVATIONAL_QUOTES = [
  "El dolor que sientes hoy será la fuerza que sientas mañana.",
  "No cuentes los días, haz que los días cuenten.",
  "Tu único límite eres tú mismo.",
  "El éxito empieza con la voluntad de intentarlo.",
  "No se trata de tener tiempo, se trata de hacer tiempo.",
  "Cada repetición te acerca a la versión que querés ser.",
  "La disciplina es elegir entre lo que querés ahora y lo que querés más.",
  "El cuerpo logra lo que la mente cree.",
  "No pares cuando te canses, parás cuando termines.",
  "Pequeños progresos diarios suman grandes resultados.",
  "La constancia vence al talento cuando el talento no es constante.",
  "Hazlo por la versión de vos que te lo va a agradecer.",
  "El único entrenamiento malo es el que no hiciste.",
  "Tu cuerpo escucha todo lo que tu mente dice. Mantenete positivo.",
  "Resultados requieren compromiso, no motivación temporal.",
];
const getDailyQuote = () => {
  const day = new Date().getDate() + new Date().getMonth()*31;
  return MOTIVATIONAL_QUOTES[day % MOTIVATIONAL_QUOTES.length];
};

// ─── Today View — Guía diaria completa ───────────────────────────────────────
function TodayView({profile,routines,schedule,logs,cardioLogs,cardioSchedule,restDays,exercises,onExecuteSlot,goToTab,goToMealsTab,notifsEnabled}) {
  const [view, setView] = useState("info"); // "info" | "hoy"
  const today  = nowISO();
  const wday   = new Date(today+"T12:00:00").getDay();
  const hora   = new Date().getHours();
  const greet  = hora<12?"Buenos días":hora<20?"Buenas tardes":"Buenas noches";
  const firstName = (profile?.name||"Atleta").split(" ")[0];
  const goal  = GOALS[profile?.objetivo]||GOALS.hipertrofia;

  const todaySlots  = (schedule||[]).filter(s=>(s.repeat&&s.weekday===wday)||(s.date===today))
    .sort((a,b)=>a.time.localeCompare(b.time));
  const todayCardio = (cardioLogs||[]).filter(l=>l.date===today);
  const todayCardioScheduled = (cardioSchedule||[]).find(s=>s.date===today);
  const todayRest   = (restDays||[]).find(r=>r.date===today);
  const todayLogs   = (logs||[]).filter(l=>l.date===today);

  // Las kcal objetivo de hoy varían según lo que tengas programado (entreno, cardio, descanso)
  const tgt = calcTargets(profile, getDayActivityFactor({
    hasRoutine: todaySlots.length>0, hasCardio: !!todayCardioScheduled, isRest: !!todayRest,
  }));

  const getProgress = (rt) => {
    const total = (rt.exercises||[]).reduce((s,i)=>{
      const b=Array.isArray(i.blocks)&&i.blocks.length?i.blocks:[{type:"normal",repeats:1}];
      return s+b.reduce((a,bl)=>a+(bl.type==="drop"?bl.repeats||1:1),0);
    },0);
    const done = todayLogs.filter(l=>l.routineName===rt.name).length;
    return {total, done, pct: total>0?Math.min(100,Math.round(done/total*100)):0};
  };
  // Progreso global de entrenamiento hoy (para el anillo resumen)
  const trainingProgress = todaySlots.length>0
    ? (()=>{ const rt=routines.find(r=>r.id===todaySlots[0].routineId); return rt?getProgress(rt):{pct:0}; })()
    : {pct: todayRest ? 100 : 0};

  const weekMealsStored = loadWeekMeals();
  const realMeals = (weekMealsStored[wday]||[]).slice().sort((a,b)=>(a.time||"").localeCompare(b.time||""));
  const mealTotalsOf = m => (m.items||[]).reduce((a,i)=>({
    kcal:a.kcal+(+i.kcal||0), prot:a.prot+(+i.prot||0), carb:a.carb+(+i.carb||0), gras:a.gras+(+i.gras||0)
  }),{kcal:0,prot:0,carb:0,gras:0});
  const mealsWithFoodToday = realMeals.filter(m=>(m.items||[]).length>0);
  const dayTotals = realMeals.reduce((acc,m)=>{
    const t=mealTotalsOf(m);
    return {kcal:acc.kcal+t.kcal, prot:acc.prot+t.prot, carb:acc.carb+t.carb, gras:acc.gras+t.gras};
  },{kcal:0,prot:0,carb:0,gras:0});
  const totalCardioKcal = todayCardio.reduce((s,l)=>s+l.kcal,0);
  const cardioGoalKcal = 400; // meta de referencia para el anillo de cardio


  const kcalPct = Math.min(100, Math.round((dayTotals.kcal/(tgt.meta||1))*100));
  const cardioPct = Math.min(100, Math.round((totalCardioKcal/cardioGoalKcal)*100));

  // ── Alertas de horario: comidas, entrenamiento y cardio programados para "ahora" ──
  const [nowClock, setNowClock] = useState(()=>nowTime()); // se actualiza solo cada minuto
  useEffect(()=>{
    const id = setInterval(()=>setNowClock(nowTime()), 60000);
    return ()=>clearInterval(id);
  },[]);
  const addMinToTime = (hhmm, min) => {
    const [h,m] = hhmm.split(":").map(Number);
    const total = h*60+m+min;
    const hh = Math.floor((total%1440)/60), mm = total%60;
    return `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
  };
  const isTimeNow = (hhmm, windowMin=45) => hhmm && nowClock>=hhmm && nowClock<=addMinToTime(hhmm,windowMin);

  const timeAlerts = [];
  mealsWithFoodToday.forEach(m=>{
    if(isTimeNow(m.time)) timeAlerts.push({id:"meal-"+m.id,icon:"🍽",color:T.blue,text:`Es hora de ${m.name}`,onClick:()=>goToMealsTab()});
  });
  todaySlots.forEach(sl=>{
    if(isTimeNow(sl.time)){
      const rt = routines.find(r=>r.id===sl.routineId);
      if(rt) timeAlerts.push({id:"routine-"+sl.id,icon:"🏋",color:T.accent,text:`Es hora de entrenar: ${rt.name}`,onClick:()=>onExecuteSlot(sl,rt)});
    }
  });
  if(todayCardioScheduled && isTimeNow(todayCardioScheduled.time)){
    timeAlerts.push({id:"cardio-"+todayCardioScheduled.id,icon:"🏃",color:T.purple,text:"Es hora de tu cardio programado",onClick:()=>goToTab("cardio")});
  }

  // ── Notificaciones del sistema — si el celular tiene un reloj inteligente emparejado
  // (Apple Watch, Wear OS), la notificación se refleja ahí solo, sin nada más que hacer.
  // El encendido/apagado se controla desde Configuraciones (⚙️ en el header). ──
  const notifiedRef = useRef(new Set());
  const [popupAlert, setPopupAlert] = useState(null);

  useEffect(()=>{
    timeAlerts.forEach(a=>{
      if(notifiedRef.current.has(a.id)) return;
      notifiedRef.current.add(a.id);
      if(!notifsEnabled) return; // el usuario apagó las alertas desde Configuraciones
      setPopupAlert(a); // siempre mostramos el popup emergente en pantalla, aunque no haya permiso de notificaciones del sistema
      if(typeof Notification!=="undefined" && Notification.permission==="granted"){
        try{ new Notification("GYMDRAFFIN", {body:a.text, icon:"/icon-192.png"}); }catch{}
      }
    });
  },[timeAlerts.map(a=>a.id).join(","), notifsEnabled]);

  const QUOTES=[
    "Cuidarte no es un lujo, es un acto de respeto hacia vos mismo.",
    "La disciplina es la forma más honesta de quererte.",
    "No busques motivación, buscá un motivo. La motivación se va, el motivo se queda.",
    "El esfuerzo de hoy es una carta que le escribís a tu yo del futuro.",
    "No entrenás para verte distinto, entrenás para sentirte distinto.",
    "Lo que hoy parece difícil, mañana va a ser tu punto de partida.",
    "A veces la victoria más grande es simplemente aparecer.",
    "Hoy no se trata de ser perfecto, se trata de no rendirte.",
    "Sos el resultado de las decisiones pequeñas que tomás todos los días.",
    "No estás compitiendo con nadie. Estás construyendo algo que solo vos vas a entender del todo.",
    "Nadie te va a cuidar como vos mismo. Hoy es una buena oportunidad para demostrártelo.",
    "Tu cuerpo escucha todo lo que tu mente le dice. Hoy, decile que puede.",
    "Cada gota de esfuerzo de hoy es un mensaje de amor propio.",
    "No es sobre llegar rápido, es sobre no dejar de caminar.",
    "El cambio no se nota de un día para otro. Se nota en quién decidís ser cada día.",
    "Tu constancia de hoy es el regalo que le hacés a tu salud de mañana.",
    "No tenés que sentirte con ganas para empezar. Solo tenés que empezar.",
    "Cada entrenamiento es una prueba de que sos capaz de sostener lo que empezás.",
    "Elegirte a vos mismo, todos los días, es la victoria más silenciosa que existe.",
    "El progreso no siempre se ve. A veces se siente en cómo aguantás un día difícil.",
    "Hoy podés sumar una razón más para confiar en vos.",
    "No hay una versión perfecta de vos esperando en el futuro. Hay una mejor versión que se construye hoy.",
    "Ser constante no es fácil, pero es lo que te va a diferenciar dentro de un año.",
    "Cada vez que aparecés a entrenar, le ganás a la excusa que casi te convence de no venir.",
    "Tu cuerpo es el único lugar en el que vas a vivir toda tu vida. Cuidalo como tal.",
    "No necesitás ser el mejor. Necesitás ser mejor que la versión de vos que no se animaba.",
    "El descanso también es parte del compromiso. Escuchate.",
    "Hoy es un buen día para demostrarte que podés sostener lo que decidiste.",
  ];
  const q=QUOTES[new Date().getDate()%QUOTES.length];

  // ── Clima — hooks SIEMPRE al nivel superior del componente ──
  const [weather, setWeather] = useState(null);
  const [wLoading, setWLoading] = useState(false);
  const [wErr, setWErr] = useState("");

  useEffect(()=>{
    const cacheKey = "gd_weather_"+nowISO()+"_h"+new Date().getHours(); // se renueva cada hora, no todo el día
    const saved = lsGet(cacheKey, null);
    if(saved){ setWeather(saved); return; }
    setWLoading(true);
    if(!navigator.geolocation){ setWErr("Ubicación no disponible"); setWLoading(false); return; }
    navigator.geolocation.getCurrentPosition(async pos=>{
      try {
        const {latitude:lat, longitude:lon} = pos.coords;
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m&wind_speed_unit=kmh&timezone=auto`;
        const res = await fetch(url);
        const d = await res.json();
        const cur = d.current;
        const code = cur.weather_code;
        const getInfo = (code) => {
          if(code===0) return {icon:"☀️",label:"Despejado"};
          if(code<=2) return {icon:"🌤️",label:"Parcialmente nublado"};
          if(code<=3) return {icon:"☁️",label:"Nublado"};
          if(code<=49) return {icon:"🌫️",label:"Neblina"};
          if(code<=57) return {icon:"🌦️",label:"Llovizna"};
          if(code<=67) return {icon:"🌧️",label:"Lluvia"};
          if(code<=77) return {icon:"❄️",label:"Nieve"};
          if(code<=82) return {icon:"🌧️",label:"Chaparrones"};
          if(code<=86) return {icon:"🌨️",label:"Nevadas"};
          if(code<=99) return {icon:"⛈️",label:"Tormenta"};
          return {icon:"🌡️",label:"Variable"};
        };
        const info = getInfo(code);
        const temp = Math.round(cur.temperature_2m);
        const feels = Math.round(cur.apparent_temperature);
        const humidity = cur.relative_humidity_2m;
        const wind = Math.round(cur.wind_speed_10m);
        let rec = "", recColor = T.green;
        if(code>=95) { rec="Tormenta eléctrica — no entrenes al aire libre."; recColor=T.red; }
        else if(code>=80 || (code>=61&&code<=67)) { rec="Está lloviendo — mejor entrenamiento bajo techo."; recColor=T.red; }
        else if(code>=51&&code<=57) { rec="Llovizna — llevá algo impermeable si salís."; recColor=T.orange; }
        else if(code>=71&&code<=86) { rec="Nieve/frío intenso — cuidado con el piso resbaladizo."; recColor=T.blue; }
        else if(wind>=40) { rec="Viento muy fuerte — precaución si entrenás al aire libre."; recColor=T.red; }
        else if(wind>=25) { rec="Viento fuerte — puede complicar el cardio exterior."; recColor=T.orange; }
        else if(temp>35) { rec="Calor extremo — hidratate bien y evitá el sol del mediodía."; recColor=T.red; }
        else if(temp>28) { rec="Calor — hidratate cada 15-20 min en cardio."; recColor=T.orange; }
        else if(temp<5) { rec="Frío intenso — calentá bien y abrigate para cardio exterior."; recColor=T.blue; }
        else if(temp<15) { rec="Fresco — buena para correr, llevá una capa extra."; recColor=T.blue; }
        else { rec="Condiciones ideales para entrenar al aire libre."; recColor=T.green; }
        const data = {icon:info.icon, label:info.label, temp, feels, humidity, wind, rec, recColor};
        setWeather(data);
        lsSet(cacheKey, data);
      } catch { setWErr("No se pudo obtener el clima"); }
      setWLoading(false);
    }, ()=>{ setWErr("Permiso de ubicación denegado"); setWLoading(false); });
  },[]);

  // ── Anillo de progreso circular con brillo, estilo Activity Rings ──
  const Ring = ({pct, color, size=58, stroke=8}) => {
    const r = (size-stroke)/2, circ = 2*Math.PI*r;
    const off = circ - (Math.min(100,Math.max(0,pct))/100)*circ;
    return <svg width={size} height={size} style={{transform:"rotate(-90deg)",flexShrink:0,
      filter:`drop-shadow(0 0 5px ${color}66)`}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.surface} strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round" style={{transition:"stroke-dashoffset .6s ease"}}/>
    </svg>;
  };

  // ── Módulo táctil: borde de color + sombra + número grande protagonista ──
  const Module = ({icon, color, label, hero, heroUnit, caption, onClick, children}) => (
    <div onClick={onClick} style={{background:T.card,borderRadius:20,padding:"16px 18px",
      cursor:onClick?"pointer":"default", borderLeft:`4px solid ${color}`,
      boxShadow:`0 6px 20px -8px ${color}55, 0 2px 8px rgba(0,0,0,.35)`,
      display:"flex",flexDirection:"column",gap:2, transition:"transform .12s"}}
      onTouchStart={e=>{if(onClick)e.currentTarget.style.transform="scale(0.98)";}}
      onTouchEnd={e=>{e.currentTarget.style.transform="scale(1)";}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
        <div style={{width:30,height:30,borderRadius:9,background:color,
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0,
          boxShadow:`0 2px 8px ${color}77`}}>{icon}</div>
        <span style={{fontSize:12,color:T.muted,fontWeight:700,letterSpacing:".02em"}}>{label}</span>
        {onClick&&<span style={{marginLeft:"auto",color,fontSize:18,fontWeight:700}}>›</span>}
      </div>
      {hero!=null&&<div style={{display:"flex",alignItems:"baseline",gap:6}}>
        <span style={{fontFamily:"'Bebas Neue'",fontSize:32,color:T.text,letterSpacing:".02em",lineHeight:1}}>{hero}</span>
        {heroUnit&&<span style={{fontSize:13,color:T.muted,fontWeight:600}}>{heroUnit}</span>}
      </div>}
      {caption&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>{caption}</div>}
      {children}
    </div>
  );

  return <div style={{display:"flex",flexDirection:"column",gap:10,minHeight:"calc(100vh - 220px)"}}>

    {/* ── Popup emergente: aparece solo cuando una alerta se activa por primera vez ── */}
    {popupAlert&&<Modal title="🔔 ALERTA" onClose={()=>setPopupAlert(null)} width={340}>
      <div style={{display:"flex",flexDirection:"column",gap:14,textAlign:"center"}}>
        <div style={{fontSize:44}}>{popupAlert.icon}</div>
        <p style={{fontSize:16,color:T.text,fontWeight:700,margin:0}}>{popupAlert.text}</p>
        <div style={{display:"flex",gap:8}}>
          <Btn v="ghost" onClick={()=>setPopupAlert(null)}>Ahora no</Btn>
          <Btn full v="success" onClick={()=>{popupAlert.onClick();setPopupAlert(null);}}>Ir ahora</Btn>
        </div>
      </div>
    </Modal>}

    {/* ── Alertas de horario — comidas, entrenamiento y cardio que tocan "ahora" ── */}
    {timeAlerts.length>0&&<div style={{display:"flex",flexDirection:"column",gap:8}}>
      {timeAlerts.map((a)=>(
        <div key={a.id} onClick={a.onClick} style={{background:a.color+"22",border:`1px solid ${a.color}`,
          borderRadius:16,padding:"12px 16px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",
          boxShadow:`0 4px 14px -4px ${a.color}66`}}>
          <span style={{fontSize:20,flexShrink:0}}>{a.icon}</span>
          <span style={{fontSize:13,color:T.text,fontWeight:700,flex:1}}>🔔 {a.text}</span>
          <span style={{color:a.color,fontSize:18,fontWeight:700}}>›</span>
        </div>
      ))}
    </div>}

    <div style={{fontSize:13,color:T.muted,padding:"0 2px"}}>
      {greet}, <span style={{color:T.text,fontWeight:700}}>{firstName}</span>
    </div>

    {/* ── Resumen del día, estilo Activity Rings ── */}
    <div style={{background:T.card,borderRadius:20,padding:"14px 18px",display:"flex",alignItems:"center",gap:14,
      boxShadow:"0 4px 16px rgba(0,0,0,.3)"}}>
      <div style={{display:"flex",flexShrink:0}}>
        <div style={{position:"relative",width:50,height:50}}><Ring pct={trainingProgress.pct} color={T.accent} size={50} stroke={7}/></div>
        <div style={{position:"relative",width:50,height:50,marginLeft:-16}}><Ring pct={cardioPct} color={T.orange} size={50} stroke={7}/></div>
        <div style={{position:"relative",width:50,height:50,marginLeft:-16}}><Ring pct={kcalPct} color={T.blue} size={50} stroke={7}/></div>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:11,color:T.muted,fontWeight:600,marginBottom:4}}>RESUMEN DE HOY</div>
        <div style={{display:"flex",flexDirection:"column",gap:2}}>
          {[["Entreno",trainingProgress.pct,T.accent],["Cardio",cardioPct,T.orange],["Comida",kcalPct,T.blue]].map(([l,v,c])=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:c,flexShrink:0}}/>
              <span style={{fontSize:11,color:T.muted}}>{l}</span>
              <span style={{fontSize:11,color:T.text,fontWeight:700,marginLeft:"auto"}}>{v}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Selector Info / Hoy — pill con contraste real, se nota que es tocable */}
    <div style={{display:"flex",background:T.surface,borderRadius:16,padding:5,gap:5,
      border:`1px solid ${T.border}`}}>
      {[["info","✨ Info"],["hoy","🏋 Hoy"]].map(([id,label])=>{
        const active = view===id;
        return <button key={id} onClick={()=>setView(id)}
          style={{flex:1,padding:"11px 0",borderRadius:12,fontWeight:800,fontSize:14,cursor:"pointer",
            background:active?T.accent:T.card,color:active?"#000":T.muted,
            border:"none",transition:"all .15s",
            boxShadow:active?`0 4px 14px -4px ${T.accent}88`:"none"}}>
          {label}
        </button>;
      })}
    </div>

    {/* ── VISTA: INFO — frase motivacional, clima, alimentación ── */}
    {view==="info"&&<div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{background:`linear-gradient(135deg,${T.purple}22,${T.accent}11)`,
        borderRadius:20,padding:"16px 18px",border:`1px solid ${T.purple}33`}}>
        <div style={{fontSize:11,color:T.purple,fontWeight:700,marginBottom:6,letterSpacing:".03em"}}>✨ MOTIVACIÓN DEL DÍA</div>
        <p style={{fontSize:14,color:T.text,lineHeight:1.45,fontWeight:600,margin:0}}>{q}</p>
      </div>

      {wLoading&&<div style={{background:T.card,borderRadius:20,padding:18,
        display:"flex",alignItems:"center",gap:10}}>
        <Spinner/><span style={{color:T.muted,fontSize:13}}>Obteniendo clima...</span>
      </div>}
      {wErr&&<div style={{background:T.card,borderRadius:20,padding:18,fontSize:13,color:T.muted}}>{wErr}</div>}
      {weather&&<Module icon={weather.icon} color={weather.recColor} label="CLIMA"
        hero={`${weather.temp}°`} caption={`${weather.label} · ${weather.humidity}% hum · ${weather.wind} km/h`}>
        <div style={{background:weather.recColor+"1c",borderRadius:12,padding:"8px 12px",marginTop:8,
          fontSize:12,color:T.text,lineHeight:1.4,fontWeight:500}}>
          {weather.rec}
        </div>
      </Module>}

      <Module icon="🍽" color={T.blue} label="ALIMENTACIÓN" onClick={()=>goToTab("nutrition")}
        hero={rnd(dayTotals.kcal)} heroUnit={`/ ${tgt.meta} kcal`}
        caption={`${mealsWithFoodToday.length} comida${mealsWithFoodToday.length!==1?"s":""} · ${goal.label} · ${todayRest?"día de descanso":todaySlots.length>0&&todayCardioScheduled?"entreno + cardio hoy":todaySlots.length>0?"día de entreno":todayCardioScheduled?"día de cardio":"día libre"}`}>
        <div style={{display:"flex",gap:8,marginTop:10}}>
          {[["Proteínas",dayTotals.prot,tgt.protG,T.blue],["Carbos",dayTotals.carb,tgt.carbG,T.orange],["Grasas",dayTotals.gras,tgt.grasG,T.green]].map(([l,v,tg,c])=>(
            <div key={l} style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                <span style={{fontSize:10,color:c,fontWeight:700}}>{l}</span>
                <span style={{fontSize:10,color:T.muted,fontWeight:600}}>{rnd(v)}/{tg}g</span>
              </div>
              <div style={{height:6,borderRadius:3,background:T.surface,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${Math.min(100,(v/(tg||1))*100)}%`,background:c,borderRadius:3}}/>
              </div>
            </div>
          ))}
        </div>
      </Module>
    </div>}

    {/* ── VISTA: HOY — entrenamiento, running/cardio, descanso ── */}
    {view==="hoy"&&<div style={{display:"flex",flexDirection:"column",gap:10}}>
      {todaySlots.length===0
        ? <Module icon="🏋" color={T.muted} label="ENTRENAMIENTO" onClick={()=>goToTab("routines")}
            hero="—" caption="Sin rutina programada para hoy"/>
        : todaySlots.map(sl=>{
            const rt = routines.find(r=>r.id===sl.routineId);
            if(!rt) return null;
            const prog = getProgress(rt);
            return <Module key={sl.id} icon="🏋" color={T.accent} label={`HOY TOCA · ${sl.time}`}
              onClick={()=>onExecuteSlot(sl,rt)} hero={rt.name}
              caption={`${prog.done}/${prog.total} series completadas`}/>;
          })}

      {todayRest
        ? <Module icon="😴" color={T.blue} label="DESCANSO" onClick={()=>goToTab("cardio")}
            hero="Hoy toca descansar" caption={`Sueño ${todayRest.sleep}/5`}/>
        : todayCardioScheduled
          ? <Module icon="🏃" color={T.purple} label={`PROGRAMADO · ${todayCardioScheduled.time}`}
              onClick={()=>goToTab("cardio")} hero="Hoy toca cardio" caption="Tocá para registrarlo"/>
          : <Module icon="🏃" color={T.orange} label="CARDIO / RUNNING" onClick={()=>goToTab("cardio")}
              hero={todayCardio.length>0?totalCardioKcal:"—"} heroUnit={todayCardio.length>0?"kcal":""}
              caption={todayCardio.length>0?"quemadas hoy":"Sin cardio hoy"}/>}
    </div>}
  </div>;
}

function HomeView({profile,routines,schedule,logs,cardioLogs,restDays,exercises,onExecuteSlot,goToTab}) {
  const today = nowISO();
  const wday  = new Date(today+"T12:00:00").getDay();

  const todaySlots = (schedule||[]).filter(s=>(s.repeat&&s.weekday===wday)||(s.date===today));
  const todayCardio = (cardioLogs||[]).filter(c=>c.date===today);
  const todayRest    = (restDays||[]).find(r=>r.date===today);
  const todayLogsCount = (logs||[]).filter(l=>l.date===today).length;

  const tgt  = calcTargets(profile);
  const goal = GOALS[profile?.objetivo]||GOALS.hipertrofia;
  const quote = getDailyQuote();

  const dateLabel = `${WLONG[wday]} ${fmtDateFull(today)}`;

  return <div className="fu" style={{display:"flex",flexDirection:"column",gap:16}}>
    <div>
      <h1 style={{fontFamily:"'Bebas Neue'",fontSize:30,letterSpacing:".06em",color:T.accent,lineHeight:1}}>
        HOLA, {(profile?.name||"").split(" ")[0]?.toUpperCase()}
      </h1>
      <p style={{fontSize:12,color:T.muted,marginTop:4}}>{dateLabel}</p>
    </div>

    {/* Frase motivadora */}
    <div style={{background:`linear-gradient(135deg, ${T.accent}18, ${T.purple}12)`,
      borderRadius:14,padding:"16px 18px",border:`1px solid ${T.accent}33`}}>
      <div style={{fontSize:11,color:T.accent,fontWeight:700,letterSpacing:".08em",marginBottom:6}}>
        ✨ FRASE DEL DÍA
      </div>
      <p style={{fontSize:14,fontStyle:"italic",color:T.text,lineHeight:1.5}}>"{quote}"</p>
    </div>

    {/* HOY TOCA */}
    <div>
      <div style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.text,letterSpacing:".05em",marginBottom:10}}>
        ⚡ HOY TOCA
      </div>
      {todaySlots.length===0 && !todayRest
        ? <div style={{background:T.card,borderRadius:12,padding:"16px",border:`1px solid ${T.border}`,
            textAlign:"center",color:T.muted,fontSize:13}}>
            No tenés actividades programadas para hoy.
            <div style={{marginTop:8}}>
              <Btn sz="sm" v="ghost" onClick={()=>goToTab("routines")}>📅 Programar rutina</Btn>
            </div>
          </div>
        : <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {todaySlots.map(sl=>{
              const rt=routines.find(r=>r.id===sl.routineId); if(!rt) return null;
              const exCount=(rt.exercises||[]).length;
              return <div key={sl.id} style={{background:T.card,borderRadius:12,padding:"12px 14px",
                border:`1px solid ${rt.color}55`,borderLeft:`4px solid ${rt.color}`,
                display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                <div>
                  <div style={{fontFamily:"'Bebas Neue'",fontSize:16,color:rt.color,letterSpacing:".04em"}}>{rt.name}</div>
                  <div style={{fontSize:11,color:T.muted}}>🕐 {sl.time} · {exCount} ejercicios</div>
                </div>
                <Btn sz="sm" v="success" onClick={()=>onExecuteSlot(sl,rt)}>▶ Ejecutar</Btn>
              </div>;
            })}
            {todayRest && <div style={{background:T.blue+"15",borderRadius:12,padding:"12px 14px",
              border:`1px solid ${T.blue}44`,display:"flex",alignItems:"center",gap:10}}>
              <div style={{fontSize:24}}>😴</div>
              <div>
                <div style={{fontFamily:"'Bebas Neue'",fontSize:15,color:T.blue}}>DÍA DE DESCANSO</div>
                <div style={{fontSize:11,color:T.muted}}>Sueño: {todayRest.sleep}/5{todayRest.notes?` · ${todayRest.notes}`:""}</div>
              </div>
            </div>}
          </div>}
    </div>

    {/* Cardio de hoy */}
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.text,letterSpacing:".05em"}}>🏃 RUNNING / CARDIO</span>
        <Btn sz="sm" v="ghost" onClick={()=>goToTab("cardio")}>+ Agregar</Btn>
      </div>
      {todayCardio.length===0
        ? <div style={{background:T.card,borderRadius:12,padding:"14px",border:`1px solid ${T.border}`,
            textAlign:"center",color:T.muted,fontSize:13}}>
            Sin actividad cardiovascular registrada hoy.
          </div>
        : <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {todayCardio.map(c=>(
              <div key={c.id} style={{background:T.card,borderRadius:10,padding:"10px 14px",
                border:`1px solid ${T.orange}33`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:13,fontWeight:600}}>
                  {({running:"🏃 Running",bici:"🚴 Bici",caminata:"🚶 Caminata",natacion:"🏊 Natación",eliptico:"⚙ Elíptico"})[c.type]||c.type}
                </span>
                <div style={{display:"flex",gap:5}}>
                  <Badge color={T.orange}>{c.duration} min</Badge>
                  <Badge color={T.red}>{c.kcal} kcal</Badge>
                </div>
              </div>
            ))}
          </div>}
    </div>

    {/* Alimentación del día */}
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.text,letterSpacing:".05em"}}>🥗 ALIMENTACIÓN</span>
        <Btn sz="sm" v="ghost" onClick={()=>goToTab("nutrition")}>Ver plan</Btn>
      </div>
      <div style={{background:T.card,borderRadius:12,padding:"14px",border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <span style={{fontSize:12,color:T.muted}}>Meta diaria</span>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.accent}}>{tgt.meta.toLocaleString()} kcal</span>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Badge color={T.blue}>P {tgt.protG}g</Badge>
          <Badge color={T.orange}>C {tgt.carbG}g</Badge>
          <Badge color={T.green}>G {tgt.grasG}g</Badge>
          <Badge color={goal.color}>🎯 {goal.label}</Badge>
        </div>
        <p style={{fontSize:11,color:T.muted,marginTop:8}}>
          Cargá lo que comiste hoy desde la pestaña Nutrición para ver tu progreso en tiempo real.
        </p>
      </div>
    </div>

    {/* Resumen rápido */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
      {[["Ejercicios hoy",todayLogsCount,"💪"],["Rutinas activas",routines.length,"📋"],["Biblioteca",exercises.length,"📦"]].map(([l,v,i])=>(
        <div key={l} style={{background:T.card,borderRadius:10,padding:"10px 8px",textAlign:"center",border:`1px solid ${T.border}`}}>
          <div style={{fontSize:16}}>{i}</div>
          <div style={{fontFamily:"'Bebas Neue'",fontSize:18,color:T.accent}}>{v}</div>
          <div style={{fontSize:9,color:T.muted,marginTop:1}}>{l}</div>
        </div>
      ))}
    </div>
  </div>;
}

// ── Modal para elegir/cambiar objetivo + por cuánto tiempo mantenerlo ──
function ChangeObjectiveModal({currentObjetivo, onConfirm, onClose, isExpiredNotice}) {
  const [objetivo, setObjetivo] = useState(currentObjetivo||"");
  const [meses, setMeses] = useState(3);
  const [useCustomEnd, setUseCustomEnd] = useState(false);
  const [customEnd, setCustomEnd] = useState("");

  const endDate = useCustomEnd && customEnd ? customEnd : addMonthsISO(nowISO(), meses);

  return <Modal title={isExpiredNotice?"🎯 ELEGÍ TU PRÓXIMO OBJETIVO":"CAMBIAR OBJETIVO"} onClose={onClose} width={380}>
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {isExpiredNotice&&<p style={{fontSize:13,color:T.orange}}>
        Se cumplió el plazo de tu objetivo anterior. Elegí cómo seguir — lo que ya cargaste con el objetivo viejo no se pierde.
      </p>}
      <div>
        <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
          Objetivo
        </label>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {Object.entries(GOALS).map(([key,g])=>(
            <button key={key} onClick={()=>setObjetivo(key)}
              style={{padding:"10px 12px",borderRadius:10,fontWeight:700,fontSize:13,cursor:"pointer",textAlign:"left",
                background:objetivo===key?g.color+"22":T.surface,
                color:objetivo===key?g.color:T.muted,
                border:`1px solid ${objetivo===key?g.color:T.border}`}}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {objetivo&&<div>
        <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
          ¿Por cuántos meses lo vas a mantener?
        </label>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
          {[1,2,3,6,12].map(m=>(
            <button key={m} onClick={()=>{setMeses(m);setUseCustomEnd(false);}}
              style={{padding:"7px 14px",borderRadius:20,fontWeight:700,fontSize:12,cursor:"pointer",
                background:!useCustomEnd&&meses===m?T.accent:T.surface,color:!useCustomEnd&&meses===m?"#000":T.muted,
                border:`1px solid ${!useCustomEnd&&meses===m?T.accent:T.border}`}}>
              {m} {m===1?"mes":"meses"}
            </button>
          ))}
        </div>
        {!useCustomEnd&&<button onClick={()=>setUseCustomEnd(true)}
          style={{background:"none",color:T.blue,border:"none",fontSize:12,cursor:"pointer",padding:0}}>
          📅 Elegir una fecha exacta en el calendario
        </button>}
        {useCustomEnd&&<Field label="Fecha de fin" type="date" value={customEnd} onChange={setCustomEnd} min={addDaysISO(nowISO(),1)}/>}
        <p style={{fontSize:12,color:T.muted,marginTop:8}}>
          Vas a mantener este objetivo hasta el <b style={{color:T.text}}>{fmtDateFull(endDate)}</b>.
        </p>
      </div>}

      <Btn full v="success" disabled={!objetivo || (useCustomEnd && !customEnd)} onClick={()=>{
        if(useCustomEnd && !customEnd){ alert("Elegí una fecha en el calendario antes de confirmar."); return; }
        onConfirm(objetivo,endDate);
      }}>
        <Icon n="check" s={15}/>Confirmar
      </Btn>
    </div>
  </Modal>;
}

function MainApp({authUser, onLogout}) {
  const stored=loadData();
  const [exercises,  setExercises]  = useState(()=>stored.exercises||[]);
  const [logs,       setLogs]       = useState(()=>stored.logs||[]);
  const [routines,   setRoutines]   = useState(()=>stored.routines||[]);
  const [cardioLogs, setCardioLogs] = useState(()=>stored.cardioLogs||[]);
  const [cardioSchedule, setCardioSchedule] = useState(()=>stored.cardioSchedule||[]); // cardio programado, todavía no realizado
  const [supplementLogs, setSupplementLogs] = useState(()=>stored.supplementLogs||[]); // registro de tomas de suplementos
  const [restDays,   setRestDays]   = useState(()=>stored.restDays||[]);
  const [schedule,   setSchedule]   = useState(()=>stored.schedule||[]);
  const [profile,    setProfile]    = useState(()=>stored.profile||DEFAULT_PROFILE);
  const [objectiveHistory, setObjectiveHistory] = useState(()=>stored.objectiveHistory||[]);
  const [tab,           setTab]           = useState("today");
  const [showForm,      setShowForm]      = useState(false);
  const [editEx,        setEditEx]        = useState(null);
  const [showSearch,    setShowSearch]    = useState(false);
  const [showProfile,   setShowProfile]   = useState(false);
  const [showSettings,  setShowSettings]  = useState(false);
  const [storageWarning, setStorageWarning] = useState(false);
  useEffect(()=>{
    const onWarn = () => setStorageWarning(true);
    window.addEventListener("gd:storage-full", onWarn);
    return () => window.removeEventListener("gd:storage-full", onWarn);
  },[]);
  const [notifsEnabled, setNotifsEnabledState] = useState(()=>lsGet("gd_notifs_enabled", true));
  const setNotifsEnabled = (v) => { setNotifsEnabledState(v); lsSet("gd_notifs_enabled", v); };
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  const startTour = () => {
    setShowProfile(false);
    setTourStep(0);
    setTab(TOUR_STEPS[0].tabId);
    setTourActive(true);
  };
  const finishTour = () => {
    setTourActive(false);
    setProfile(p=>({...p, tourSeen:true}));
  };
  const nextTourStep = () => {
    if(tourStep < TOUR_STEPS.length-1){
      const next = tourStep+1;
      setTourStep(next);
      setTab(TOUR_STEPS[next].tabId);
    } else {
      finishTour();
    }
  };
  const prevTourStep = () => {
    if(tourStep>0){
      const prev = tourStep-1;
      setTourStep(prev);
      setTab(TOUR_STEPS[prev].tabId);
    }
  };

  // Mostrar la guía automáticamente la primera vez (una sola vez por cuenta)
  useEffect(()=>{
    if(!profile.tourSeen){
      const t = setTimeout(()=>startTour(), 600); // pequeña pausa para que la app termine de cargar
      return ()=>clearTimeout(t);
    }
  },[]);

  const [showChangeObjective, setShowChangeObjective] = useState(false);
  const [dismissedExpiry, setDismissedExpiry] = useState(false); // cerrar el aviso por esta sesión (vuelve a aparecer la próxima vez)

  const changeObjective = (newObjetivo, endDate) => {
    const today = nowISO();
    const active = getActiveObjectivePeriod(objectiveHistory);
    setObjectiveHistory(prev => {
      const updated = prev.map(p => {
        if(active && p.id===active.id) return {...p, endDate: addDaysISO(today,-1)};
        return p;
      });
      return [...updated, {id:uid(), objetivo:newObjetivo, startDate:today, endDate}];
    });
    setProfile(p=>({...p, objetivo:newObjetivo}));
    setShowChangeObjective(false);
    setDismissedExpiry(false);
    // El aviso se muestra después de que la ventana ya cerró visualmente, para que no se sienta trabada
    setTimeout(()=>alert(`✅ Objetivo actualizado: ${GOALS[newObjetivo]?.label} hasta el ${fmtDateFull(endDate)}.`), 150);
  };

  const objectiveExpired = isActiveObjectiveExpired(objectiveHistory) && !dismissedExpiry;
  const [activeSession, setActiveSession] = useState(null); // {slot, routine}
  const [editingProfile,setEditingProfile]= useState(false);
  // ── Estado de las filas 2 del header, por sección (subido desde cada vista para que quede fijo) ──
  const [showNewRt,      setShowNewRt]      = useState(false);      // Rutinas: nueva rutina
  const [showGenAI,      setShowGenAI]      = useState(false);      // Rutinas: generar con IA
  const [routinesInnerTab, setRoutinesInnerTab] = useState("rutinas"); // Rutinas: Mis Rutinas / Calendario
  const [nutriTab,        setNutriTab]        = useState("comidasdia"); // Nutrición: categoría activa
  const [selectedWeekday, setSelectedWeekday] = useState(()=>new Date().getDay()); // Nutrición: día editado
  const [showCopyDay,     setShowCopyDay]     = useState(false);    // Nutrición: modal copiar día
  const [showScheduleMeals, setShowScheduleMeals] = useState(false); // Nutrición: modal calendario "Programar comidas"
  const [nutriWeekOffset, setNutriWeekOffset] = useState(0);        // Nutrición: navegación de semana (referencia de fechas)
  const [statTab,         setStatTab]         = useState("gym");    // Estadísticas: categoría activa
  const [profForm,      setProfForm]      = useState(profile);
  const [saveMsg,       setSaveMsg]       = useState("");
  const importRef=useRef();

  useEffect(()=>{
    saveData({exercises,logs,routines,cardioLogs,cardioSchedule,restDays,schedule,profile,objectiveHistory,supplementLogs});
  },[exercises,logs,routines,cardioLogs,cardioSchedule,restDays,schedule,profile,objectiveHistory,supplementLogs]);

  // ── Sincronización con la nube: cada 25s, y cuando la app pasa a segundo plano ──
  useEffect(()=>{
    const auth = loadAuth();
    if(!auth?.token) return;
    const interval = setInterval(()=>{ syncToCloud(auth.token); }, 25000);
    const onHide = () => { if(document.visibilityState==="hidden") syncToCloud(auth.token); };
    document.addEventListener("visibilitychange", onHide);
    return () => { clearInterval(interval); document.removeEventListener("visibilitychange", onHide); };
  },[]);

  const handleExecuteSlot = (slot, routine) => {
    setActiveSession({slot, routine});
    setTab("routines");
  };

  const handleFinishSession = (newLogs) => {
    if(newLogs.length > 0) {
      setLogs(p => [...p, ...newLogs]);
    }
    setActiveSession(null);
  };

  const saveEx=ex=>{setExercises(p=>p.find(e=>e.id===ex.id)?p.map(e=>e.id===ex.id?ex:e):[...p,ex]);setShowForm(false);setEditEx(null);};
  const [confirmDelEx, setConfirmDelEx] = useState(null); // ejercicio a eliminar
  const delEx = id => setConfirmDelEx(exercises.find(e=>e.id===id)||null);
  const openEdit=ex=>{setEditEx(ex);setShowForm(true);};
  const saveProfile=()=>{setProfile(profForm);setEditingProfile(false);setSaveMsg("✅ Perfil actualizado");setTimeout(()=>setSaveMsg(""),3000);};

  const exportData=()=>{
    try{
      const dietData = lsGet(DIET_KEY, {});
      const recipesData = lsGet(RECIPES_KEY, []);
      const productsData = lsGet(PROD_KEY, []);
      const blob=new Blob([JSON.stringify({version:9,exportedAt:nowISO(),data:{exercises,logs,routines,cardioLogs,cardioSchedule,restDays,schedule,profile,objectiveHistory,supplementLogs,dietData,recipesData,productsData}},null,2)],{type:"application/json"});
      const url=URL.createObjectURL(blob);const a=document.createElement("a");
      a.href=url;a.download=`GYMDRAFFIN_${nowISO()}.json`;document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
      setSaveMsg("✅ Archivo guardado");setTimeout(()=>setSaveMsg(""),3000);
    }catch(e){setSaveMsg("❌ Error");setTimeout(()=>setSaveMsg(""),3000);}
  };

  const importData=e=>{
    const file=e.target.files?.[0];if(!file)return;
    const reader=new FileReader();
    reader.onload=ev=>{
      try{
        const backup=JSON.parse(ev.target.result);
        if(!backup.data){setSaveMsg("❌ Archivo inválido");setTimeout(()=>setSaveMsg(""),3000);return;}
        const {exercises:ex,logs:lg,routines:rt,cardioLogs:cl,cardioSchedule:cs,restDays:rd,schedule:sc,profile:pr,objectiveHistory:oh,supplementLogs:sl,dietData,recipesData,productsData}=backup.data;
        if(Array.isArray(ex))setExercises(ex);if(Array.isArray(lg))setLogs(lg);
        if(Array.isArray(rt))setRoutines(rt);if(Array.isArray(cl))setCardioLogs(cl);
        if(Array.isArray(cs))setCardioSchedule(cs);
        if(Array.isArray(rd))setRestDays(rd);if(Array.isArray(sc))setSchedule(sc);
        if(pr)setProfile(pr);
        if(Array.isArray(oh))setObjectiveHistory(oh);
        if(Array.isArray(sl))setSupplementLogs(sl);
        if(dietData)saveDiet(dietData);
        if(Array.isArray(recipesData))saveRecipes(recipesData);
        if(Array.isArray(productsData))saveProducts(productsData);
        setSaveMsg("✅ Datos restaurados — recargá la app para ver nutrición actualizada");setTimeout(()=>setSaveMsg(""),5000);
      }catch{setSaveMsg("❌ Error al leer archivo");setTimeout(()=>setSaveMsg(""),3000);}
    };
    reader.readAsText(file);e.target.value="";
  };

  const groups=["Todos",...new Set(exercises.map(e=>e.muscleGroup).filter(Boolean))];
  const [filterGroup,setFilterGroup]=useState("Todos");
  const filtered=filterGroup==="Todos"?exercises:exercises.filter(e=>e.muscleGroup===filterGroup);
  const today=nowISO();

  const TABS=[["today","Hoy","today"],["routine","Ejercicios","dumbbell"],["routines","Rutinas","layers"],
    ["nutrition","Nutrición","chart"],["cardio","Cardio","cardio"],["stats","Stats","trend"],["share","Compartir","share"]];

  const PANEL_GAP = 18; // espacio visual entre pantallas mientras se deslizan

  // ── Navegación por gesto: el contenido sigue al dedo en tiempo real, y la pestaña
  // adyacente ya se ve entrando desde el costado (como el selector de apps del celular) ──
  const [dragX, setDragX] = useState(0);          // desplazamiento actual en px mientras se arrastra
  const [dragTransition, setDragTransition] = useState(false); // true solo durante la animación de snap (no durante el arrastre)
  const touchRef = useRef({x:0, y:0, tracking:false, locked:false});
  const pendingTabRef = useRef(null); // pestaña destino cuando se aceptó un swipe (se aplica al terminar la animación)
  const lastAdjacentRef = useRef({tabId:null, side:null}); // recuerda el panel vecino mientras dura la animación de vuelta (aunque dragX ya sea 0)

  // Si el toque empezó dentro de una tira que ya se desplaza horizontalmente
  // (filtros, selector de días, etc.), dejamos que esa tira scrollee normal y no interceptamos.
  const startsInsideHorizontalScroller = el => {
    let node = el;
    while(node && node instanceof HTMLElement){
      const style = window.getComputedStyle(node);
      if((style.overflowX==="auto"||style.overflowX==="scroll") && node.scrollWidth > node.clientWidth) return true;
      node = node.parentElement;
    }
    return false;
  };

  const contentTouchRef = useRef(null); // ref al contenedor completo — usado para el listener nativo de touchmove

  const handleTouchStart = () => {
    // Desplazamiento lateral desactivado por el momento — el cambio de pestaña
    // queda solo a cargo de los botones del menú inferior.
    touchRef.current = {x:0, y:0, tracking:false, locked:false};
  };

  // Listener nativo (no pasivo) — permite bloquear el scroll vertical de la página
  // en cuanto se confirma que el gesto es horizontal, dejando la pantalla "rígida" en ese eje.
  useEffect(()=>{
    const el = contentTouchRef.current;
    if(!el) return;
    const onNativeMove = e => {
      if(!touchRef.current.tracking) return;
      if(document.body.style.overflow === "hidden"){ touchRef.current.tracking = false; setDragX(0); return; }
      const t = e.touches[0];
      const dx = t.clientX - touchRef.current.x;
      const dy = t.clientY - touchRef.current.y;

      if(!touchRef.current.locked){
        if(Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if(Math.abs(dy) > Math.abs(dx)){
          touchRef.current.tracking = false; // gana el eje vertical → dejamos el scroll nativo de la página
          return;
        }
        touchRef.current.locked = true; // gana el eje horizontal → a partir de ahora bloqueamos el vertical
      }

      e.preventDefault();
      const idx = TABS.findIndex(([id])=>id===tab);
      let follow = dx;
      if(dx > 0 && idx===0) follow = dx*0.35;
      if(dx < 0 && idx===TABS.length-1) follow = dx*0.35;
      setDragX(follow);
    };
    el.addEventListener('touchmove', onNativeMove, {passive:false});
    return () => el.removeEventListener('touchmove', onNativeMove);
  }, [tab]);

  const handleTouchEnd = () => {
    if(!touchRef.current.tracking){ return; }
    touchRef.current.tracking = false;
    const idx = TABS.findIndex(([id])=>id===tab);
    const width = window.innerWidth || 360;
    const step = width + PANEL_GAP; // distancia real entre paneles, incluyendo el espacio visual entre ellos
    const threshold = Math.min(110, width*0.22);

    if(dragX <= -threshold && idx < TABS.length-1){
      pendingTabRef.current = TABS[idx+1][0];
      setDragTransition(true);
      setDragX(-step); // el panel actual sale del todo mientras el siguiente (ya visible al costado) termina de entrar
    } else if(dragX >= threshold && idx > 0){
      pendingTabRef.current = TABS[idx-1][0];
      setDragTransition(true);
      setDragX(step);
    } else if(Math.abs(dragX) > 0.5){
      pendingTabRef.current = null; // no llegó al umbral → vuelve suave a su lugar, sin cambiar de pestaña
      setDragTransition(true);
      setDragX(0);
    } else {
      // Toque simple, sin desplazamiento real: nada que animar
      pendingTabRef.current = null;
      setDragTransition(false);
      setDragX(0);
    }
    // Red de seguridad: si por algún motivo transitionend no se disparara, esto igual resuelve el estado.
    setTimeout(()=>{
      if(pendingTabRef.current){
        setTab(pendingTabRef.current);
        pendingTabRef.current = null;
        setDragTransition(false);
        setDragX(0);
      }
    }, 400);
  };

  // Se dispara cuando la animación CSS realmente termina. Como la pestaña adyacente ya estaba
  // renderizada y visualmente en su lugar durante todo el arrastre, el cambio de pestaña acá
  // no produce ningún salto — el panel que ya se veía simplemente pasa a ser "el actual".
  const handleContentTransitionEnd = e => {
    if(e.propertyName!=="transform") return;
    if(pendingTabRef.current){
      const nextId = pendingTabRef.current;
      pendingTabRef.current = null;
      setTab(nextId);
    }
    lastAdjacentRef.current = {tabId:null, side:null}; // la animación terminó del todo, ya no hace falta mostrar el vecino
    setDragTransition(false); // siempre, sea que cambió de pestaña o que volvió a su lugar
    setDragX(0);
  };

  // Determina si hay una pestaña adyacente visible durante el arrastre/animación actual,
  // y de qué lado se muestra, para renderizarla junto a la actual sin espacio vacío entre medio.
  const idxNow = TABS.findIndex(([id])=>id===tab);
  let adjacentTabId = null, adjacentSide = null;
  if(dragX < -0.5 && idxNow < TABS.length-1){
    adjacentTabId = TABS[idxNow+1][0]; adjacentSide = "next";
    lastAdjacentRef.current = {tabId:adjacentTabId, side:adjacentSide};
  } else if(dragX > 0.5 && idxNow > 0){
    adjacentTabId = TABS[idxNow-1][0]; adjacentSide = "prev";
    lastAdjacentRef.current = {tabId:adjacentTabId, side:adjacentSide};
  } else if(dragTransition && lastAdjacentRef.current.tabId){
    // dragX ya volvió a 0, pero la animación de vuelta sigue en curso — seguimos mostrando
    // el panel vecino para que se deslice junto con el actual, sin desaparecer de golpe.
    adjacentTabId = lastAdjacentRef.current.tabId;
    adjacentSide = lastAdjacentRef.current.side;
  }

  return <>
    <style>{CSS}</style>
    <div ref={contentTouchRef} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
      style={{minHeight:"100vh",background:T.bg,paddingBottom:"calc(80px + env(safe-area-inset-bottom))"}}>
      {/* Header — fijo, siempre visible arriba de todo. Incluye una segunda fila con los
          botones de Ejercicios cuando corresponde, como parte del mismo bloque (sin gap). */}
      <div style={{background:T.surface,borderBottom:`1px solid ${T.border}`,
        position:"fixed",top:0,left:0,right:0,zIndex:150,display:"flex",flexDirection:"column"}}>

        {/* Fila 1: GYMDRAFFIN + usuario */}
        <div style={{padding:"0 16px",height:"calc(60px + env(safe-area-inset-top))",boxSizing:"border-box",
          paddingTop:"env(safe-area-inset-top)",
          display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
          <div>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:24,letterSpacing:".1em",lineHeight:1}}>
              <span style={{color:T.accent}}>GYM</span><span style={{color:T.text}}>DRAFFIN</span>
            </div>
            <div style={{fontSize:10,color:T.muted}}>{fmtDateFull(today)}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>setShowSettings(true)}
              style={{background:"none",border:`1px solid ${T.border}`,borderRadius:10,
                width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",
                cursor:"pointer",fontSize:16}}>
              ⚙️
            </button>
            <button onClick={()=>setShowProfile(v=>!v)}
              style={{background:showProfile?T.accent+"22":"none",
                border:`1px solid ${showProfile?T.accent:T.border}`,borderRadius:10,padding:"4px 10px",
                cursor:"pointer",display:"flex",alignItems:"center",gap:8,transition:"all .15s"}}>
              <Avatar user={profile} size={28}/>
              <div style={{textAlign:"left"}}>
                <div style={{fontSize:11,fontWeight:700,color:showProfile?T.accent:T.text,lineHeight:1}}>{(profile.name||"").split(" ")[0]}</div>
                <div style={{fontSize:10,color:T.muted,lineHeight:1.3}}>{GOALS[profile.objetivo]?.label||"—"}</div>
              </div>
            </button>
          </div>
        </div>

        {/* Fila 2: solo en Ejercicios — filtros + acciones, pegada sin espacio a la fila de arriba */}
        {tab==="routine"&&<div style={{padding:"0 14px 8px",height:46,boxSizing:"border-box",
          display:"flex",gap:6,overflowX:"auto",alignItems:"center",borderTop:`1px solid ${T.border}`}}>
          {groups.map(g=><button key={g} onClick={()=>setFilterGroup(g)}
            style={{background:filterGroup===g?T.accent:T.card,color:filterGroup===g?"#000":T.muted,
              border:`1px solid ${filterGroup===g?T.accent:T.border}`,borderRadius:20,padding:"5px 12px",
              fontSize:11,fontWeight:600,whiteSpace:"nowrap",cursor:"pointer",flexShrink:0}}>{g}</button>)}
          <button onClick={()=>{setEditEx(null);setShowForm(true);}}
            style={{background:T.accent,color:"#000",border:"none",borderRadius:20,padding:"5px 14px",
              fontSize:11,fontWeight:700,whiteSpace:"nowrap",cursor:"pointer",flexShrink:0,
              display:"flex",alignItems:"center",gap:4}}>
            <Icon n="plus" s={12}/>Nuevo
          </button>
          <button onClick={()=>setShowSearch(true)}
            style={{background:T.blue+"22",color:T.blue,border:`1px solid ${T.blue}44`,borderRadius:20,
              padding:"5px 14px",fontSize:11,fontWeight:700,whiteSpace:"nowrap",
              cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",gap:4}}>
            🔍 Buscar en la web
          </button>
        </div>}

        {/* Fila 2: RUTINAS — título + Nueva rutina + Generar con IA */}
        {tab==="routines"&&<div style={{padding:"0 14px 8px",minHeight:46,boxSizing:"border-box",
          display:"flex",flexWrap:"wrap",gap:8,alignItems:"center",justifyContent:"space-between",
          borderTop:`1px solid ${T.border}`}}>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:18,letterSpacing:".05em",color:T.accent}}>RUTINAS</span>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <button onClick={()=>setShowNewRt(v=>!v)}
              style={{background:T.accent,color:"#000",border:"none",borderRadius:20,padding:"5px 12px",
                fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
              <Icon n="plus" s={12}/>Nueva rutina
            </button>
            <button onClick={()=>setShowGenAI(true)}
              style={{background:"transparent",color:T.purple,border:`1px solid ${T.purple}55`,borderRadius:20,
                padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
              ✨ Generar con IA
            </button>
          </div>
        </div>}
        {tab==="routines"&&<div style={{padding:"0 14px 8px",boxSizing:"border-box",display:"flex",
          background:T.surface,gap:3}}>
          {[["rutinas","📋 Mis Rutinas"],["calendario","📅 Calendario"]].map(([id,label])=>(
            <button key={id} onClick={()=>setRoutinesInnerTab(id)}
              style={{flex:1,padding:"6px 0",borderRadius:8,fontWeight:700,fontSize:12,
                background:routinesInnerTab===id?T.accent:T.card,color:routinesInnerTab===id?"#000":T.muted,
                border:"none",cursor:"pointer",transition:"all .15s"}}>
              {label}
            </button>
          ))}
        </div>}

        {/* Fila 2: CARDIO — solo título */}
        {tab==="cardio"&&<div style={{padding:"0 14px 8px",minHeight:38,boxSizing:"border-box",
          display:"flex",alignItems:"center",borderTop:`1px solid ${T.border}`}}>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:18,letterSpacing:".05em",color:T.accent}}>CARDIO</span>
        </div>}

        {/* Filas de NUTRICIÓN: título+objetivo / 4 categorías / (si Comidas del día) selector de semana */}
        {tab==="nutrition"&&(()=>{
          const goal = GOALS[profile?.objetivo]||GOALS.hipertrofia;
          const NUTRI_CATS = [
            ["comida","🍎","Alimentos"],
            ["comidasdia","📋","Comidas"],
            ["suplementos","💊","Suplem."],
            ["biblioteca","📦","Productos"],
          ];
          return <>
            <div style={{padding:"0 14px 10px",boxSizing:"border-box",
              display:"flex",alignItems:"center",gap:8,
              borderTop:`1px solid ${T.border}`,paddingTop:6}}>
              <span style={{fontFamily:"'Bebas Neue'",fontSize:16,letterSpacing:".04em",color:T.accent,flexShrink:0}}>NUTRICIÓN</span>
              <div style={{display:"flex",gap:6,flex:1,minWidth:0}}>
                {NUTRI_CATS.map(([key,icon,label])=>{
                  const active = nutriTab===key;
                  return <button key={key} onClick={()=>{setNutriTab(key);window.scrollTo({top:0,behavior:"smooth"});}}
                    style={{background:active?T.accent:T.card,color:active?"#000":T.muted,
                      border:`1px solid ${active?T.accent:T.border}`,borderRadius:10,
                      flex:1,minWidth:0,height:54,cursor:"pointer",
                      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2}}>
                    <span style={{fontSize:20,lineHeight:1}}>{icon}</span>
                    <span style={{fontSize:8,fontWeight:active?800:600,lineHeight:1,whiteSpace:"nowrap"}}>{label}</span>
                  </button>;
                })}
              </div>
            </div>
            {nutriTab==="comidasdia"&&<div style={{padding:"0 14px 8px",boxSizing:"border-box",
              borderTop:`1px solid ${T.border}`,paddingTop:6}}>
              <button onClick={()=>setShowScheduleMeals(true)}
                style={{width:"100%",background:T.accent,color:"#000",border:"none",borderRadius:10,
                  padding:"9px 0",fontSize:13,fontWeight:700,cursor:"pointer",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                📅 Programar comidas — {WLONG[selectedWeekday]}
              </button>
            </div>}
          </>;
        })()}

        {/* Fila 2: ESTADÍSTICAS — solo título */}
        {tab==="stats"&&<div style={{padding:"0 14px 8px",minHeight:38,boxSizing:"border-box",
          display:"flex",alignItems:"center",borderTop:`1px solid ${T.border}`}}>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:18,letterSpacing:".05em",color:T.accent}}>ESTADÍSTICAS</span>
        </div>}

        {/* Fila 2: COMPARTIR — solo título */}
        {tab==="share"&&<div style={{padding:"0 14px 8px",minHeight:38,boxSizing:"border-box",
          display:"flex",alignItems:"center",borderTop:`1px solid ${T.border}`}}>
          <span style={{fontFamily:"'Bebas Neue'",fontSize:18,letterSpacing:".05em",color:T.accent}}>COMPARTIR</span>
        </div>}
      </div>

      {/* Espaciador — se ajusta según cuántas filas tenga el header de la sección activa */}
      <div style={{height: `calc(${
        tab==="routine" ? 106 :
        tab==="routines" ? 144 :
        tab==="cardio" ? 98 :
        tab==="stats" ? 98 :
        tab==="share" ? 98 :
        tab==="nutrition" ? (nutriTab==="comidasdia" ? 202 : 130) :
        60
      }px + env(safe-area-inset-top))`}}/>

      {/* Panel perfil — ahora es un modal real, aparece por encima de toda la app */}
      {showProfile&&<Modal title="MI PERFIL" onClose={()=>setShowProfile(false)} width={420}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {!editingProfile&&<div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
          <Avatar user={profile} size={50}/>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Bebas Neue'",fontSize:20}}>{profile.name}</div>
            <div style={{display:"flex",gap:8,marginTop:5,flexWrap:"wrap"}}>
              {profile.peso>0&&<span style={{fontSize:12,color:T.muted}}>⚖ {profile.peso}kg</span>}
              {profile.altura>0&&<span style={{fontSize:12,color:T.muted}}>📏 {profile.altura}cm</span>}
              {profile.edad>0&&<span style={{fontSize:12,color:T.muted}}>🎂 {profile.edad}a</span>}
              <span style={{fontSize:12,color:GOALS[profile.objetivo]?.color||T.accent}}>🎯 {GOALS[profile.objetivo]?.label}</span>
            </div>
          </div>
          <Btn sz="sm" v="ghost" onClick={()=>{setProfForm(profile);setEditingProfile(true);}}><Icon n="edit" s={13}/>Editar</Btn>
        </div>}

        {!editingProfile&&(()=>{
          const active = getActiveObjectivePeriod(objectiveHistory);
          return <div style={{background:T.surface,borderRadius:10,padding:"10px 14px",
            display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div>
              <div style={{fontSize:11,color:T.muted}}>Objetivo actual</div>
              {active
                ? <div style={{fontSize:12,color:T.text}}>
                    Hasta el <b>{fmtDateFull(active.endDate)}</b>
                  </div>
                : <div style={{fontSize:12,color:T.orange}}>Sin plazo definido</div>}
            </div>
            <Btn sz="sm" v="ghost" onClick={()=>setShowChangeObjective(true)}>Cambiar</Btn>
          </div>;
        })()}
        {editingProfile&&<div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8}}>
            <Field label="Nombre" value={profForm.name||""} onChange={v=>setProfForm(f=>({...f,name:v}))}/>
            <Field label="Peso kg" type="number" value={profForm.peso||""} onChange={v=>setProfForm(f=>({...f,peso:toNum(v)}))}/>
            <Field label="Altura cm" type="number" value={profForm.altura||""} onChange={v=>setProfForm(f=>({...f,altura:toNum(v)}))}/>
            <Field label="Edad" type="number" value={profForm.edad||""} onChange={v=>setProfForm(f=>({...f,edad:toNum(v)}))}/>
            <Sel label="Sexo" value={profForm.sexo||"m"} onChange={v=>setProfForm(f=>({...f,sexo:v}))} options={[{value:"m",label:"Masculino"},{value:"f",label:"Femenino"}]}/>
            <Sel label="Objetivo" value={profForm.objetivo||"hipertrofia"} onChange={v=>setProfForm(f=>({...f,objetivo:v}))} options={Object.entries(GOALS).map(([k,v])=>({value:k,label:v.label}))}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={saveProfile}><Icon n="check" s={15}/>Guardar</Btn>
            <Btn v="ghost" onClick={()=>setEditingProfile(false)}>Cancelar</Btn>
          </div>
        </div>}
        <div style={{paddingTop:12,borderTop:`1px solid ${T.border}`,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",width:"100%"}}>💾 Copia de seguridad</span>
          <Btn sz="sm" v="success" onClick={exportData}><Icon n="download" s={13}/>Guardar archivo</Btn>
          <Btn sz="sm" v="info" onClick={()=>importRef.current?.click()}>📂 Cargar archivo</Btn>
          <input ref={importRef} type="file" accept=".json" style={{display:"none"}} onChange={importData}/>
          <p style={{fontSize:11,color:T.muted,width:"100%"}}>Guardá antes de cerrar para no perder los datos.</p>
        </div>
        {authUser&&<div style={{paddingTop:12,borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:6}}>
          <span style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>👤 Cuenta</span>
          <p style={{fontSize:12,color:T.text}}>{authUser.email}</p>
          <Btn sz="sm" v="danger" onClick={async()=>{
            if(!confirm("¿Cerrar sesión? Tus datos ya están guardados en la nube.")) return;
            if(onLogout) await onLogout();
          }}>
            🚪 Cerrar sesión
          </Btn>
        </div>}
        <div style={{paddingTop:12,borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:6}}>
          <span style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>🎓 Ayuda</span>
          <Btn sz="sm" v="ghost" onClick={startTour}>
            🎓 Ver guía de inicio
          </Btn>
        </div>
        <div style={{paddingTop:12,borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:6}}>
          <span style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>🔧 Mantenimiento</span>
          <p style={{fontSize:11,color:T.muted}}>
            Si la app no se actualiza sola o se ve rara, probá esto (no borra tus datos guardados, solo el caché técnico):
          </p>
          <Btn sz="sm" v="danger" onClick={async()=>{
            try{
              if('serviceWorker' in navigator){
                const regs=await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map(r=>r.unregister()));
              }
              if('caches' in window){
                const keys=await caches.keys();
                await Promise.all(keys.map(k=>caches.delete(k)));
              }
            }catch(e){}
            window.location.href=window.location.pathname+'?v='+Date.now();
          }}>
            🔄 Forzar actualización completa
          </Btn>
        </div>
        {saveMsg&&<div style={{fontSize:13,fontWeight:600,color:saveMsg.startsWith("✅")?T.green:T.red}}>{saveMsg}</div>}
        </div>
      </Modal>}

      {showChangeObjective&&<ChangeObjectiveModal currentObjetivo={profile.objetivo}
        onConfirm={changeObjective} onClose={()=>setShowChangeObjective(false)}/>}

      {objectiveExpired&&!showChangeObjective&&<ChangeObjectiveModal currentObjetivo=""
        onConfirm={changeObjective} onClose={()=>setDismissedExpiry(true)} isExpiredNotice/>}

      {showSettings&&(()=>{
        const browserPermission = typeof Notification!=="undefined" ? Notification.permission : "unsupported";
        const lastError = lsGet("gd_last_error", null);

        // ── Diagnóstico de almacenamiento — para saber qué está ocupando espacio y poder liberarlo ──
        const bytesOf = (v) => { try{ return new Blob([typeof v==="string"?v:JSON.stringify(v)]).size; }catch{ return 0; } };
        let totalBytes = 0;
        try{ for(const k in localStorage) if(localStorage.hasOwnProperty(k)) totalBytes += bytesOf(localStorage[k]) + k.length; }catch{}
        const totalMB = (totalBytes/1024/1024).toFixed(2);
        const QUOTA_MB = 5; // estimación conservadora del límite típico del navegador

        const storedRecipes = lsGet(RECIPES_KEY, []);
        const storedProducts = lsGet(PROD_KEY, []);
        const bigItems = [
          ...storedRecipes.filter(r=>r.foto).map(r=>({tipo:"Receta", nombre:r.nombre, bytes:bytesOf(r.foto), kind:"recipe", id:r.id})),
          ...(exercises||[]).filter(e=>e.photo && e.photo.startsWith("data:")).map(e=>({tipo:"Ejercicio", nombre:e.name, bytes:bytesOf(e.photo), kind:"exercise", id:e.id})),
          ...storedProducts.filter(p=>p.foto).map(p=>({tipo:"Producto", nombre:p.nombre, bytes:bytesOf(p.foto), kind:"product", id:p.id})),
        ].sort((a,b)=>b.bytes-a.bytes);

        const removeBigItemPhoto = (item) => {
          if(!confirm(`¿Sacarle la foto a "${item.nombre}"? Esto libera espacio; el resto de los datos no se toca.`)) return;
          if(item.kind==="recipe"){
            const next = storedRecipes.map(r=>r.id===item.id?{...r,foto:""}:r);
            saveRecipes(next);
          } else if(item.kind==="product"){
            const next = storedProducts.map(p=>p.id===item.id?{...p,foto:""}:p);
            saveProducts(next);
          } else if(item.kind==="exercise"){
            setExercises(prev=>prev.map(e=>e.id===item.id?{...e,photo:""}:e));
          }
          setShowSettings(false); setTimeout(()=>setShowSettings(true), 50); // refrescar el diagnóstico
        };

        return <Modal title="⚙️ CONFIGURACIONES" onClose={()=>setShowSettings(false)} width={400}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div style={{background:T.surface,borderRadius:14,padding:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.text,letterSpacing:".04em"}}>
                  🔔 Notificaciones
                </span>
                <button onClick={()=>setNotifsEnabled(!notifsEnabled)}
                  style={{width:46,height:26,borderRadius:13,border:"none",cursor:"pointer",position:"relative",
                    background:notifsEnabled?T.accent:T.border,transition:"background .2s"}}>
                  <div style={{position:"absolute",top:3,left:notifsEnabled?23:3,width:20,height:20,
                    borderRadius:"50%",background:"#fff",transition:"left .2s"}}/>
                </button>
              </div>
              <p style={{fontSize:12,color:T.muted,lineHeight:1.5,margin:0}}>
                Cuando está activado, GYMDRAFFIN te avisa apenas llega el horario de una comida, un entrenamiento
                o una sesión de cardio programada — con una ventana emergente en la app, y si le das permiso
                también como notificación del sistema.
              </p>

              {browserPermission==="unsupported"&&<p style={{fontSize:11,color:T.orange,marginTop:10}}>
                Tu navegador no soporta notificaciones del sistema. Igual vas a ver la ventana emergente dentro de la app.
              </p>}
              {browserPermission==="denied"&&<p style={{fontSize:11,color:T.red,marginTop:10}}>
                Bloqueaste las notificaciones del sistema para este sitio. Podés habilitarlas desde la configuración
                del navegador si querés que también te avisen fuera de la app.
              </p>}
              {browserPermission==="default"&&notifsEnabled&&
                <Btn sz="sm" v="success" style={{marginTop:10}} onClick={()=>{
                  if(typeof Notification!=="undefined") Notification.requestPermission();
                }}>
                  🔔 Permitir notificaciones del sistema
                </Btn>}
              {browserPermission==="granted"&&<p style={{fontSize:11,color:T.green,marginTop:10}}>
                ✓ Notificaciones del sistema permitidas.
              </p>}

              <div style={{background:T.bg,borderRadius:10,padding:"10px 12px",marginTop:12,
                border:`1px solid ${T.border}`}}>
                <p style={{fontSize:11,color:T.muted,margin:0,lineHeight:1.5}}>
                  ⌚ <b style={{color:T.text}}>Aclaración:</b> si tenés un reloj inteligente emparejado con tu celular
                  (Apple Watch, o cualquiera con Wear OS), estas notificaciones se reflejan ahí automáticamente —
                  no hace falta configurar nada aparte. Funciona mejor con la app abierta o recientemente usada;
                  con la app completamente cerrada, la confiabilidad depende del navegador.
                </p>
              </div>
            </div>

            {/* ── Almacenamiento ── */}
            <div style={{background:T.surface,borderRadius:14,padding:16}}>
              <span style={{fontFamily:"'Bebas Neue'",fontSize:16,color:T.text,letterSpacing:".04em"}}>
                💾 Almacenamiento
              </span>
              <div style={{marginTop:10,marginBottom:4}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                  <span style={{color:T.muted}}>Usado (estimado)</span>
                  <span style={{color:totalBytes/1024/1024>QUOTA_MB*0.8?T.red:T.text,fontWeight:700}}>
                    {totalMB} MB de ~{QUOTA_MB} MB
                  </span>
                </div>
                <div style={{height:6,background:T.border,borderRadius:3,overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:3,transition:"width .3s",
                    width:`${Math.min(100,(totalBytes/1024/1024/QUOTA_MB)*100)}%`,
                    background:totalBytes/1024/1024>QUOTA_MB*0.8?T.red:T.accent}}/>
                </div>
              </div>

              {bigItems.length===0
                ? <p style={{fontSize:11,color:T.muted,marginTop:8}}>No hay fotos guardadas ocupando espacio de sobra.</p>
                : <>
                    <p style={{fontSize:11,color:T.muted,marginTop:10,marginBottom:8}}>
                      Esto es lo que más espacio ocupa — tocá "Sacar foto" en lo que no necesites para liberar lugar:
                    </p>
                    <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:220,overflowY:"auto"}}>
                      {bigItems.slice(0,15).map((it,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:8,background:T.bg,
                          borderRadius:8,padding:"7px 10px"}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                              {it.nombre||"(sin nombre)"}
                            </div>
                            <div style={{fontSize:10,color:T.muted}}>{it.tipo} · {(it.bytes/1024).toFixed(0)} KB</div>
                          </div>
                          <button onClick={()=>removeBigItemPhoto(it)}
                            style={{background:"none",color:T.red,border:`1px solid ${T.red}44`,borderRadius:6,
                              padding:"4px 8px",fontSize:10,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                            Sacar foto
                          </button>
                        </div>
                      ))}
                    </div>
                  </>}
              <p style={{fontSize:10,color:T.muted,marginTop:10,lineHeight:1.4}}>
                Tip: las fotos que subas de ahora en más ya se comprimen solas, así que esto no debería volver a pasar
                seguido. También podés hacer una copia de seguridad completa desde tu perfil antes de borrar cualquier cosa.
              </p>
            </div>

            {/* ── Último error atrapado — para poder reportarlo con el detalle exacto ── */}
            {lastError&&<div style={{background:T.surface,borderRadius:14,padding:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontFamily:"'Bebas Neue'",fontSize:14,color:T.red,letterSpacing:".04em"}}>
                  ⚠️ Último error detectado
                </span>
                <button onClick={()=>{try{localStorage.removeItem("gd_last_error");}catch{};setShowSettings(false);setTimeout(()=>setShowSettings(true),50);}}
                  style={{background:"none",color:T.muted,border:"none",fontSize:11,cursor:"pointer"}}>
                  Borrar
                </button>
              </div>
              <p style={{fontSize:10,color:T.muted,marginBottom:6}}>{new Date(lastError.at).toLocaleString("es-AR")}</p>
              <div style={{background:T.bg,borderRadius:8,padding:"8px 10px",fontSize:10,color:T.red,
                fontFamily:"monospace",wordBreak:"break-word",maxHeight:180,overflowY:"auto",whiteSpace:"pre-wrap"}}>
                <b>{lastError.message}</b>
                {lastError.stack&&<div style={{marginTop:6,color:T.muted}}>{lastError.stack}</div>}
                {lastError.componentStack&&<div style={{marginTop:6,color:T.orange}}>--- Componentes ---{lastError.componentStack}</div>}
              </div>
              <Btn sz="sm" v="ghost" style={{marginTop:8}} onClick={()=>{
                const text = `${lastError.message}\n\n--- STACK ---\n${lastError.stack||""}\n\n--- COMPONENTES ---\n${lastError.componentStack||""}`;
                try{ navigator.clipboard.writeText(text); }catch{}
              }}>
                📋 Copiar detalle completo
              </Btn>
              <p style={{fontSize:10,color:T.muted,marginTop:8}}>
                Copiá este texto y pasámelo si el problema se repite — ayuda a encontrar la causa exacta.
              </p>
            </div>}
          </div>
        </Modal>;
      })()}


      {/* Contenido */}
      <div style={{padding:"16px 14px",paddingBottom:"calc(96px + env(safe-area-inset-bottom))",maxWidth:780,margin:"0 auto",overflowX:"hidden",overflowY:"visible"}}>
       {(()=>{
         // Devuelve el contenido de cualquier pestaña por id — se usa tanto para la actual
         // como para la adyacente que ya se ve entrando/saliendo durante el gesto.
         const renderTab = id => {
           switch(id){
             case "today": return <TodayView profile={profile} routines={routines} schedule={schedule}
               logs={logs} cardioLogs={cardioLogs} cardioSchedule={cardioSchedule}
               restDays={restDays} exercises={exercises} notifsEnabled={notifsEnabled}
               onExecuteSlot={handleExecuteSlot} goToTab={setTab}
               goToMealsTab={()=>{
                 setNutriTab("comidasdia");
                 setSelectedWeekday(new Date().getDay());
                 setTab("nutrition");
               }}/>;
             case "home": return <HomeView profile={profile} routines={routines} schedule={schedule}
               logs={logs} cardioLogs={cardioLogs} restDays={restDays} exercises={exercises}
               onExecuteSlot={handleExecuteSlot} goToTab={setTab}/>;
             case "routine": return <div className="fu">
               {filtered.length===0
                 ? <div style={{textAlign:"center",padding:"50px 20px",color:T.muted}}>
                     <Icon n="dumbbell" s={44} c={T.border}/>
                     <p style={{marginTop:12,fontSize:14}}>No hay ejercicios todavía.</p>
                     <p style={{fontSize:12,marginTop:6}}>Tocá <b style={{color:T.accent}}>+ Nuevo</b> para agregar.</p>
                   </div>
                 : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
                     {filtered.map(ex=><ExerciseCard key={ex.id} ex={ex} onEdit={openEdit} onDelete={delEx}/>)}
                   </div>}
             </div>;
             case "routines": return <RoutinesView routines={routines} exercises={exercises}
               setRoutines={setRoutines} schedule={schedule} setSchedule={setSchedule}
               onExecuteSlot={handleExecuteSlot} logs={logs} profile={profile} goToTab={setTab}
               showNewRt={showNewRt} setShowNewRt={setShowNewRt} showGenAI={showGenAI} setShowGenAI={setShowGenAI}
               innerTab={routinesInnerTab} setInnerTab={setRoutinesInnerTab}/>;
             case "nutrition": return <NutritionView profile={profile} logs={logs} cardioLogs={cardioLogs}
               nutriTab={nutriTab} setNutriTab={setNutriTab}
               selectedWeekday={selectedWeekday} setSelectedWeekday={setSelectedWeekday}
               showCopyDay={showCopyDay} setShowCopyDay={setShowCopyDay} weekOffset={nutriWeekOffset} setWeekOffset={setNutriWeekOffset}
               showScheduleMeals={showScheduleMeals} setShowScheduleMeals={setShowScheduleMeals}
               schedule={schedule} cardioSchedule={cardioSchedule} restDays={restDays}
               supplementLogs={supplementLogs} setSupplementLogs={setSupplementLogs}/>;
             case "cardio": return <CardioView cardioLogs={cardioLogs} restDays={restDays} setCardioLogs={setCardioLogs} setRestDays={setRestDays}
               cardioSchedule={cardioSchedule} setCardioSchedule={setCardioSchedule} profile={profile}/>;
             case "stats": return <StatsView logs={logs} exercises={exercises} cardioLogs={cardioLogs} profile={profile} setLogs={setLogs} setCardioLogs={setCardioLogs}
               statTab={statTab} setStatTab={setStatTab}/>;
             case "share": return <ShareView profile={profile} routines={routines} exercises={exercises}/>;
             default: return null;
           }
         };

         const width = window.innerWidth || 360;
         const step = width + PANEL_GAP; // distancia real entre paneles, dejando un espacio visual entre ellos
         // El conjunto se corre para que, si el adyacente va antes (prev), arranque desplazado
         // un panel completo (+ el espacio) hacia la izquierda como posición de reposo.
         const rowOffset = dragX - (adjacentSide==="prev" ? step : 0);
         const isActive = dragX !== 0 || dragTransition; // solo mientras se arrastra o anima

         return <div style={{display:"flex", gap:PANEL_GAP,
           transform: isActive ? `translateX(${rowOffset}px)` : "none",
           transition: dragTransition ? "transform .24s cubic-bezier(.22,.68,0,1)" : "none",
           willChange: isActive ? "transform" : "auto"}}
           onTransitionEnd={handleContentTransitionEnd}>
           {/* Las key son el id de la pestaña (no "actual"/"adyacente") para que React reutilice
               el mismo elemento cuando el adyacente pasa a ser el actual — sin eso, remonta todo
               el árbol al soltar el dedo y se ve como un parpadeo. */}
           {adjacentSide==="prev" && <div key={adjacentTabId} style={{flex:"0 0 100%",minWidth:"100%"}}>{renderTab(adjacentTabId)}</div>}
           <div key={tab} style={{flex:"0 0 100%",minWidth:"100%"}}>{renderTab(tab)}</div>
           {adjacentSide==="next" && <div key={adjacentTabId} style={{flex:"0 0 100%",minWidth:"100%"}}>{renderTab(adjacentTabId)}</div>}
         </div>;
       })()}
      </div>

      {/* ── GUÍA GUIADA: fondo oscurecido + globo de explicación ── */}
      {tourActive&&(()=>{
        const step = TOUR_STEPS[tourStep];
        return <>
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:70,background:"rgba(0,0,0,.6)",
            zIndex:95,pointerEvents:"none"}}/>
          <div className="fu" style={{position:"fixed",left:14,right:14,bottom:96,zIndex:110,
            background:T.card,borderRadius:20,padding:"18px 20px",
            border:`2px solid ${T.accent}`,boxShadow:`0 10px 40px rgba(0,0,0,.6), 0 0 24px ${T.accent}33`}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <span style={{fontSize:11,color:T.accent,fontWeight:800,letterSpacing:".05em"}}>
                PASO {tourStep+1} DE {TOUR_STEPS.length}
              </span>
              <button onClick={finishTour} style={{background:"none",color:T.muted,border:"none",
                fontSize:12,cursor:"pointer",fontWeight:600}}>Saltar guía ×</button>
            </div>
            <h3 style={{fontFamily:"'Bebas Neue'",fontSize:19,color:T.text,letterSpacing:".03em",marginBottom:6}}>
              {step.title}
            </h3>
            <p style={{fontSize:13,color:T.muted,lineHeight:1.5,marginBottom:14}}>{step.message}</p>
            <div style={{display:"flex",gap:8}}>
              {tourStep>0&&<button onClick={prevTourStep}
                style={{background:T.surface,color:T.muted,border:`1px solid ${T.border}`,borderRadius:12,
                  padding:"10px 16px",fontSize:13,fontWeight:700,cursor:"pointer"}}>← Atrás</button>}
              <button onClick={nextTourStep}
                style={{flex:1,background:T.accent,color:"#000",border:"none",borderRadius:12,
                  padding:"10px 16px",fontSize:13,fontWeight:800,cursor:"pointer"}}>
                {tourStep<TOUR_STEPS.length-1?"Siguiente →":"¡Listo, a entrenar! 🎉"}
              </button>
            </div>
            {/* Flechita apuntando hacia el ícono resaltado abajo */}
            <div style={{position:"absolute",bottom:-9,left:"50%",transform:"translateX(-50%) rotate(45deg)",
              width:16,height:16,background:T.card,borderRight:`2px solid ${T.accent}`,borderBottom:`2px solid ${T.accent}`}}/>
          </div>
        </>;
      })()}

      {/* ── Aviso de almacenamiento lleno — banner propio, no bloquea la app como alert() ── */}
      {storageWarning&&<div style={{position:"fixed",left:14,right:14,bottom:96,zIndex:120,
        background:T.red+"ee",borderRadius:14,padding:"12px 14px",display:"flex",alignItems:"center",gap:10,
        boxShadow:"0 8px 24px rgba(0,0,0,.5)"}}>
        <span style={{fontSize:18,flexShrink:0}}>⚠️</span>
        <span style={{fontSize:12,color:"#fff",fontWeight:600,flex:1,lineHeight:1.3}}>
          No se pudo guardar — el almacenamiento está lleno. Liberá espacio en Configuraciones.
        </span>
        <button onClick={()=>{setStorageWarning(false);setShowSettings(true);}}
          style={{background:"#fff",color:T.red,border:"none",borderRadius:8,padding:"6px 10px",
            fontSize:11,fontWeight:800,cursor:"pointer",flexShrink:0}}>
          Ver
        </button>
        <button onClick={()=>setStorageWarning(false)}
          style={{background:"none",color:"#fff",border:"none",fontSize:16,cursor:"pointer",flexShrink:0}}>×</button>
      </div>}

      {/* Nav — estilo "Liquid Glass" (iOS 26): flotante, translúcido, separado de los bordes */}
      <div style={{position:"fixed",bottom:"calc(14px + env(safe-area-inset-bottom))",left:12,right:12,
        background:"rgba(28,29,38,.72)",backdropFilter:"blur(24px) saturate(180%)",WebkitBackdropFilter:"blur(24px) saturate(180%)",
        borderRadius:28,border:"1px solid rgba(255,255,255,.09)",
        boxShadow:"0 10px 34px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.07)",
        display:"flex",zIndex:100,padding:"6px 5px"}}>
        {TABS.map(([id,label,icon])=>{
          const isTourTarget = tourActive && TOUR_STEPS[tourStep]?.tabId===id;
          const isActive = tab===id;
          return <button key={id} onClick={()=>{
              if(id===tab) window.scrollTo({top:0,behavior:"smooth"});
              else setTab(id);
            }}
            style={{flex:1,padding:"7px 2px",background:"none",display:"flex",flexDirection:"column",
              alignItems:"center",gap:3,cursor:"pointer",border:"none",borderRadius:20,
              color:isActive?T.accent:"rgba(255,255,255,.55)",position:"relative",transition:"color .15s"}}>
            <div style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"center",
              width:34,height:26,borderRadius:14,
              background:isActive?T.accent+"26":"transparent",
              boxShadow:isTourTarget?`0 0 0 3px ${T.accent}, 0 0 16px 4px ${T.accent}99`:"none",
              animation:isTourTarget?"tourPulse 1.2s ease-in-out infinite":"none",
              transition:"background .2s"}}>
              <Icon n={icon} s={18} c={isActive?T.accent:"rgba(255,255,255,.55)"}/>
            </div>
            <span style={{fontSize:8,fontWeight:isActive?700:600,letterSpacing:".03em",textTransform:"uppercase"}}>{label}</span>
          </button>;
        })}
      </div>
    </div>
    {showForm&&<Modal title={editEx?"EDITAR EJERCICIO":"NUEVO EJERCICIO"} onClose={()=>{setShowForm(false);setEditEx(null);}}>
      <ExerciseForm initial={editEx||{}} onSave={saveEx} onClose={()=>{setShowForm(false);setEditEx(null);}}/>
    </Modal>}

    {confirmDelEx&&<Modal title="ELIMINAR EJERCICIO" onClose={()=>setConfirmDelEx(null)} width={340}>
      <div style={{display:"flex",flexDirection:"column",gap:16,alignItems:"center",textAlign:"center"}}>
        <div style={{fontSize:40}}>🗑️</div>
        <div>
          <p style={{fontSize:14,color:T.text,fontWeight:600,marginBottom:6}}>{confirmDelEx.name}</p>
          <p style={{fontSize:13,color:T.muted}}>
            ¿Eliminás este ejercicio de tu biblioteca?<br/>
            <span style={{fontSize:11,color:T.red}}>También se quitará de las rutinas donde esté asignado.</span>
          </p>
        </div>
        <div style={{display:"flex",gap:8,width:"100%"}}>
          <Btn full v="danger" onClick={()=>{
            setExercises(p=>p.filter(e=>e.id!==confirmDelEx.id));
            // quitar de rutinas también
            setRoutines(p=>p.map(r=>({...r,
              exercises:(r.exercises||[]).filter(e=>e.exId!==confirmDelEx.id)
            })));
            setConfirmDelEx(null);
          }}>
            <Icon n="trash" s={14}/>Eliminar
          </Btn>
          <Btn full v="ghost" onClick={()=>setConfirmDelEx(null)}>Cancelar</Btn>
        </div>
      </div>
    </Modal>}

    {showSearch&&<ExerciseSearchModal
      onSaveAll={list=>{
        setExercises(p=>{
          const existing=new Set(p.map(e=>e.name?.toLowerCase()));
          const newOnes=list.filter(e=>!existing.has(e.name?.toLowerCase()));
          return [...p,...newOnes];
        });
        setShowSearch(false);
        setTab("routine");
      }}
      onClose={()=>setShowSearch(false)}/>}

    {activeSession&&<SessionRunner
      slot={activeSession.slot}
      routine={activeSession.routine}
      exercises={exercises}
      onFinish={handleFinishSession}
      onClose={()=>setActiveSession(null)}/>}
  </>;
}

// ─── Autenticación y sincronización con la nube ────────────────────────────────
const AUTH_KEY = "gd_auth_v1";
const loadAuth  = () => lsGet(AUTH_KEY, null);
const saveAuth  = a  => lsSet(AUTH_KEY, a);
const clearAuth = ()  => { try{localStorage.removeItem(AUTH_KEY);}catch{} };

// Junta TODO lo guardado localmente en un solo objeto (mismo formato que el backup manual)
function gatherAllLocalData() {
  const main = loadData();
  const dietData     = lsGet(DIET_KEY, {});
  const recipesData  = lsGet(RECIPES_KEY, []);
  const productsData = lsGet(PROD_KEY, []);
  return { ...main, dietData, recipesData, productsData };
}
// Aplica datos recibidos de la nube al almacenamiento local del dispositivo
function applyAllLocalData(data) {
  if(!data) return;
  const { exercises, logs, routines, cardioLogs, cardioSchedule, restDays, schedule, profile, objectiveHistory, supplementLogs, dietData, recipesData, productsData } = data;
  const merged = {...loadData()};
  if(Array.isArray(exercises))  merged.exercises  = exercises;
  if(Array.isArray(logs))       merged.logs       = logs;
  if(Array.isArray(routines))   merged.routines   = routines;
  if(Array.isArray(cardioLogs)) merged.cardioLogs = cardioLogs;
  if(Array.isArray(cardioSchedule)) merged.cardioSchedule = cardioSchedule;
  if(Array.isArray(restDays))   merged.restDays   = restDays;
  if(Array.isArray(schedule))   merged.schedule   = schedule;
  if(profile) merged.profile = profile;
  if(Array.isArray(objectiveHistory)) merged.objectiveHistory = objectiveHistory;
  if(Array.isArray(supplementLogs)) merged.supplementLogs = supplementLogs;
  lsSet(DATA_KEY, merged);
  if(dietData) saveDiet(dietData);
  if(Array.isArray(recipesData))  saveRecipes(recipesData);
  if(Array.isArray(productsData)) saveProducts(productsData);
}
async function syncToCloud(token) {
  if(!token) return false;
  try {
    const res = await fetch("/api/data", {
      method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
      body: JSON.stringify(gatherAllLocalData()),
    });
    return res.ok;
  } catch { return false; }
}
async function syncFromCloud(token) {
  if(!token) return false;
  try {
    const res = await fetch("/api/data", { headers:{"Authorization":`Bearer ${token}`} });
    if(!res.ok) return false;
    const json = await res.json();
    if(json.data && Object.keys(json.data).length>0) { applyAllLocalData(json.data); return true; }
    return false;
  } catch { return false; }
}
async function validateSession(token) {
  try {
    const res = await fetch("/api/auth/me", { headers:{"Authorization":`Bearer ${token}`} });
    if(!res.ok) return null;
    const json = await res.json();
    return json.user || null;
  } catch { return null; }
}

// ─── Pantalla de Login / Registro ───────────────────────────────────────────────
function AuthScreen({ onAuthed }) {
  // "login" | "signup" | "verify" | "forgot" | "reset"
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [devCode, setDevCode] = useState(""); // ⚠️ modo de prueba — ver nota abajo
  const [pendingUserId, setPendingUserId] = useState(null);

  const doVerify = async (userId, codeVal) => {
    const res = await fetch("/api/auth/verify-signup", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ userId, code: codeVal }),
    });
    const json = await res.json();
    if(!res.ok) { setErr(json.error||"Código incorrecto."); return false; }
    saveAuth({ token: json.token, user: json.user });
    await syncFromCloud(json.token);
    onAuthed(json.user, json.token);
    return true;
  };

  const submitLogin = async () => {
    setErr("");
    if(!email.trim() || !password) { setErr("Completá email y contraseña."); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({email:email.trim(),password}),
      });
      const json = await res.json();
      if(!res.ok) {
        if(json.needsVerification) {
          setPendingUserId(json.userId); setMode("verify"); setErr("Todavía no verificaste tu email. Te reenviamos un código.");
          try {
            const r2 = await fetch("/api/auth/resend-code", {method:"POST",headers:{"Content-Type":"application/json"},
              body: JSON.stringify({userId: json.userId})});
            const j2 = await r2.json();
            if(r2.ok) setDevCode(j2.devCode||"");
          } catch {}
          setLoading(false); return;
        }
        setErr(json.error||"Error. Intentá de nuevo."); setLoading(false); return;
      }
      saveAuth({ token: json.token, user: json.user });
      await syncFromCloud(json.token);
      onAuthed(json.user, json.token);
    } catch { setErr("No se pudo conectar. Revisá tu internet."); }
    setLoading(false);
  };

  const submitSignup = async () => {
    setErr("");
    if(!email.trim() || !password) { setErr("Completá email y contraseña."); return; }
    if(!name.trim()) { setErr("Completá tu nombre."); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({email:email.trim(),password,name:name.trim()}),
      });
      const json = await res.json();
      if(!res.ok) { setErr(json.error||"Error. Intentá de nuevo."); setLoading(false); return; }
      setPendingUserId(json.userId);
      setDevCode(json.devCode||""); // ⚠️ modo de prueba
      setCode("");
      setMode("verify");
    } catch { setErr("No se pudo conectar. Revisá tu internet."); }
    setLoading(false);
  };

  const submitVerify = async () => {
    setErr("");
    if(!code.trim()) { setErr("Ingresá el código de 6 dígitos."); return; }
    setLoading(true);
    await doVerify(pendingUserId, code.trim());
    setLoading(false);
  };

  const submitForgot = async () => {
    setErr("");
    if(!email.trim()) { setErr("Completá tu email."); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({email:email.trim()}),
      });
      const json = await res.json();
      if(!res.ok) { setErr(json.error||"Error. Intentá de nuevo."); setLoading(false); return; }
      setDevCode(json.devCode||""); // ⚠️ modo de prueba
      setCode(""); setNewPassword("");
      setMode("reset");
    } catch { setErr("No se pudo conectar. Revisá tu internet."); }
    setLoading(false);
  };

  const submitReset = async () => {
    setErr("");
    if(!code.trim() || !newPassword) { setErr("Completá el código y la nueva contraseña."); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({email:email.trim(),code:code.trim(),newPassword}),
      });
      const json = await res.json();
      if(!res.ok) { setErr(json.error||"Error. Intentá de nuevo."); setLoading(false); return; }
      setMode("login"); setPassword(""); setCode(""); setNewPassword(""); setDevCode("");
      setErr("Contraseña actualizada — iniciá sesión de nuevo.");
    } catch { setErr("No se pudo conectar. Revisá tu internet."); }
    setLoading(false);
  };

  return <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",
    alignItems:"center",justifyContent:"center",padding:20}}>
    <style>{CSS}</style>
    <div style={{fontFamily:"'Bebas Neue'",fontSize:36,letterSpacing:".1em",marginBottom:24}}>
      <span style={{color:T.accent}}>GYM</span><span style={{color:T.text}}>DRAFFIN</span>
    </div>
    <div style={{background:T.card,borderRadius:16,padding:24,width:"100%",maxWidth:360,
      border:`1px solid ${T.border}`}}>

      {(mode==="login"||mode==="signup")&&<>
        <div style={{display:"flex",background:T.surface,borderRadius:10,padding:3,gap:3,marginBottom:18}}>
          {[["login","Iniciar sesión"],["signup","Crear cuenta"]].map(([id,label])=>(
            <button key={id} onClick={()=>{setMode(id);setErr("");}}
              style={{flex:1,padding:"9px 0",borderRadius:8,fontWeight:700,fontSize:13,
                background:mode===id?T.accent:"transparent",color:mode===id?"#000":T.muted,
                border:"none",cursor:"pointer"}}>
              {label}
            </button>
          ))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {mode==="signup"&&<Field label="Nombre" value={name} onChange={setName} placeholder="Tu nombre"/>}
          <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="tu@email.com"/>
          <Field label="Contraseña" type="password" value={password} onChange={setPassword} placeholder="Mínimo 6 caracteres"/>
          {mode==="login"&&<button onClick={()=>{setMode("forgot");setErr("");setPassword("");}}
            style={{background:"none",border:"none",color:T.blue,fontSize:12,cursor:"pointer",textAlign:"right",padding:0}}>
            ¿Olvidaste tu contraseña?
          </button>}
          {err&&<p style={{color:T.red,fontSize:12,margin:0}}>{err}</p>}
          <Btn full v="success" onClick={mode==="signup"?submitSignup:submitLogin} disabled={loading}>
            {loading?<><Spinner/>{mode==="signup"?"Creando cuenta...":"Entrando..."}</>:mode==="signup"?"Crear cuenta":"Entrar"}
          </Btn>
        </div>
      </>}

      {mode==="verify"&&<>
        <h3 style={{fontFamily:"'Bebas Neue'",fontSize:20,color:T.accent,letterSpacing:".05em",marginBottom:6}}>
          VERIFICÁ TU EMAIL
        </h3>
        <p style={{fontSize:12,color:T.muted,marginBottom:14}}>
          Te enviamos un código de 6 dígitos a <b style={{color:T.text}}>{email}</b>. Ingresalo abajo.
        </p>
        {devCode&&<div style={{background:T.orange+"18",border:`1px solid ${T.orange}44`,borderRadius:10,
          padding:"10px 12px",marginBottom:14,fontSize:12,color:T.orange}}>
          ⚠️ <b>Modo de prueba</b> (todavía no hay envío real de email): tu código es <b style={{fontSize:16}}>{devCode}</b>
        </div>}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="Código de 6 dígitos" value={code} onChange={setCode} placeholder="000000"/>
          {err&&<p style={{color:T.red,fontSize:12,margin:0}}>{err}</p>}
          <Btn full v="success" onClick={submitVerify} disabled={loading}>
            {loading?<><Spinner/>Verificando...</>:"Confirmar código"}
          </Btn>
          <button onClick={()=>{setMode("login");setErr("");}}
            style={{background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer"}}>
            ← Volver
          </button>
        </div>
      </>}

      {mode==="forgot"&&<>
        <h3 style={{fontFamily:"'Bebas Neue'",fontSize:20,color:T.accent,letterSpacing:".05em",marginBottom:6}}>
          RECUPERAR CONTRASEÑA
        </h3>
        <p style={{fontSize:12,color:T.muted,marginBottom:14}}>
          Ingresá tu email y te mandamos un código para restablecerla.
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="tu@email.com"/>
          {err&&<p style={{color:T.red,fontSize:12,margin:0}}>{err}</p>}
          <Btn full v="success" onClick={submitForgot} disabled={loading}>
            {loading?<><Spinner/>Enviando...</>:"Enviar código"}
          </Btn>
          <button onClick={()=>{setMode("login");setErr("");}}
            style={{background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer"}}>
            ← Volver
          </button>
        </div>
      </>}

      {mode==="reset"&&<>
        <h3 style={{fontFamily:"'Bebas Neue'",fontSize:20,color:T.accent,letterSpacing:".05em",marginBottom:6}}>
          NUEVA CONTRASEÑA
        </h3>
        <p style={{fontSize:12,color:T.muted,marginBottom:14}}>
          Ingresá el código que te llegó y tu nueva contraseña.
        </p>
        {devCode&&<div style={{background:T.orange+"18",border:`1px solid ${T.orange}44`,borderRadius:10,
          padding:"10px 12px",marginBottom:14,fontSize:12,color:T.orange}}>
          ⚠️ <b>Modo de prueba</b> (todavía no hay envío real de email): tu código es <b style={{fontSize:16}}>{devCode}</b>
        </div>}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="Código de 6 dígitos" value={code} onChange={setCode} placeholder="000000"/>
          <Field label="Nueva contraseña" type="password" value={newPassword} onChange={setNewPassword} placeholder="Mínimo 6 caracteres"/>
          {err&&<p style={{color:T.red,fontSize:12,margin:0}}>{err}</p>}
          <Btn full v="success" onClick={submitReset} disabled={loading}>
            {loading?<><Spinner/>Guardando...</>:"Restablecer contraseña"}
          </Btn>
          <button onClick={()=>{setMode("login");setErr("");}}
            style={{background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer"}}>
            ← Volver
          </button>
        </div>
      </>}
    </div>
  </div>;
}

// ─── Raíz de la app: decide si mostrar login o la app ya autenticada ───────────
// ─── Onboarding obligatorio: edad, objetivo, altura (define qué módulos ve el usuario) ──
function OnboardingScreen({ onComplete }) {
  const [step, setStep] = useState(1); // 1: datos básicos, 2: objetivo + duración
  const [edad, setEdad] = useState("");
  const [altura, setAltura] = useState("");
  const [peso, setPeso] = useState("");
  const [sexo, setSexo] = useState("m");
  const [objetivo, setObjetivo] = useState("");
  const [meses, setMeses] = useState(3);
  const [useCustomEnd, setUseCustomEnd] = useState(false);
  const [customEnd, setCustomEnd] = useState("");
  const [err, setErr] = useState("");

  const endDate = useCustomEnd && customEnd ? customEnd : addMonthsISO(nowISO(), meses);

  const goStep2 = () => {
    setErr("");
    if(!edad || !altura || !peso) { setErr("Completá todos los campos — son obligatorios."); return; }
    setStep(2);
  };

  const submit = () => {
    setErr("");
    if(!objetivo) { setErr("Elegí un objetivo para continuar."); return; }
    const current = loadData();
    const profile = {
      ...DEFAULT_PROFILE, ...(current.profile||{}),
      edad:+edad, altura:+altura, peso:+peso, sexo, objetivo,
      onboarded: true,
    };
    const objectiveHistory = [{id:uid(), objetivo, startDate:nowISO(), endDate}];
    saveData({...current, profile, objectiveHistory});
    onComplete();
  };

  return <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",
    alignItems:"center",justifyContent:"center",padding:20}}>
    <style>{CSS}</style>
    <div style={{background:T.card,borderRadius:16,padding:24,width:"100%",maxWidth:380,
      border:`1px solid ${T.border}`}}>

      {step===1&&<>
        <h2 style={{fontFamily:"'Bebas Neue'",fontSize:24,color:T.accent,letterSpacing:".05em",marginBottom:4}}>
          CONTANOS SOBRE VOS
        </h2>
        <p style={{fontSize:12,color:T.muted,marginBottom:18}}>
          Estos datos son obligatorios — según tu objetivo y tu edad, la app habilita el contenido adecuado para vos.
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="Edad *" type="number" value={edad} onChange={setEdad} placeholder="30"/>
            <Field label="Altura (cm) *" type="number" value={altura} onChange={setAltura} placeholder="175"/>
          </div>
          <Field label="Peso actual (kg) *" type="number" value={peso} onChange={setPeso} placeholder="80"/>
          <div>
            <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
              Sexo *
            </label>
            <div style={{display:"flex",gap:8}}>
              {[["m","Masculino"],["f","Femenino"]].map(([v,l])=>(
                <button key={v} onClick={()=>setSexo(v)}
                  style={{flex:1,padding:"9px 0",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer",
                    background:sexo===v?T.accent:T.surface,color:sexo===v?"#000":T.muted,
                    border:`1px solid ${sexo===v?T.accent:T.border}`}}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {err&&<p style={{color:T.red,fontSize:12,margin:0}}>{err}</p>}
          <Btn full v="success" onClick={goStep2}>
            Siguiente →
          </Btn>
        </div>
      </>}

      {step===2&&<>
        <h2 style={{fontFamily:"'Bebas Neue'",fontSize:24,color:T.accent,letterSpacing:".05em",marginBottom:4}}>
          TU OBJETIVO
        </h2>
        <p style={{fontSize:12,color:T.muted,marginBottom:18}}>
          Elegí tu objetivo y por cuánto tiempo lo vas a mantener. Cuando se cumpla el plazo, te vamos a avisar para elegir el próximo.
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div>
            <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
              Objetivo *
            </label>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {Object.entries(GOALS).map(([key,g])=>(
                <button key={key} onClick={()=>setObjetivo(key)}
                  style={{padding:"10px 12px",borderRadius:10,fontWeight:700,fontSize:13,cursor:"pointer",textAlign:"left",
                    background:objetivo===key?g.color+"22":T.surface,
                    color:objetivo===key?g.color:T.muted,
                    border:`1px solid ${objetivo===key?g.color:T.border}`}}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {objetivo&&<div>
            <label style={{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>
              ¿Por cuántos meses lo vas a mantener?
            </label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
              {[1,2,3,6,12].map(m=>(
                <button key={m} onClick={()=>{setMeses(m);setUseCustomEnd(false);}}
                  style={{padding:"7px 14px",borderRadius:20,fontWeight:700,fontSize:12,cursor:"pointer",
                    background:!useCustomEnd&&meses===m?T.accent:T.surface,color:!useCustomEnd&&meses===m?"#000":T.muted,
                    border:`1px solid ${!useCustomEnd&&meses===m?T.accent:T.border}`}}>
                  {m} {m===1?"mes":"meses"}
                </button>
              ))}
            </div>
            {!useCustomEnd&&<button onClick={()=>setUseCustomEnd(true)}
              style={{background:"none",color:T.blue,border:"none",fontSize:12,cursor:"pointer",padding:0}}>
              📅 Elegir una fecha exacta en el calendario
            </button>}
            {useCustomEnd&&<Field label="Fecha de fin" type="date" value={customEnd} onChange={setCustomEnd} min={addDaysISO(nowISO(),1)}/>}
            <p style={{fontSize:12,color:T.muted,marginTop:8}}>
              Vas a mantener este objetivo hasta el <b style={{color:T.text}}>{fmtDateFull(endDate)}</b>.
            </p>
          </div>}

          {err&&<p style={{color:T.red,fontSize:12,margin:0}}>{err}</p>}
          <div style={{display:"flex",gap:8}}>
            <Btn v="ghost" onClick={()=>setStep(1)}>← Atrás</Btn>
            <Btn full v="success" onClick={submit}>
              <Icon n="check" s={15}/>Empezar
            </Btn>
          </div>
        </div>
      </>}
    </div>
  </div>;
}

// ── Guía de inicio — pasos rápidos para orientar a un usuario nuevo ──
function GuideScreen({ onFinish }) {
  const [step, setStep] = useState(0);

  const STEPS = [
    {emoji:"👋", title:"¡BIENVENIDO A GYMDRAFFIN!",
      text:"Te armamos una guía rapidita para que sepas dónde está cada cosa. Son 6 pasos, 30 segundos."},
    {emoji:"🏋", title:"EJERCICIOS Y RUTINAS",
      text:"En Ejercicios cargás tu catálogo de movimientos. En Rutinas los agrupás y programás qué día tocan. Al ejecutar una rutina, podés editar peso/reps sobre la marcha, agregar series extra o hasta un ejercicio que no estaba planeado — todo queda en tu historial."},
    {emoji:"🍽", title:"NUTRICIÓN",
      text:"Elegís qué alimentos consumís habitualmente, armás recetas reutilizables, y programás las comidas de cada día. Podés editar cantidades o borrar un ingrediente puntual sin perder el resto de la comida."},
    {emoji:"🏃", title:"CARDIO",
      text:"Programá una sesión con fecha y hora, o registrala directo si ya la hiciste. La app calcula el ritmo, las calorías, y guarda el clima de ese momento automáticamente."},
    {emoji:"🎯", title:"TU OBJETIVO",
      text:"Cuando elegís un objetivo (Hipertrofia, Pérdida de grasa, etc.), definís por cuánto tiempo lo vas a sostener. Al cumplirse el plazo, te avisamos para elegir el próximo — y lo que ya cargaste no se pierde ni se recalcula."},
    {emoji:"📊", title:"ESTADÍSTICAS",
      text:"Progreso de pesos, cardio, nutrición y qué ejercicios entrenás más. Todo se arma solo con lo que vas cargando día a día."},
  ];
  const s = STEPS[step];
  const isLast = step===STEPS.length-1;

  return <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",
    alignItems:"center",justifyContent:"center",padding:20}}>
    <style>{CSS}</style>
    <div style={{background:T.card,borderRadius:20,padding:28,width:"100%",maxWidth:380,
      border:`1px solid ${T.border}`,textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:14}}>{s.emoji}</div>
      <h2 style={{fontFamily:"'Bebas Neue'",fontSize:22,color:T.accent,letterSpacing:".05em",marginBottom:12}}>
        {s.title}
      </h2>
      <p style={{fontSize:14,color:T.text,lineHeight:1.5,marginBottom:20}}>{s.text}</p>

      <div style={{display:"flex",justifyContent:"center",gap:5,marginBottom:20}}>
        {STEPS.map((_,i)=>(
          <div key={i} style={{width:i===step?18:6,height:6,borderRadius:3,
            background:i===step?T.accent:T.border,transition:"all .2s"}}/>
        ))}
      </div>

      <div style={{display:"flex",gap:8}}>
        {step>0&&<Btn v="ghost" onClick={()=>setStep(s=>s-1)}>← Atrás</Btn>}
        <Btn full v="success" onClick={()=>isLast?onFinish():setStep(s=>s+1)}>
          {isLast?"¡Empezar!":"Siguiente →"}
        </Btn>
      </div>
      {!isLast&&<button onClick={onFinish}
        style={{marginTop:14,background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer"}}>
        Saltar guía
      </button>}
    </div>
  </div>;
}

function AppRoot() {
  const [status, setStatus] = useState("checking"); // "checking" | "authed" | "guest" | "onboarding"
  const [authUser, setAuthUser] = useState(null);

  const isOnboarded = () => !!(loadData()?.profile?.onboarded);

  useEffect(()=>{
    const auth = loadAuth();
    if(!auth?.token) { setStatus("guest"); return; }
    (async () => {
      const validUser = await validateSession(auth.token);
      if(validUser) {
        await syncFromCloud(auth.token);
        setAuthUser(validUser);
        setStatus(isOnboarded() ? "authed" : "onboarding");
      } else {
        clearAuth();
        setStatus("guest");
      }
    })();
  },[]);

  const handleAuthed = u => {
    setAuthUser(u);
    setStatus(isOnboarded() ? "authed" : "onboarding");
  };
  const handleOnboardingComplete = () => {
    const auth = loadAuth();
    if(auth?.token) syncToCloud(auth.token); // guarda el perfil recién completado en la nube
    setStatus("authed");
  };
  const handleLogout = async () => {
    const auth = loadAuth();
    if(auth?.token) {
      await syncToCloud(auth.token); // último guardado antes de salir
      try { await fetch("/api/auth/logout", {method:"POST", headers:{"Authorization":`Bearer ${auth.token}`}}); } catch {}
    }
    clearAuth();
    setAuthUser(null);
    setStatus("guest");
  };

  if(status==="checking") return <div style={{minHeight:"100vh",background:T.bg,display:"flex",
    alignItems:"center",justifyContent:"center"}}>
    <style>{CSS}</style>
    <Spinner/>
  </div>;

  if(status==="guest") return <AuthScreen onAuthed={handleAuthed}/>;
  if(status==="onboarding") return <OnboardingScreen onComplete={handleOnboardingComplete}/>;

  return <MainApp authUser={authUser} onLogout={handleLogout}/>;
}

class ErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state = {error:null, info:null, copied:false}; }
  static getDerivedStateFromError(error){ return {error}; }
  componentDidCatch(error, info){
    console.error("GYMDRAFFIN — error atrapado:", error, info);
    this.setState({info});
    try{
      lsSet("gd_last_error", {
        message: error?.message||String(error),
        stack: error?.stack||"",
        componentStack: info?.componentStack||"",
        at: new Date().toISOString(),
      });
    }catch{}
  }
  copyDetails = () => {
    const text = `${this.state.error?.message||this.state.error}\n\n--- STACK ---\n${this.state.error?.stack||""}\n\n--- COMPONENTES ---\n${this.state.info?.componentStack||""}`;
    try{ navigator.clipboard.writeText(text); this.setState({copied:true}); setTimeout(()=>this.setState({copied:false}),2000); }catch{}
  };
  render(){
    if(this.state.error){
      return <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",
        alignItems:"center",justifyContent:"center",padding:24,gap:14,textAlign:"center",overflowY:"auto"}}>
        <style>{CSS}</style>
        <div style={{fontSize:44}}>⚠️</div>
        <h2 style={{fontFamily:"'Bebas Neue'",fontSize:22,color:T.accent,letterSpacing:".04em",margin:0}}>
          ALGO SALIÓ MAL
        </h2>
        <p style={{fontSize:13,color:T.muted,maxWidth:340,lineHeight:1.5}}>
          La app encontró un error inesperado. Tus datos guardados no se perdieron — probá recargar.
        </p>
        <div style={{background:T.card,borderRadius:10,padding:"10px 14px",maxWidth:360,maxHeight:220,overflowY:"auto",
          fontSize:10,color:T.red,fontFamily:"monospace",wordBreak:"break-word",textAlign:"left",whiteSpace:"pre-wrap"}}>
          <b>{this.state.error?.message||String(this.state.error)}</b>
          {this.state.error?.stack&&<div style={{marginTop:8,color:T.muted}}>{this.state.error.stack}</div>}
          {this.state.info?.componentStack&&<div style={{marginTop:8,color:T.orange}}>
            --- Componentes ---{this.state.info.componentStack}
          </div>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={this.copyDetails}
            style={{background:T.card,color:T.text,border:`1px solid ${T.border}`,borderRadius:12,
              padding:"12px 20px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
            {this.state.copied?"✓ Copiado":"📋 Copiar detalle"}
          </button>
          <button onClick={()=>window.location.reload()}
            style={{background:T.accent,color:"#000",border:"none",borderRadius:12,
              padding:"12px 20px",fontSize:13,fontWeight:800,cursor:"pointer"}}>
            🔄 Recargar
          </button>
        </div>
      </div>;
    }
    return this.props.children;
  }
}

export default function App() { return <ErrorBoundary><AppRoot/></ErrorBoundary>; }
